import {
  TEMPLATE_MSG,
  COST_KIND_LABEL,
  CostKind,
  MSG,
  EXCLUDED_WORDS,
  RATE_BASE_LABEL,
  RateBase,
  ROW_KIND_LABOR,
  ROW_ROLE_LABEL,
  RowRole,
  GROUP_MSG,
  type RatePresetList,
  type Estimate,
  type EstimateLine,
  type LineInput,
  type ProjectGroup,
  type ProjectGroupList,
  type Suggest,
  type SheetInput,
  type SheetCopyResult,
  ISSUE_MSG,
  REVISE_MSG,
  DISCARD_MSG,
  displayDocumentNo,
  type Company,
  type IssueResult,
  type RevisionDiff,
  type RevisionMarks,
  type RevisionMarkLine,
  type RevisionMarkRemoved,
  type RevisionMarkRemovedSheet,
  type MarkCell,
  MARK_LABEL,
  RESTORE_MSG,
  SHEET_TRASH_MSG,
  type SheetRestoreResult,
  type TrashResult,
  type UnissueResult,
  type Partner,
  type PartnerBranchList,
  VENDOR_IMPORT_MSG,
  type VendorImportPricing,
  type VendorImportRequest,
  type VendorProgress,
  fillLabels,
  NEGATIVE_ROUNDING_EXAMPLE,
  NEGATIVE_ROUNDING_LABEL,
  negativeRoundingOf,
  roundingCodeLabel,
  AXES_MSG,
  layoutLines,
  type LayoutAxis,
  TAKEOFF_MSG,
  LUMPSUM_RULE_DEFAULT,
  lumpsumWarningsOf,
  isLumpsumUnit,
  type QuantitySource,
  ESTIMATE_PC_MSG,
  UNISSUED_MSG,
  unissuedReissue,
  unissuedTitle,
  PHONE_MSG,
  AUTO_LOGOUT_MSG,
  rebaseEdits,
  UndoStack,
  layoutAxesFor,
  type CostSummary,
  type UseKey,
  type VendorQuoteProgress,
  ORDER_BUNDLE_MSG,
  RFQ_MSG,
  saveGateText,
  type EstimateRfqs,
  type RfqLineMark,
  type RfqLinesRemoved,
  type EstimateOrderMarks,
  type OrderRef,
  isTaxRow,
  TAX_MSG,
  hasCostField,
  shippingReducedWarning,
  taxChangedText,
  taxRowsInOrder,
  taxScreenLabel,
  type TaxChangedFromQuote,
  REFERRAL_MSG,
  autoUnitPrice,
  referralFeesOf,
  repriceForDocPct,
  uncoveredLinesOf,
  type PartnerList,
  DECIDE_MSG,
  type DecideResult,
  listNameOf,
  headRecipientOf,
} from "@genbaryoku/contract";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { hasModule } from "../core/slots.jsx";
import { uses } from "../core/uses.js";
import { PartnerPickerDrawer } from "../components/PartnerPickerDrawer.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { ContactSelect, partyFieldsHelp } from "../components/GroupFields.jsx";
import { PhoneFields } from "../components/PhoneFields.jsx";
import { Picker, type Cand, type PickTab } from "../components/Picker.jsx";
import { RoundStrip, type RoundMarks } from "../components/RoundStrip.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { UnissueDialog } from "../components/UnissueDialog.jsx";
import { Icon } from "../components/Shell.jsx";
import { VendorImportPicker, VendorOriginal, type OrigState, type TargetRow } from "../components/VendorImport.jsx";
import { ErrorText, Field, guardComposingEnter, Help, Switch, useToast } from "../components/ui.jsx";
import { AxisStrip, BulkBar, LayoutSelect, PlaceSelect, ProvisionalStrip, TradeSelect, axisNodes, namesOf, useAxesLists, type BulkPatch } from "../components/EstimateAxes.jsx";
import { LineAttachmentsDialog } from "../components/LineAttachments.jsx";
import { TakeoffDrawer, type TakeoffAnswer } from "../components/Takeoffs.jsx";
import { DeliveriesLink } from "../components/Deliveries.jsx";
import { RowMenu, type RowMenuItem } from "../components/RowMenu.jsx";
import { HeadActions, type HeadAction } from "../components/HeadActions.jsx";
import { columnsFor, longestMoney, type MoneyTexts } from "./estimates/columns.js";
import { useEstimateColumns } from "./estimates/colWidths.js";
import {
  AxisHeadingRow,
  AxisSubtotalRow,
  EditRow,
  EmptyRow,
  EstimateTable,
  NUMBERED,
  RemovedRow,
  RemovedSheetRow,
  SecRow,
  TotalRow,
  ReferralBandRow,
  ViewRow,
  pct,
  useShowInternal,
  type CostSum,
  type DragProps,
  type LineView,
  type Row,
  type RowTakeoff,
  type Sheet,
  type SourceInfo,
  type TblCtx,
} from "./estimates/EstimateTable.jsx";
import { VendorStrip, type Requested } from "./estimates/VendorStrip.jsx";
import { AxisFillDialog, StaleDialog, TopBand, type TopStatus } from "./estimates/EstimateTop.jsx";
import { dayJst, fq, md, mdSlash, yen } from "../lib/num.js";
import { RfqAddLineDialog, RfqVendorDialog, PhotoLedgerDialog } from "./rfq/EstimateRfq.jsx";
import { markNewRfq } from "./rfq/sendPrefs.js";
import { useQuoteDelivery } from "./delivery-notes/FromQuote.jsx";
import { DeletedSheetsDialog, trashedOf, useUntrash } from "./estimates/Trash.jsx";
import { TrashedStop } from "../components/StopScreen.jsx";
import { t, useLabels } from "../labels.js";
import { useCreateInvoice } from "./Invoices.jsx";
import { PctField, pctShort, type PricingMode } from "../components/ReferralParts.jsx";

/* ============================================================================
   見積を書く（縦串2b：表紙だけの見積を1枚、下書きで保存）。試作 v3.10.1 の紙をそのまま React に。
   ・行はその場で書き直せる（行を押すだけ）。編集は行の中、右パネルは作らない（docs/10 §5.5）
   ・金額はぜんぶ DB が計算する（014＋017）。画面は行を送って、返ってきた金額を見せるだけ
   ・下書きは自動で保存（打ち終わって 0.5 秒後）。番号は出すときに付く
   ・試作レビュー R1：「この行の種類」の高さはとなりのボタンと同じ 44px（docs/16）
   ============================================================================ */

type Head = {
  clientId: string;
  /** 正式名称（紙の宛名） */
  clientName: string;
  /** 略称（#315：画面の頭は「略称 ／ 支店 ／ 担当者」。空なら正式名称＝listNameOf） */
  clientShort: string;
  honorific: string;
  /** 案件（039 から見積は必ずどれかの案件に入る。null は古い形の読み込みのときだけ＝送ると API は今の案件のままにする） */
  projectGroupId: string | null;
  /** 顧客を変えたとき true：次の保存で新しい顧客の新しい案件に入り直す（docs/28 決めごと7）。保存が返ったら落とす */
  newGroup?: boolean;
  /** 宛先の支店・営業所（028）。null＝本社（支店なし）。顧客に支店があるときだけセレクトが出る */
  partnerBranchId: string | null;
  /** 顧客担当者（039）。選ぶと紙の宛名が「会社名／支店名／部署 氏名 様」（御中は付けない） */
  partnerContactId: string | null;
  subject: string;
  issueDate: string;
  siteAddress: string;
  notes: string;
  workPeriodText: string;
  workOutline: string;
  validityText: string;
  staffName: string;
  /** 差し替えの一文（035）。2回目からの回を直しているときだけ欄が出る */
  replaceSentence: string;
  /** 帳票の取引日（T2 の頭の欄「取引日」。null＝見積日で計算）。欄は taxDateHint のときだけ出す */
  taxPointDate: string | null;
  /** パートナー（#213、docs/40 §4）と見積の率。null＝パートナーなし。送るのはモジュール referral の会社だけ（OFF の会社は隠すだけ＝保存で消さない） */
  referrerId: string | null;
  referrerPct: number | null;
};
/** qty・basis・tk・unassigned は #130：takeoff の行の数量（DB が決めた値）・一式の印・行ごとの拾い出しの数・行に入れていない拾い出しの数 */
type Calc = { rateBase: Record<string, number | null>; qty: Record<string, number>; basis: Record<string, EstimateLine["lumpsumBasis"]>; tk: Record<string, number>; att: Record<string, { photos: number; memos: number }>; unassigned: number; amounts: Record<string, number>; sheetTotals: Record<string, number>; subtotal: number; taxTotal: number; total: number; taxes: Estimate["taxes"]; roundingMode: Estimate["revision"]["roundingMode"]; lineRoundingMode: Estimate["revision"]["lineRoundingMode"]; roundingNow: Estimate["roundingNow"] | null; costs: CostSummary | null; reimbursementTotal: number; taxDateHint: boolean; taxChanged: TaxChangedFromQuote | null };
/** 内訳 ⇄ 1枚 の往復のための印（画面の中だけ。保存しない。試作 v3.8） */
type Mark = "secHead" | "secEnd" | "coverRow";
/** 相違（#190）：なし（既定）／表示／変更行のみ */
type MarksMode = "none" | "show" | "only";
const MARKS_MODES: readonly MarksMode[] = ["none", "show", "only"];

/**
 * 行の中の欄の順路（#215、2026-10-19 射場さん。docs/10 §9.1）：Enter と Tab は同じ「次の欄へ」、Shift＋Tab は逆順。
 * 名称 → 規格 → 数量 → 単位 → 単価 →（数量・単価とも空のときだけ 金額）→（税区分の列のある会社だけ 税区分）→ 備考 → 仕入れ。
 * 欄が無いもの（金額の欄が近道でない・税区分の列が無い・社内用を出していない）は DOM に無いので飛ばす
 */
const ORDERS: Record<RowRole, string[]> = {
  // T2：税区分（f_tax）は金額の右の列。列の無い会社（使う機能 reducedTax が OFF）は欄が無いので飛ばす
  detail: ["f_name", "f_spec", "f_qty", "f_unit", "f_price", "f_amt", "f_tax", "f_note", "f_cost"],
  // #306：費目 → 率 → 対象（対象はボタン＝Enter・Tab は次の欄へ、Space・押すとポップ）。費目の無い会社（field なし）は欄が無いので飛ばす
  overhead_rate: ["f_name", "f_kind", "f_rate", "f_base", "f_spec", "f_tax", "f_note"],
  excluded: ["f_name", "f_spec", "f_label", "f_note"],
  discount: ["f_name", "f_spec", "f_price", "f_target", "f_tax", "f_note"],
  subtotal: ["f_name", "f_note"],
  group: ["f_name"],
  note: ["f_name"],
};
/** 数量・単価・仕入れ：↓ で下の行の同じ列へ（#215。#131 の「Enter で下の行」を改めた＝Enter は次の欄へ） */
const DOWN_FIELDS = new Set(["f_qty", "f_price", "f_cost"]);
/**
 * 仕入れのあと、単価が空の行だけ行の詳細の下段へ（#215）：粗利率 → パートナー（見積にパートナーが付いているときだけ）→ 上乗せ率 → 次の行。
 * 「円で」のときは 上乗せ額 → パートナー。見積だけ（請求書・テンプレートの行の詳細には無い＝DOM に無いので飛ばす）
 */
const PRICING_ORDER = ["f_margin", "f_markup_yen", "f_ref", "f_markup"];
/** 順路で止まるボタン（#306：率の行の対象）。ほかのボタン（拾い出しの合計の数量など）は順路で飛ばす */
const ORDER_BUTTONS = new Set(["f_base"]);
/** 順路で止まる欄（無い・押せない欄は null）。拾い出しの合計の数量（読むだけのボタン。#130）は飛ばす */
const fieldEl = (id: string): HTMLElement | null => {
  const el = document.getElementById(id);
  return el && (el.tagName !== "BUTTON" || ORDER_BUTTONS.has(id)) && !(el as HTMLInputElement).disabled ? el : null;
};
/** 欄に移る（Tab と同じく中身を選ぶ＝打てば置き換わる） */
const focusField = (el: HTMLElement) => {
  el.focus();
  if (el instanceof HTMLInputElement && el.value) el.select();
};

/** 2027-04-10 → 27/04/10（お知らせの1行の日付。T2・#320 ★8＝画面の年月日は年2桁） */
const jdateOf = (iso: string): string => ymdShort(iso.slice(0, 10)) || iso;

/** 「？」の中の文を1行ずつ並べる（#92：帯・確かめの使い方・仕組みの説明は「？」へ）。1つも無ければ null（「？」を出さない） */
export function helpText(lines: Array<string | null | undefined | false>): React.ReactNode | null {
  const xs = lines.filter((x): x is string => !!x);
  if (!xs.length) return null;
  return (
    <>
      {xs.map((x, i) => (
        <Fragment key={i}>
          {i ? <br /> : null}
          {x}
        </Fragment>
      ))}
    </>
  );
}

export function newRow(role: RowRole, patch: Partial<Row> = {}): Row {
  return {
    id: crypto.randomUUID(),
    role,
    name: role === "subtotal" ? "小計" : "",
    spec: "",
    note: "",
    quantity: null,
    unit: "",
    unitPrice: null,
    amount: null,
    rate: null,
    rateBase: role === "overhead_rate" ? "block" : null,
    rateBaseLineIds: [],
    amountLabel: role === "excluded" ? "別途" : null,
    discountTargetTotal: null,
    isLabor: false,
    costKind: null,
    costAmount: null,
    costOwn: false,
    sheetId: null,
    placeId: null,
    tradeId: null,
    workType: null,
    lineState: "final",
    provisionalNote: null,
    vendorPartnerId: null,
    noteKind: null,
    quantitySource: "manual",
    parentLineId: null,
    itemId: null,
    ...patch,
  };
}
const newSheet = (type: SheetInput["type"], name: string): Sheet => ({ id: crypto.randomUUID(), type, name, note: "", isLabor: false, inUse: true });
const sheetOf = (x: Estimate["sheets"][number]): Sheet => ({ id: x.id, type: x.type, name: x.name, note: x.note, isLabor: x.isLabor, inUse: x.inUse });

const fromServerLine = (l: EstimateLine): Row => ({
  id: l.id,
  role: l.role,
  name: l.name,
  spec: l.spec,
  note: l.note,
  quantity: l.quantity,
  unit: l.unit,
  unitPrice: l.unitPrice,
  amount: l.role === "discount" && l.discountTargetTotal == null ? l.amount : null,
  rate: l.rate,
  rateBase: l.rateBase,
  rateBaseLineIds: l.rateBaseLineIds,
  amountLabel: l.amountLabel,
  discountTargetTotal: l.discountTargetTotal,
  isLabor: l.isLabor,
  costKind: l.costKind,
  // #131 の2回目：仕入れ「自社」は API が仕入れ 0 で返す。画面では仕入れを空にして「自社」と出す（外注先を付けて自社を外したとき、0 円の手入力にしない）
  costAmount: l.costOwn ? null : l.costAmount,
  costOwn: l.costOwn ?? false,
  sheetId: l.sheetId,
  placeId: l.placeId ?? null,
  tradeId: l.tradeId ?? null,
  workType: l.workType ?? null,
  lineState: l.lineState ?? "final",
  provisionalNote: l.provisionalNote ?? null,
  vendorPartnerId: l.vendorPartnerId ?? null,
  noteKind: l.noteKind ?? null,
  quantitySource: l.quantitySource ?? "manual",
  parentLineId: l.parentLineId ?? null,
  // #118：写真の引き出しで作った行の品目。受け取ったまま送り返す（PC の保存で消えないように）
  itemId: l.itemId ?? null,
  // T1：税区分・行の取引日も受け取ったまま送り返す（PC の保存で標準に戻らないように。税区分の列は T2）
  taxCategory: l.taxCategory,
  taxDate: l.taxDate ?? null,
  // #213：パートナーの率（null＝見積の率に従う・0＝対象外）。受け取ったまま送り返す（パートナー手数料を OFF にした会社の保存で消えないように）
  referralPct: l.referralPct ?? null,
  // #233：{order}の行の内訳（field の会社の画面だけが出す。ほかの画面は受け取ったまま送り返す）
  laborBreakdown: l.laborBreakdown ?? null,
});
const fromServerHead = (e: Estimate): Head => ({
  clientId: e.client.id,
  clientName: e.client.name,
  clientShort: e.client.shortName ?? "",
  honorific: e.client.honorific,
  projectGroupId: e.projectGroup?.id ?? null,
  partnerBranchId: e.client.branch?.id ?? null,
  partnerContactId: e.client.contact?.id ?? null,
  subject: e.revision.subject,
  issueDate: e.revision.issueDate,
  siteAddress: e.revision.siteAddress,
  notes: e.revision.notes,
  workPeriodText: e.revision.workPeriodText,
  workOutline: e.revision.workOutline,
  validityText: e.revision.validityText,
  staffName: e.revision.staffName,
  replaceSentence: e.revision.replaceSentence,
  taxPointDate: e.revision.taxPointDate ?? null,
  referrerId: e.referral?.partnerId ?? null,
  referrerPct: e.referral?.pct ?? null,
});
const fromServerCalc = (e: Estimate): Calc => ({
  // #306：率の行の対象の金額（API の計算値＝fn_rate_base_amount。テンプレートには無い）
  rateBase: Object.fromEntries(e.lines.filter((l) => l.role === "overhead_rate" && l.rateBaseAmount !== undefined).map((l) => [l.id, l.rateBaseAmount ?? null])),
  qty: Object.fromEntries(e.lines.filter((l) => l.quantitySource === "takeoff").map((l) => [l.id, l.quantity ?? 0])),
  basis: Object.fromEntries(e.lines.map((l) => [l.id, l.lumpsumBasis ?? null])),
  tk: Object.fromEntries(e.lines.filter((l) => (l.takeoffCount ?? 0) > 0).map((l) => [l.id, l.takeoffCount ?? 0])),
  // #117：行の写真・社内メモの数（スマホで足したもの）。あるときだけ名称の右に印
  att: Object.fromEntries(e.lines.filter((l) => (l.photoCount ?? 0) > 0 || (l.memoCount ?? 0) > 0).map((l) => [l.id, { photos: l.photoCount ?? 0, memos: l.memoCount ?? 0 }])),
  unassigned: e.takeoffs?.unassigned ?? 0,
  amounts: Object.fromEntries(e.lines.map((l) => [l.id, l.amount])),
  sheetTotals: Object.fromEntries(e.sheets.map((x) => [x.id, x.subtotal])),
  subtotal: e.revision.subtotal,
  taxTotal: e.revision.taxTotal,
  total: e.revision.total,
  taxes: e.taxes,
  roundingMode: e.revision.roundingMode,
  lineRoundingMode: e.revision.lineRoundingMode,
  roundingNow: e.roundingNow ?? null,
  costs: e.costSummary ?? null,
  // T2：立替金の合計（合計欄の「立替金」の行）・取引日の欄を出すか・見積 → 請求書で税率が変わったか（請求書だけ）
  reimbursementTotal: e.revision.reimbursementTotal ?? 0,
  taxDateHint: !!e.taxDateHint,
  taxChanged: (e as Estimate & { taxChangedFromQuote?: TaxChangedFromQuote | null }).taxChangedFromQuote ?? null,
});

const hhmm = () => new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
/** 文字を打つ欄（⌘Z はブラウザの文字の取り消しに任せる。#140）。チェックボックス・ボタン・選ぶ欄は含めない */
const NON_TEXT = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);
const isTextField = (el: Element | null): boolean =>
  !!el && ((el instanceof HTMLInputElement && !NON_TEXT.has(el.type)) || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable));

export function EstimatePage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["estimate", id], queryFn: () => api<Estimate>("GET", `/estimates/${id}`), retry: false });
  const untrash = useUntrash();
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  // #200：ゴミ箱の見積書（止めの画面＋復元。復元したら読み直してその見積書を開く）
  const trashed = trashedOf(q.error);
  if (trashed) return <TrashedStop info={trashed} onRestore={() => void untrash(id).then((r) => r && void q.refetch())} />;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/estimates">見積書一覧へ</Link>
        </p>
      </div>
    );
  }
  // 訂正版（#27）：版が変わったら（つくる・出す・やめる）エディタを作り直す（行の id が新しくなるため）
  return <Editor key={`${q.data.id}:${q.data.revision.id}${q.data.revising ? ":r" : ""}`} initial={q.data} />;
}

/**
 * 雛形モード（023）：同じエディタで、保存先だけ雛形（/estimate-templates/:id）にする。
 * 紙の上（顧客・案件・番号・日付）と 発行・PDF は出さず、タイトル欄を1つ持つ。紙の備考（表の下の欄）は見積書と同じ欄（#197）。
 * save は Estimate の形（タイトル＝subject・紙の備考＝notes）で返す。行の編集・計算の見え方は見積と同じ。
 * 画面は設定の2列に入れず全幅・左のメニューは畳んで始める（#197、app.tsx の lineEntry。docs/78）
 */
export type TemplateBackend = { save: (body: { subject: string; notes: string; sheets: SheetInput[]; lines: LineInput[] }) => Promise<Estimate> };

/**
 * 請求書モード（024、縦串5）：同じエディタで 行・内訳 だけをここで書き、保存先を請求書（PUT /invoices/:id の lines/sheets）にする。
 * 紙の上（宛名・件名・請求日・支払期限）、右上のボタン、帯、備考は請求書の画面（Invoice.tsx）が描いて渡す。
 * 行の編集・候補の引き出し・内訳・計算の見え方は見積と同じ（明細を写したあとに直せる：射場さん 2026-09-16）
 */
export type InvoiceBackend = {
  save: (body: { sheets: SheetInput[]; lines: LineInput[] }) => Promise<Estimate>;
  /** 紙の上。合計（税込）はエディタが計算結果を渡す */
  head: (ctx: { total: number }) => React.ReactNode;
  /** 右上（「顧客に出す」「PDF を開く」） */
  actions: React.ReactNode;
  /** 見出しの右の状態チップとボタン（発行済・下書き（番号ありも）・分割の回・入金・取消・領収書。#171 で題の下の帯から移した） */
  chips?: React.ReactNode;
  /** 題の右の「？」に入れる文（帯の使い方・仕組み。#92）。エディタの「自動で保存」などと一緒に1つの「？」にまとめる */
  help?: Array<string | null | undefined | false>;
  /** 明細の下（備考・捨てる） */
  footer?: React.ReactNode;
  /** 「発行」「PDF」の前に書きかけの行を送り終えるのを待つ ready を受け取る（#177。保存できていなければ理由の文、できていれば null） */
  register?: (ready: () => Promise<string | null>) => void;
  /*
   * 版（データが消えうる不具合の直し、2026-10-13）：版は請求書の画面が持つ（明細と紙の上の欄の2つの保存で1つ）。
   * version＝いまの版（並べ方の保存に付ける）／reload＝最新を読む（読み直し）／stale＝ぶつかった（紙の上の欄も止める）／
   * registerStale＝紙の上の欄の保存がぶつかったとき、この画面の「読み直しますか？」を開く口を渡す／synced＝最新に合わせた（版と紙の上の欄を重ねる）
   */
  version?: () => string;
  reload?: () => Promise<Estimate>;
  stale?: () => void;
  registerStale?: (open: () => void) => void;
  synced?: (e: Estimate) => void;
  /**
   * {order}の画面（#233、docs/93 §4.3）：請求書と同じ作りで、題・戻り先・控えの鍵（React Query）・一覧の鍵を変える。
   * 社内用の列は出さない（この紙の金額が仕入れそのもの）・表示順は手動だけ・field の会社は行の詳細に内訳4つ（材料費・労務費・法定福利費・安全衛生経費）
   */
  doc?: { kind: "order" | "payment_notice" | "delivery_note" | "acceptance"; title: string; queryKey: string; listKey: string; back: { to: string; label: string }; labor: boolean };
  /** 明細を直せない（#266：納品書の消費税を足す請求書＝明細は納品書で直す）。頭の欄は請求書の画面が持つ */
  linesReadOnly?: boolean;
  /**
   * 帳票の頭（#245、docs/94 B-4 案B）：渡すと頭は DocHead の3段（1・2段目＝主の色の帯・3段目＝白）。actions は使わず primary・menu に分ける。
   * 合計（税込）・税抜・消費税はエディタの計算で2段目の右に出す。head（紙の上の欄）には頭に移さない分（回の帯・条件の帯など）だけを描く
   */
  docHead?: {
    kind: Area;
    kindLabel: string;
    /** 2段目の題（件名の欄。読むだけの帳票は文字） */
    title: React.ReactNode;
    /** 3段目の左（名前が上・値が下の項目＝DocField） */
    meta: React.ReactNode;
    /** 3段目の右（表示順・紙の形・詳細 など） */
    tools?: React.ReactNode;
    /** 1段目の右の主ボタン（発行・PDF 表示）と主なボタン（HeadActions part="main"） */
    primary?: React.ReactNode;
    /** 1段目のいちばん右の「⋯」（HeadActions part="more"） */
    menu?: React.ReactNode;
    /** 合計を出さない帳票は false */
    showTotal?: boolean;
  };
};

import { BackButton, namePageNow, useBackNav, usePageName } from "../components/BackButton.jsx";
import { docShort, type Area } from "../components/AreaIcon.jsx";
import { DocHead } from "../components/DocHead.jsx";
import { EditingChip, useEditors } from "../components/Editing.jsx";
import { useSaveQueue } from "../lib/saveQueue.jsx";
import { usePdfTab } from "../lib/pdfOpen.js";
import { OrderAddLineDialog } from "./orders/bundle.jsx";
import { usePcOnly } from "../lib/pcOnly.js";
import { readStash, registerUnsaved, removeStash, writeStash } from "../lib/autoLogout.js";

import { ymdShort } from "../lib/dates.js";
/** 自動ログアウトで退避した見積書の書きかけ（#189 ④）。base＝最後にサーバーと同じだった形、mine＝その時の画面の形 */
type EstimateStash<H, R, S> = { v: 1; revisionId: string; base: { head: H; rows: R[]; sheets: S[] }; mine: { head: H; rows: R[]; sheets: S[] } };

export function Editor({ initial, template, invoice }: { initial: Estimate; template?: TemplateBackend | undefined; invoice?: InvoiceBackend | undefined }) {
  /** 見積以外（雛形・請求書）：紙の上・案件・発行・PDF は見積のものを出さない */
  const alt = !!template || !!invoice;
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  // 前の回の控え（PDF）を新しいタブで：押した瞬間に「PDF 作成中…」、作成中は押せない（docs/72 §10）
  const pdfTab = usePdfTab();
  const L = useLabels(); // 会社の言葉（027）：顧客・工事名・案件…
  /** {order}（#233）：請求書と同じ作りの別の帳票 */
  const orderDoc = invoice?.doc ?? null;
  /** 控えの鍵（React Query）：見積・請求書・{order} */
  const docQ = orderDoc ? orderDoc.queryKey : invoice ? "invoice" : "estimate";
  const pageKind = template ? TEMPLATE_MSG.editTitle : invoice ? (orderDoc?.title ?? "請求書") : "見積書";
  usePageName(pageKind, template ? null : docShort(pageKind, initial.displayNo ?? initial.documentNo));
  const createInvoice = useCreateInvoice(toast);
  const fresh = !!(loc.state as { fresh?: boolean } | null)?.fresh;
  // 前の画面からの一言（新しい見積書・案件の「見積追加」・外注見積の確定など。039・047）は通知（#168、App の NoticeHost が出す）
  // 訂正版の下書き（#27）を書いているときは、出したあとでも編集できる（編集先はその版）
  const readOnly = (initial.status !== "draft" && !initial.revising) || !!invoice?.linesReadOnly;
  /** 新しい行の仕入れの既定（#131 の2回目、設定 → 見積）：自社なら新しい明細の行は「自社」で始まる。請求書・雛形は未定のまま */
  const ownDefault = !alt && initial.costDefault === "own";
  const mkRow = (role: RowRole, patch: Partial<Row> = {}): Row => newRow(role, { ...(role === "detail" && ownDefault ? { costOwn: true } : {}), ...patch });
  const openDraftPdf = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    // 書きかけを送り終えてから紙の画面へ。保存できていなければ移らずに理由を出す（#177）
    void sq.ready().then((why) => (why == null ? nav(pdfUrl) : toast(saveGateText("pdf", why), undefined, "error")));
  };

  const [head, setHead] = useState<Head>(() => fromServerHead(initial));
  const [rows, setRowsState] = useState<Row[]>(() => initial.lines.map(fromServerLine));
  /** 仕入れの出どころ（047）：外注見積から入れた行 → 「見積 沼田電気工事 N-2026-03」。initial（サーバーの答え）から引く */
  const costRefs = useMemo(() => new Map(initial.lines.map((l) => [l.id, l.costRef])), [initial.lines]);
  /* ---- 外注見積から見積を作る（049、docs/34、#84）：帯の進捗・取込の1枚・原本 ---- */
  const me = useMe();
  /** パートナー手数料（#213、docs/40）：モジュール referral の会社の見積だけ（請求書・雛形には無い）。OFF の会社は隠すだけ（値は残す） */
  const referralOn = !alt && hasModule(me.data, "referral");
  // 外注見積の取込は使う機能「{vendorQuote}の取込」（#142 段3）。OFF の会社は帯・行の「取込」・「…」の項目を出さない
  const vprog = useQuery({ queryKey: ["vendor-progress", initial.id], queryFn: () => api<VendorProgress>("GET", `/estimates/${initial.id}/vendor-progress`), enabled: !alt && uses(me.data, "vendorQuote"), staleTime: 5_000 });
  // 見積依頼（#119、docs/56）：外注先ごとの依頼・行のしるし。使う機能 vendorQuote の会社だけ（見積だけ。請求書・雛形には無い）
  // 見積依頼（#119）は使う機能 rfq の会社だけ読む（#149：外注見積の取込 vendorQuote とは別のスイッチ）
  const rfqs = useQuery({ queryKey: ["estimate-rfqs", initial.id], queryFn: () => api<EstimateRfqs>("GET", `/estimates/${initial.id}/rfqs`), enabled: !alt && uses(me.data, "rfq"), staleTime: 5_000 });
  /** 頭の帯の「見積依頼 n件 ›」の数（#275：取消は数えない） */
  const liveRfqCount = rfqs.data?.rfqs.filter((r) => r.status !== "cancelled").length ?? 0;
  // #233 2a：見積の行から作った{order}（行のしるし「発注済 PO-0001」・「{order}に追加…」）。モジュール sales の会社の見積だけ
  const orderOn = !alt && hasModule(me.data, "sales");
  // {deliveryNote}（#266、docs/96 の起こし方1）：受注した見積（分割の予定なし）・sales かつ使う機能「{deliveryNote}」の会社だけ。残・作成の窓・束ね請求
  const dnq = useQuoteDelivery({
    quote: { id: initial.id, documentNo: initial.displayNo ?? initial.documentNo, subject: initial.revision.subject, clientName: initial.client.name, honorific: initial.client.honorific, siteAddress: initial.revision.siteAddress ?? "", groupId: initial.projectGroup?.id ?? null },
    enabled: orderOn && uses(me.data, "deliveryNote") && initial.status !== "draft" && !initial.revising && initial.order?.status === "ordered" && !initial.order.plan,
    onDirectInvoice: () => void createInvoice(initial.id),
  });
  const orderMarksQ = useQuery({ queryKey: ["estimate-order-marks", initial.id], queryFn: () => api<EstimateOrderMarks>("GET", `/estimates/${initial.id}/order-marks`), enabled: orderOn, staleTime: 5_000 });
  const [rfqPick, setRfqPick] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [askRfqCancel, setAskRfqCancel] = useState<number | null>(null);
  const [vimp, setVimp] = useState<{ quoteId: string; targets: string[] } | null>(null);
  const [orig, setOrig] = useState<OrigState | null>(null);
  /** 粗利率・上乗せ（決めごと5）：既定は会社の設定、この見積の中では変えた値を覚える */
  const [vPricing, setVPricing] = useState<VendorImportPricing | null>(null);
  const pricingBase: VendorImportPricing = vPricing ?? { kind: "margin", value: vprog.data?.marginPct ?? 20 };
  /** #213：パートナー ON の見積は「＋パートナー」（既定は見積の率）。外した見積は送らない */
  const pricingNow: VendorImportPricing =
    pricingBase.kind === "margin"
      ? referralOn && head.referrerId && head.referrerPct != null
        ? { ...pricingBase, referralPct: pricingBase.referralPct ?? head.referrerPct }
        : { kind: "margin", value: pricingBase.value }
      : pricingBase;
  /**
   * 出した回の仕入れだけ直す（PUT /estimates/:id/costs。決めごと11）。紙は変わらない。
   * #202：版を付けて送り（ほかで変わっていたら 409 →「再読込しますか？」）、答えの版を覚える。行の保存と重ねない（exclusive）
   */
  const saveIssuedCost = async (lineId: string, v: number | null) => {
    try {
      // #302（B3）：請求書も同じ（社内用の列は発行後でも直せる＝PUT /invoices/:id/costs）
      const e = await sq.exclusive(() => api<Estimate & { message: string }>("PUT", `/${docPath}/${initial.id}/costs`, { costs: [{ lineId, costAmount: v }], version: docVersion() }));
      afterInternal(e);
      putRows((rs) => rs.map((x) => (x.id === lineId ? { ...x, costAmount: v, costOwn: false } : x)));
      toast(e.message);
    } catch (err) {
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    }
  };
  /**
   * 出した回の行の仕入元だけ直す（#302 B3：PUT /estimates|invoices/:id/line-vendor。紙は変わらない）。行の「…」の「仕入元変更」→ 取引先検索のドロアー。
   * 付けると自社の仕入れは未入力に戻る（DB）。書ける回は行の詳細の仕入元の欄（VendorPick）で直す
   */
  const [vendorPickLine, setVendorPickLine] = useState<string | null>(null);
  const saveIssuedVendor = async (lineId: string, vendorPartnerId: string | null) => {
    try {
      const e = await sq.exclusive(() => api<Estimate>("PUT", `/${docPath}/${initial.id}/line-vendor`, { lineId, vendorPartnerId, version: docVersion() }));
      afterInternal(e);
      const l = e.lines.find((x) => x.id === lineId);
      putRows((rs) => rs.map((x) => (x.id === lineId ? { ...x, vendorPartnerId, ...(l ? { costOwn: l.costOwn, costAmount: l.costAmount } : {}) } : x)));
      toast(t(vendorPartnerId ? ESTIMATE_PC_MSG.vendorChanged : ESTIMATE_PC_MSG.vendorCleared));
    } catch (err) {
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    }
  };
  /** 行の仕入元を変える（「…」の「仕入元変更」「仕入元解除」）：出した回は社内用の口（line-vendor）、下書きは画面の行を直して自動保存（請求書の下書き＝#302 B3） */
  const setVendorOf = (lineId: string, vendorPartnerId: string | null) => {
    if (readOnly) {
      void saveIssuedVendor(lineId, vendorPartnerId);
      return;
    }
    const i = latest.current.rows.findIndex((x) => x.id === lineId);
    if (i < 0) return;
    upd(i, { vendorPartnerId });
    toast(t(vendorPartnerId ? ESTIMATE_PC_MSG.vendorChanged : ESTIMATE_PC_MSG.vendorCleared));
  };
  const [sheets, setSheetsState] = useState<Sheet[]>(() => initial.sheets.map(sheetOf));
  /** いま開いている紙："cover" か内訳の id。内訳を使わないときは無視 */
  const [cur, setCur] = useState<string>("cover");
  /** 表紙の「内訳の合計」の行を書いている（名前・備考） */
  const [secEdit, setSecEdit] = useState<string | null>(null);
  const marks = useRef<Record<string, Mark>>({});
  /** 1枚にまとめる前の紙の id（見出しの行の id → 内訳の紙の id、表紙の id）。分け直すとき同じ紙に戻す（同じ紙だと分かるように。038） */
  const origSheets = useRef<{ bySecHead: Record<string, string>; cover?: string | undefined }>({ bySecHead: {} });
  const [calc, setCalc] = useState<Calc>(() => fromServerCalc(initial));
  // T2：請求書の紙の上（請求日・取引日）は請求書の画面が保存する。税率が変わることがあるので、その答え（initial）で合計・お知らせを描き直す
  useEffect(() => {
    if (invoice) setCalc(fromServerCalc(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice ? initial : null]);
  const [edit, setEdit] = useState<number | null>(null);
  /* ---- 見積の区分（#129、docs/47）：並べ方・場所と工種の列・まとめて付ける ---- */
  const [axis, setAxis] = useState<LayoutAxis>(initial.layoutAxis ?? "manual");
  const [axSel, setAxSel] = useState<Set<string>>(() => new Set());
  const axLists = useAxesLists(initial.id, { estimate: !alt }, (m) => toast(m, undefined, "error"));
  /* ---- 拾い出し（#130、docs/47 §2.2）：数量の欄のしるしから開く引き出し。lineId が null＝行に入れていない拾い出しだけ ---- */
  const [tkOpen, setTkOpen] = useState<{ lineId: string | null } | null>(null);
  /** 行の写真・メモの一覧の窓（#117） */
  const [attOpen, setAttOpen] = useState<string | null>(null);
  /** 頭の帯の「詳細」（工事場所・工期・工事概要・有効期限・担当）。新しい見積（作った直後）は開いた状態で始める（#138） */
  const [more, setMore] = useState(() => fresh && !alt);
  /** 行の詳細の下段の 率で／円で（#213。画面の中だけ・保存しない） */
  const [pricingMode, setPricingMode] = useState<PricingMode>("rate");
  /** 見積の詳細の「パートナー」のスイッチを ON にして、まだ選んでいない（#213） */
  const [refPicking, setRefPicking] = useState(false);
  const [pickClient, setPickClient] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  /** 取消し（021）：出した見積を下書きに戻す確認の画面。done は戻した番号（直後は通知、そのあとは見出しのチップ「下書き」。#171・#273・#278） */
  const [unissue, setUnissue] = useState<{ open: boolean; busy: boolean; error?: string | undefined; done?: string | undefined }>({ open: false, busy: false });
  /** モジュール field（現場力だけ。#142 段2、docs/52 §2）：労務費・何の費用か・材工区分・施主・写真を出す。無い会社は隠すだけ（値は残す） */
  const field = hasModule(me.data, "field");
  /** 使う機能（#142 段3、docs/52 §3）：OFF の機能のボタン・欄は隠すだけ（判定は core/uses.ts 1か所） */
  const can = (k: UseKey) => uses(me.data, k);
  /* ---- 顧客に出す（縦串4a）：確認の画面。初回だけ会社名（住所・電話は空でも出せる） ---- */
  const [issueDlg, setIssueDlg] = useState<{ open: boolean; company: { name: string; address: string; phone: string }; error?: string | undefined; busy: boolean; justIssued?: IssueResult | undefined }>({ open: false, company: { name: "", address: "", phone: "" }, busy: false });
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  /**
   * 自動保存（#177：送る順番・「送っている最中」の印・発行や PDF の前の確かめ・離れるときの確かめは共通の仕組み lib/saveQueue.tsx）。
   * 打ち終わって 0.5 秒後（打ち続けても 1.5 秒に1回）に丸ごと PUT。返ってきた金額で紙を描き直す（sendDoc）
   */
  const sq = useSaveQueue({
    send: () => sendDoc(),
    onError: (err) => {
      // #131：ほかで変わっていた（スマホの行の保存など）。上書きせずに止めて「読み直しますか？」。
      // 請求書も同じ（2026-10-13：ほかのタブ・ほかの人の保存とぶつかった＝INVOICE_STALE。紙の上の欄の保存も止める）
      if (isStale(err)) {
        enterStale();
        return { msg: ESTIMATE_PC_MSG.staleSaving, then: "stop" };
      }
    },
    blocked: () => staleRef.current,
    // ページを閉じるときの keepalive（#93）は見積の下書きだけ（請求書・雛形は別の保存）
    ...(template || invoice
      ? {}
      : {
          keepalive: () => {
            const body = JSON.stringify(estimateBody());
            return body.length > 60_000 ? null : { url: `/api/v1/estimates/${initial.id}`, method: "PUT" as const, body };
          },
        }),
  });
  const save = sq.state;
  const setSave = sq.setState;
  const markDirty = sq.mark;
  /**
   * 戻る（#98、docs/37）：直前の画面があればそこ。無ければ その{project}（請求書は請求書のタブ）・雛形は見積書の雛形の一覧（#197）。
   * ふだんは自動で保存するので確かめない。保存が断られて送れていない間（400・ほかで変更・通信できない）だけ、離れる前に確かめる。
   * 送る前・送っている最中は送り終えてから戻る（#176 B3・#177：打った分を知らせなく失わない。画面の中のリンク・ページを閉じるときも同じ）
   */
  const unsent = sq.unsent;
  const back = useBackNav(
    template
      ? { to: "/settings/templates", label: TEMPLATE_MSG.listTitle }
      : orderDoc
        ? orderDoc.back
        : initial.projectGroup
        ? { to: `/groups/${initial.projectGroup.id}${invoice ? "?tab=invoices" : ""}`, label: initial.projectGroup.name }
        : invoice
          ? { to: "/invoices", label: "請求書" }
          : { to: "/estimates", label: "見積書" },
    unsent,
  );
  /* ---- 版（#131、docs/47 §12）：丸ごとの保存に版を付ける。スマホの行の保存などで版が進んでいたら 409 → 「読み直しますか？」→ 読み直して重ねる ---- */
  const versionRef = useRef(initial.updatedAt);
  /** #302（B3）：社内用の列だけの保存（仕入れ・仕入元）の口と版。請求書は請求書の画面が版を持つ（InvoiceBackend.version・synced） */
  const docPath = invoice ? "invoices" : "estimates";
  const docVersion = () => (invoice ? invoice.version?.() : versionRef.current);
  const afterInternal = (e: Estimate) => {
    if (invoice) invoice.synced?.(e);
    else versionRef.current = e.updatedAt;
    qc.setQueryData([invoice ? "invoice" : "estimate", initial.id], e);
  };
  /** 編集中（#202、docs/82 §4）：直せる見積（下書き・訂正中）を開いている間だけ合図を送り、自分以外を見出しのチップに（請求書は請求書の画面） */
  const editors = useEditors(alt ? undefined : initial.id, !readOnly);
  /** 最後にサーバーと同じだった中身（読み直しのとき「この画面で直した分」を見分ける元） */
  const baseRef = useRef<{ head: Head; rows: Row[]; sheets: Sheet[] }>({ head: fromServerHead(initial), rows: initial.lines.map(fromServerLine), sheets: initial.sheets.map(sheetOf) });
  const staleRef = useRef(false);
  const [stale, setStale] = useState<{ busy: boolean } | null>(null);
  /** ほかで変わっていた（409）。見積の版・請求書の版・拾い出しの保存（見積の版）で同じ扱い */
  const isStale = (err: unknown) => err instanceof ApiError && (err.code === "ESTIMATE_STALE" || err.code === "INVOICE_STALE" || err.code === "ORDER_STALE");
  /** 送るのを止めて「読み直しますか？」を開く（請求書は紙の上の欄の保存も止める） */
  const enterStale = () => {
    staleRef.current = true;
    setStale({ busy: false });
    invoice?.stale?.();
  };
  /** 読み直しが済んだ回数（拾い出しの引き出しが、断られた直しを送り直す合図） */
  const [reloadTick, setReloadTick] = useState(0);
  /**
   * 保存のあとに読み直す GET（性能の改善 A、2026-10-13、scripts/loadtest）：前は保存のたびに 案件の一覧・外注見積の進み・見積依頼 の3本を
   * 必ず読み直していた（保存1回の DB の重さの約 25%）。サーバーの答えの中で、その GET の中身に関わる所が前の答えから変わったときだけ読み直す。
   *   案件の一覧（GET /partners/:id/groups）＝顧客・案件・工事名・工事場所（自動の案件の名前と現場住所は工事名・工事場所に合わせて変わる。見積の数は案件を移ったとき）
   *   外注見積の進み（GET …/vendor-progress、v_vendor_quote_progress）＝案件と、仕入れの出どころが外注見積の行の 系譜・金額・仕入れ
   *   見積依頼（GET …/rfqs、fn_rfq_line_marks・行の数）＝外注先の付いた 明細・文章の行の 系譜・外注先・種類
   */
  const followSigOf = (e: Estimate) => ({
    groups: JSON.stringify([e.client.id, e.projectGroup?.id ?? null, e.revision.subject, e.revision.siteAddress]),
    vendor: JSON.stringify([e.projectGroup?.id ?? null, e.lines.filter((l) => l.costSource === "vendor_quote").map((l) => [l.lineageId, l.amount, l.costAmount])]),
    rfq: JSON.stringify(e.lines.filter((l) => l.vendorPartnerId && (l.role === "detail" || l.role === "note")).map((l) => [l.lineageId, l.vendorPartnerId, l.role])),
  });
  const followSig = useRef(followSigOf(initial));
  /** 見積依頼に追加している間は、保存の答えで見積依頼を読み直さず、追加のあとで1回だけ読み直す（addToRfq） */
  const rfqFollowHold = useRef({ on: false, held: false });
  const refetchFollows = (e: Estimate) => {
    if (template || invoice) return;
    const prev = followSig.current;
    const next = followSigOf(e);
    followSig.current = next;
    if (next.groups !== prev.groups) void qc.invalidateQueries({ queryKey: ["groups"] });
    if (next.vendor !== prev.vendor) void qc.invalidateQueries({ queryKey: ["vendor-progress", initial.id] }); // 049：行を消したら外注の行は「残り」に戻る
    if (next.rfq !== prev.rfq) {
      if (rfqFollowHold.current.on) rfqFollowHold.current.held = true;
      else void qc.invalidateQueries({ queryKey: ["estimate-rfqs", initial.id] });
    }
  };
  const [focusTick, setFocusTick] = useState(0);
  const pendingFocus = useRef<{ id: string | readonly string[]; keep: boolean } | null>(null);

  /* ---- 自動保存：打ち終わって 0.5 秒後に PUT。返ってきた金額で紙を描き直す ---- */
  const latest = useRef({ head, rows, sheets });
  latest.current = { head, rows, sheets };
  /**
   * 行・内訳を変えるのは必ずここ（#140）。送る中身（latest）もすぐ入れ替える：同じ手の中で2回変えても（空の行を落としてから足すなど）、
   * 元に戻すの控え（record）が正しい形を取れるように
   */
  function putRows(u: Row[] | ((rs: Row[]) => Row[])) {
    const next = typeof u === "function" ? u(latest.current.rows) : u;
    latest.current = { ...latest.current, rows: next };
    setRowsState(next);
  }
  function putSheets(u: Sheet[] | ((xs: Sheet[]) => Sheet[])) {
    const next = typeof u === "function" ? u(latest.current.sheets) : u;
    latest.current = { ...latest.current, sheets: next };
    setSheetsState(next);
  }
  /* ---- 元に戻す／やり直す（#140、docs/47 §15・docs/10 §5.4）：画面の中の 行と内訳 の状態を 50 手まで。
     入力・追加・削除・移動・並べ替え・内訳へ移動・区分や場所の変更。変える前に record() で1手を控える（同じ行の同じ欄を打ち続けているあいだは1手）。
     サーバーで起きたこと（拾い出し・取込・外す・端数処理・読み直し）のあとは履歴を切る（cutHistory）。出すと画面ごと作り直す（履歴も消える）。
     訂正中の「戻す」「前回の内容に復元」は ↶ で戻せる（#195：復元の前の形を1手として控える＝doRestore）。
     元に戻したあとは丸ごとの保存（版つき）で送る ---- */
  /** ref（#213）：見積のパートナーと率（付ける・外す・率を変えると行の単価も変わるので、行と一緒に1手で戻す） */
  type Snap = { rows: Row[]; sheets: Sheet[]; marks: Record<string, Mark>; orig: { bySecHead: Record<string, string>; cover?: string | undefined }; ref: { id: string | null; pct: number | null } };
  const hist = useRef<UndoStack<Snap> | null>(null);
  hist.current ??= new UndoStack<Snap>(
    (a, b) =>
      a.ref.id === b.ref.id && a.ref.pct === b.ref.pct &&
      ((a.rows === b.rows && a.sheets === b.sheets) || (JSON.stringify(a.rows) === JSON.stringify(b.rows) && JSON.stringify(a.sheets) === JSON.stringify(b.sheets))),
    50,
  );
  const [, setHistTick] = useState(0);
  const snapNow = (): Snap => ({ rows: latest.current.rows, sheets: latest.current.sheets, marks: marks.current, orig: origSheets.current, ref: { id: latest.current.head.referrerId, pct: latest.current.head.referrerPct } });
  /** 行・内訳を変える前に呼ぶ。key が前と同じ（同じ行の同じ欄を打ち続けている）なら控えない */
  const record = (key: string | null = null) => {
    if (readOnly) return;
    if (hist.current!.record(snapNow(), key)) setHistTick((t) => t + 1);
  };
  /** 打ち続けのまとまりを切る（行を開く・閉じる・移るとき） */
  const breakTyping = () => hist.current!.breakTyping();
  const cutHistory = () => {
    hist.current!.clear();
    setHistTick((t) => t + 1);
  };
  /** いまと同じ形の控え（空の行を落として元に戻ったときなど）は捨てる */
  const dropSameTop = () => {
    if (hist.current!.dropSame(snapNow())) setHistTick((t) => t + 1);
  };
  /** 行・紙の系譜（037）。直しているときだけ。覚えておいて保存のとき送り返す（「元に戻す」で戻した行が同じ行だと分かるように） */
  const lineageRef = useRef<{ lines: Record<string, string>; sheets: Record<string, string> }>({ lines: { ...initial.lineage?.lines }, sheets: { ...initial.lineage?.sheets } });
  const rememberLineage = (e: Estimate) => {
    Object.assign(lineageRef.current.lines, e.lineage?.lines);
    Object.assign(lineageRef.current.sheets, e.lineage?.sheets);
  };
  /** 見積の保存（PUT /estimates/:id）の本文。いま画面にあるもの（latest）から作る */
  const estimateBody = () => {
    const { head: h, rows: r, sheets: sh } = latest.current;
    return {
      clientId: h.clientId,
      projectGroupId: h.projectGroupId,
      ...(h.newGroup ? { newGroup: true } : {}),
      partnerBranchId: h.partnerBranchId,
      partnerContactId: h.partnerContactId,
      subject: h.subject,
      issueDate: h.issueDate,
      siteAddress: h.siteAddress,
      notes: h.notes,
      workPeriodText: h.workPeriodText,
      workOutline: h.workOutline,
      validityText: h.validityText,
      staffName: h.staffName,
      taxPointDate: h.taxPointDate,
      // #130：下書き（1回目）でも、拾い出しのぶら下がる行の系譜は送り返す（消した行を「元に戻す」で戻したとき、拾い出しが付いたままになるように）
      ...(initial.revising ? { replaceSentence: h.replaceSentence, lineage: lineageRef.current } : Object.keys(lineageRef.current.lines).length ? { lineage: lineageRef.current } : {}),
      // #213：パートナー（モジュール referral の会社だけ送る。OFF の会社は送らない＝変えない）
      ...(referralOn ? { referrerId: h.referrerId, referrerPct: h.referrerPct } : {}),
      sheets: sh,
      lines: r,
      version: versionRef.current,
    };
  };
  /**
   * 丸ごとの保存を1本送る（いつ・何本送るか、断られたときの扱いは useSaveQueue。#177）。いま画面にあるもの（latest）を送り、返ってきた金額で紙を描き直す
   */
  const sendDoc = async (): Promise<void> => {
    const { head: h, rows: r, sheets: sh } = latest.current;
    const e = template
      ? await template.save({ subject: h.subject, notes: h.notes, sheets: sh, lines: r })
      : invoice
      ? await invoice.save({ sheets: sh, lines: r })
      : await api<Estimate>("PUT", `/estimates/${initial.id}`, estimateBody());
    rememberLineage(e);
    setCalc(fromServerCalc(e));
    if (!template) {
      if (!invoice) versionRef.current = e.updatedAt; // 請求書の版は請求書の画面が持つ（InvoiceBackend.save）
      baseRef.current = { head: h, rows: r, sheets: sh };
    }
    if (initial.revising) {
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] }); // 回の帯の下書きの合計
      void qc.invalidateQueries({ queryKey: ["estimate-marks", initial.id] }); // 直しているときの印（037）。保存のたびに取り直す
    }
    if (invoice) {
      qc.setQueryData([docQ, initial.id], e);
      void qc.invalidateQueries({ queryKey: [orderDoc?.listKey ?? "invoices"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
    } else if (!template) {
      qc.setQueryData(["estimate", initial.id], e);
      // この画面に出ていない一覧は印を付けるだけ（読み直すのは開いたとき）
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["group-list"] });
      // この画面に出ている 案件・外注見積の進み・見積依頼 は、関わる所が変わったときだけ読み直す（性能の改善 A）
      refetchFollows(e);
      // 顧客を変えて新しい案件に入り直した：その案件の id を覚えて、次の保存でまた作らない
      if (h.newGroup && e.projectGroup) {
        const gid = e.projectGroup.id;
        setHead((x) => (x.newGroup && x.clientId === h.clientId ? { ...x, newGroup: false, projectGroupId: gid } : x));
        const lh = latest.current.head;
        if (lh.newGroup && lh.clientId === h.clientId) latest.current = { ...latest.current, head: { ...lh, newGroup: false, projectGroupId: gid } };
      }
    }
  };
  // 請求書：紙の上の欄の保存がぶつかったら、この画面の「読み直しますか？」を開く（2026-10-13）
  const registerStale = invoice?.registerStale;
  useEffect(() => {
    registerStale?.(() => {
      staleRef.current = true;
      setStale({ busy: false });
    });
  }, [registerStale]);
  // 請求書：「発行」「PDF」の前に、行の書きかけを送り終えるのを待つ（#177）
  const registerInvoice = invoice?.register;
  useEffect(() => {
    registerInvoice?.(() => sq.ready());
  }, [registerInvoice]); // sq.ready は画面のあいだ同じ関数

  /* ---- 状態の更新（変えたら必ず markDirty）。文字や数字を打つ欄だけ待ち、それ以外はすぐ送る ---- */
  const TYPING = new Set(["name", "spec", "note", "unit", "quantity", "unitPrice", "amount", "rate", "discountTargetTotal", "costAmount", "amountLabel", "subject", "siteAddress", "notes", "workPeriodText", "workOutline", "validityText", "staffName", "laborBreakdown"]);
  const typingOnly = (patch: object) => Object.keys(patch).every((k) => TYPING.has(k));
  const setH = (patch: Partial<Head>) => {
    setHead((h) => ({ ...h, ...patch }));
    // 送る中身（latest）にもすぐ入れておく。非同期の続き（その場で登録した顧客など）から呼ばれると、
    // 描き直しより先に保存が走り、古い見出しを送ってしまうため（039 で顧客を変えたときに見つけた）
    latest.current = { ...latest.current, head: { ...latest.current.head, ...patch } };
    markDirty(!typingOnly(patch));
  };
  /** 外注先を付けた行は仕入れの「自社」を外す（#131 の2回目：外注先を付けた行は自動でそちらに変わる） */
  const withVendor = (patch: Partial<Row>): Partial<Row> => (patch.vendorPartnerId ? { ...patch, costOwn: false } : patch);
  const upd = (i: number, patch: Partial<Row>) => {
    const p = withVendor(patch);
    const id = latest.current.rows[i]?.id ?? String(i);
    record(typingOnly(p) ? `${id}:${Object.keys(p).sort().join(",")}` : null);
    putRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p } : r)));
    markDirty(!typingOnly(p));
  };
  /** 描き直したあとにその欄へ。並びで渡すと、あるもののうち最初の欄（#215：Shift＋Tab で上の行の最後の欄） */
  const focusNext = (fid: string | readonly string[], keep = false) => {
    pendingFocus.current = { id: fid, keep };
    setFocusTick((t) => t + 1);
  };
  useEffect(() => {
    const p = pendingFocus.current;
    if (!p) return;
    pendingFocus.current = null;
    const el = (typeof p.id === "string" ? document.getElementById(p.id) : p.id.map(fieldEl).find((x) => x != null)) as HTMLInputElement | HTMLSelectElement | null | undefined;
    if (!el) return;
    el.focus();
    if (el instanceof HTMLInputElement && el.value && !p.keep) el.select();
    el.closest("tr")?.scrollIntoView({ block: "nearest" });
  }, [focusTick, edit]);

  /* ---- 内訳の紙：どの行がいま見えているか ---- */
  const useSections = sheets.length > 0;
  const coverSheet = sheets.find((x) => x.type === "cover");
  const onCover = useSections && cur === "cover";
  const curSheetId: string | null = useSections ? (onCover ? (coverSheet?.id ?? null) : cur) : null;
  const curSheet = useSections && !onCover ? sheets.find((x) => x.id === cur) : undefined;
  const inCur = (r: Row) => (r.sheetId ?? null) === curSheetId;
  /** #199（docs/80）：不使用の内訳。その行は 合計・仕入れ・原価割れの数・表紙の内訳の合計・紙に入れない（金額は DB が計算する） */
  const unusedSheetIds = new Set(sheets.filter((x) => x.type === "breakdown" && x.inUse === false).map((x) => x.id));
  const inUseRow = (r: Row) => !r.sheetId || !unusedSheetIds.has(r.sheetId);
  /**
   * 並べ方（#129）：手で並べる以外は、layoutLines（contract。PDF と同じ関数）が作った並びで紙を描く。データ（行の順・金額）は変わらない。
   * 出した回・請求書は固めた名前（initial.axes）、下書きはいまの工種・場所の一覧
   */
  const axisMode = axis !== "manual" && !template;
  const axNodes = axisNodes(axLists, initial.axes ? { trades: initial.axes.trades, places: initial.axes.places } : undefined, readOnly || !!invoice);
  const placeName = namesOf(axNodes.places);
  const tradeName = namesOf(axNodes.trades);
  const axLayout = axisMode
    ? layoutLines(
        // #199：不使用の内訳の行は紙（PDF）に出ないので、並べ方の紙にも並べない（直すときは表示順を「手動」に）
        rows.filter(inUseRow).map((r) => ({ id: r.id, role: r.role, name: r.name, amount: calc.amounts[r.id] ?? 0, sheetId: r.sheetId, placeId: r.placeId, tradeId: r.tradeId, noteKind: r.noteKind })),
        sheets.filter((x) => !unusedSheetIds.has(x.id)).map((x) => ({ id: x.id, type: x.type, name: x.name, note: x.note, subtotal: calc.sheetTotals[x.id] ?? 0 })),
        axis,
        axNodes,
      )
    : null;
  const rowIndex = new Map(rows.map((r, i) => [r.id, i]));
  /** いま見えている行の、rows の中の位置（並べ方のときは紙に並ぶ順） */
  const visible: number[] = axLayout
    ? axLayout.flat.flatMap((x) => (x.kind === "line" ? [rowIndex.get(x.line.id)!] : []))
    : rows.map((r, i) => (inCur(r) ? i : -1)).filter((i) => i >= 0);
  const breakdowns = sheets.filter((x) => x.type === "breakdown");
  /** 使う内訳（#199）：表紙の「内訳の合計」の行・「n内訳」・紙の形の判定はこちら。タブは不使用も並べる（灰色＋「不使用」） */
  const usedBreakdowns = breakdowns.filter((x) => x.inUse !== false);
  /** PDF（縦串4b）。下書きは保存してから開く（新しいタブは押した瞬間に開いておかないとブラウザに止められる）。
   *  紙の形（R8）：内訳があるときだけ選べる。sheets＝表紙＋内訳ごとの紙／flat＝1枚に続けて */
  const [pdfForm, setPdfForm] = useState<"sheets" | "flat">("sheets");
  const hasBreakdown = axisMode || usedBreakdowns.length > 0;
  // 紙は画面の中で見る（/estimates/:id/paper。R13：印刷のボタンとタブの名前「見積書：工事名」）。API の URL は直接開かない
  const pdfUrl = `/estimates/${initial.id}/paper${hasBreakdown && pdfForm === "flat" ? "?form=flat" : ""}`;
  const pcOnly = usePcOnly(); // 発行のあと紙の画面へ移るのは PC だけ（スマホは編集画面に残る。PC_ONLY_FEATURES の mailSend と同じ判定）
  const pdfFormSel = hasBreakdown ? (
    <select className="pdfform" aria-label="PDF の構成" value={pdfForm} onChange={(e) => setPdfForm(e.target.value as "sheets" | "flat")}>
      <option value="sheets">表紙＋内訳書</option>
      <option value="flat">1枚</option>
    </select>
  ) : null;
  /** 内訳の紙ごとの合計・仕入れ */
  const sheetTotal = (id: string) => calc.sheetTotals[id] ?? 0;

  /**
   * #301（2026-11-04 経理担当の指摘、docs/47 §18.9）：利用者が追加した行（「＋ 行追加」・「＋ 下に追加」・行の種類のボタン）は、何も書いていなくても下書きの間は消さない。
   * 前は「何も書いていない行は閉じるときに消す」が全部の空の行に効き、「＋ 行追加」を続けて押す・行を足して何も入力せずに別の行を押す と
   * 足したばかりの行が黙って消えた（落としたあとの位置で開くので、押した回数より少ない行しか残らなかった）。
   * 消すのは、行の最後の欄で Enter／Tab／↓ を押したときに画面が自動で足した行（autoRows）だけ＝何も書かずに離れたら前のとおり消す
   * （打ち終えるたびに空の行が末尾に残って紙に出るのを防ぐ）。利用者が足した空の行を消すのは行の「削除」だけ
   */
  const autoRows = useRef(new Set<string>());
  const isBlankRow = (r: Row | undefined) => !!r && r.role === "detail" && !r.name && !r.spec && !r.note && !r.quantity && !r.unitPrice && r.costAmount == null;
  /** 開いている行が、画面が自動で足したままの空の行なら消す。消したらその位置（index）、消さなければ null */
  const dropAutoBlank = (): number | null => {
    if (edit == null) return null;
    const r = rows[edit];
    if (!r || !autoRows.current.has(r.id) || !isBlankRow(r)) return null;
    autoRows.current.delete(r.id);
    putRows((rs) => rs.filter((_, k) => k !== edit));
    dropSameTop(); // 足しただけの空の行を落とした＝足す前と同じ形（元に戻すの1手にしない）
    markDirty(true);
    return edit;
  };
  const editRow = (i: number, fid = "f_name") => {
    if (readOnly) return;
    breakTyping();
    setSecEdit(null);
    const dropped = dropAutoBlank();
    setEdit(dropped != null && dropped < i ? i - 1 : i);
    focusNext(fid);
  };
  const closeEdit = () => {
    breakTyping();
    dropAutoBlank();
    setEdit(null);
    if (sq.isDirty()) markDirty(true);
  };
  /** いま開いている紙の末尾に行を足す（rows の中では、その紙の最後の行の次） */
  const addLine = (role: RowRole) => {
    setSecEdit(null);
    // 並べ方（#129）のときは表紙（か1枚の紙）の末尾。場所・工種は行の帯で付ける（「その他」に並ぶ）
    const r = mkRow(role, { sheetId: axisMode ? (useSections ? (coverSheet?.id ?? null) : null) : curSheetId });
    const at0 = axisMode ? rows.length : visible.length ? visible[visible.length - 1]! + 1 : rows.length;
    // 自動で足した空の行を落としたら、その分だけ位置を詰める（#301：前はここを詰めず、開いていない行ができていた）
    const dropped = dropAutoBlank();
    const at = dropped != null && dropped < at0 ? at0 - 1 : at0;
    record();
    putRows((rs) => [...rs.slice(0, at), r, ...rs.slice(at)]);
    setEdit(at);
    markDirty(true);
    focusNext("f_name");
  };
  /** Enter の続き：同じ紙の次の行へ。無ければ（か insert なら）下に新しい行を足す。auto＝画面が足す行（Enter／Tab／↓）＝空のまま離れたら消す（#301） */
  const nextLine = (i: number, insert: boolean, auto = false) => {
    // 紙に並ぶ順で次の行（並べ方＝#129 のときは rows の順と違う）
    const pos = visible.indexOf(i);
    const nextVisible = pos >= 0 ? visible[pos + 1] : visible.find((k) => k > i);
    if (insert || nextVisible == null) {
      // 並べ方（#129）のときは、同じ場所・工種の行にする（同じ見出しの下に並ぶ）
      const r = mkRow("detail", { sheetId: rows[i]?.sheetId ?? null, ...(axisMode ? { placeId: rows[i]?.placeId ?? null, tradeId: rows[i]?.tradeId ?? null } : {}) });
      if (auto) autoRows.current.add(r.id);
      record();
      putRows((rs) => [...rs.slice(0, i + 1), r, ...rs.slice(i + 1)]);
      setEdit(i + 1);
      markDirty(true);
    } else {
      breakTyping();
      setEdit(nextVisible);
      if (sq.isDirty()) markDirty(true);
    }
    focusNext("f_name");
  };
  /**
   * 行の ⋮（No の左のつまみ）の「上に追加」「下に追加」（#302 C1）：その行の上・下に明細の行を1つ足して名称へ（利用者が足した行＝空でも消さない。#301）。
   * 自動で足した空の行（Enter）を落としたら、その分だけ位置を詰める（addLine と同じ）
   */
  const insertLine = (i: number, where: "above" | "below") => {
    setSecEdit(null);
    const base = rows[i];
    if (!base) return;
    const r = mkRow("detail", { sheetId: base.sheetId ?? null, ...(axisMode ? { placeId: base.placeId ?? null, tradeId: base.tradeId ?? null } : {}) });
    const at0 = where === "above" ? i : i + 1;
    const dropped = dropAutoBlank();
    const at = dropped != null && dropped < at0 ? at0 - 1 : at0;
    record();
    putRows((rs) => [...rs.slice(0, at), r, ...rs.slice(at)]);
    setEdit(at);
    markDirty(true);
    focusNext("f_name");
  };
  /** 数量・単価・仕入れの ↓＝下の行の同じ列へ（その欄がある行まで飛ばす。#215 で Enter から ↓ に）。下に無ければ下に行を足して名称へ */
  const goDown = (i: number, fid: string) => {
    const pos = visible.indexOf(i);
    const below = (pos >= 0 ? visible.slice(pos + 1) : visible.filter((k) => k > i)).find((k) => ORDERS[rows[k]!.role].includes(fid));
    if (below == null) {
      nextLine(i, true, true);
      return;
    }
    breakTyping();
    setEdit(below);
    if (sq.isDirty()) markDirty(true);
    focusNext(fid);
  };
  const delLine = (i: number) => {
    record();
    putRows((rs) => rs.filter((_, k) => k !== i));
    setEdit(null);
    markDirty(true);
  };
  /** 区分を変える。#305：「労務費」は明細＋労務費の印（材工が空なら「手間」）、ほかの区分にすると労務費の印は外す */
  const changeType = (i: number, k: RowRole | typeof ROW_KIND_LABOR) => {
    const r = rows[i]!;
    const was = r.role;
    const t: RowRole = k === ROW_KIND_LABOR ? "detail" : k;
    const patch: Partial<Row> = { role: t };
    if (k === ROW_KIND_LABOR) {
      patch.isLabor = true;
      if (!r.workType) patch.workType = "labor";
    } else if (r.isLabor) patch.isLabor = false;
    if (t === "subtotal" && !r.name) patch.name = "小計";
    if (t === "overhead_rate") patch.rateBase = r.rateBase ?? "block";
    if (t === "excluded") patch.amountLabel = r.amountLabel ?? "別途";
    if (t === "discount") {
      patch.discountTargetTotal = r.discountTargetTotal ?? null;
      if (was === "detail" && (r.unitPrice ?? 0) > 0) patch.amount = -(r.unitPrice ?? 0);
    }
    if (t === "detail" && was === "discount") patch.amount = null;
    upd(i, patch);
    focusNext(t === "overhead_rate" && r.name ? "f_rate" : "f_name");
  };
  /** 金額の欄の打ち方（ふつうの行で数量・単価が空のとき）：数字→1式、10%→率の行、別途→別途の行。true＝行の種類が変わった */
  const amtShortcut = (i: number, v: string): boolean => {
    const r = rows[i]!;
    if (r.role !== "detail") return false;
    const t = String(v || "").normalize("NFKC").trim();
    if (!t) return false;
    const m = /^([\d.]+)\s*%$/.exec(t.replace(/,/g, ""));
    if (m) {
      upd(i, { role: "overhead_rate", rate: Math.round(parseFloat(m[1]!) * 100) / 10000, rateBase: "block", rateBaseLineIds: [] });
      focusNext(r.name ? "f_note" : "f_name");
      toast(`区分を「率」にしました（${m[1]}%・枠の合計に対して）。`);
      return true;
    }
    if ((EXCLUDED_WORDS as readonly string[]).includes(t)) {
      upd(i, { role: "excluded", amountLabel: t });
      focusNext(r.name ? "f_note" : "f_name");
      toast(`区分を「別途」にしました（「${t}」と印字・合計に入れない）。`);
      return true;
    }
    const n = parseInt(t.replace(/[^\d-]/g, ""), 10);
    if (!Number.isNaN(n) && n !== 0) {
      upd(i, { quantity: 1, unit: r.unit || "式", unitPrice: n });
      focusNext(["f_tax", "f_note"]); // #215：金額 →（税区分）→ 備考
      return true;
    }
    toast("金額（数字）か「10%」か「別途」を打ってください。");
    return false;
  };

  /* Enter で次の欄へ、最後の欄なら次の行へ。Esc で閉じる。変換確定の Enter は無視（docs/10 §5.2） */
  /* ---- 候補の引き出し（試作 v3.10.1、縦串3b） ---- */
  type PickState = { open: boolean; by: "typing" | "button"; q: string; tab: PickTab; sel: number; armed: boolean; multi: boolean; dismissedQ: string | null };
  const [pick, setPick] = useState<PickState>({ open: false, by: "typing", q: "", tab: "all", sel: 0, armed: false, multi: false, dismissedQ: null });
  const editRowObj = edit == null ? undefined : rows[edit];
  const pickable = !!editRowObj && (editRowObj.role === "detail" || editRowObj.role === "overhead_rate");
  const canRate = !!editRowObj && (editRowObj.role === "overhead_rate" || (editRowObj.role === "detail" && !editRowObj.quantity && !editRowObj.unitPrice));
  const pq = pick.q.trim();
  // 候補を探すのは打つ手が止まってから（0.25秒）。1文字ごとに探して引き出しが開いたり閉じたりすると紙が左右に動いてチラつく（射場さん 9/14 R11-③）
  const [dq, setDq] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDq(pq), 250);
    return () => window.clearTimeout(t);
  }, [pq]);
  // #306：会社の率の名前（率の行で費目を選んだとき、率が空なら同じ費目の率）。テンプレートでも使う
  const ratePresets = useQuery({ queryKey: ["rate-presets"], queryFn: () => api<RatePresetList>("GET", "/estimates/rate-presets"), enabled: !readOnly && field, staleTime: 60_000 });
  const suggest = useQuery({
    queryKey: ["suggest", initial.id, dq],
    queryFn: () => api<Suggest>("GET", `/estimates/suggest?q=${encodeURIComponent(dq)}&exclude=${initial.id}`),
    enabled: pickable,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const hit = (name: string, spec: string) => !pq || name.includes(pq) || spec.includes(pq);
  const hereSeen = new Set<string>();
  const hereCands: Cand[] = rows
    .filter((x, k) => x.role === "detail" && x.name && k !== edit && hit(x.name, x.spec) && !hereSeen.has(x.name + "|" + x.spec) && hereSeen.add(x.name + "|" + x.spec))
    .map((x) => ({ src: "here", name: x.name, spec: x.spec, unit: x.unit, price: x.unitPrice, cost: x.costAmount, taxCategory: x.taxCategory }));
  const pastCands: Cand[] = (suggest.data?.past ?? []).map((p) => ({ src: "past", name: p.name, spec: p.spec, unit: p.unit, price: p.unitPrice, cost: p.costAmount, count: p.count, last: p.lastIssueDate, taxCategory: p.taxCategory }));
  // T2：品目マスタ（使う機能「品目マスタ」の会社。選ぶと 名称・単位・税区分・品目 が写る）
  const itemCands: Cand[] = can("items") && editRowObj?.role !== "overhead_rate" ? (suggest.data?.items ?? []).map((x) => ({ src: "item", id: x.id, name: x.name, unit: x.unit, taxCategory: x.taxCategory })) : [];
  const rateCands: Cand[] = canRate ? (suggest.data?.rates ?? []).filter((r) => field || r.rateBase !== "labor").map((r) => ({ src: "rate", name: r.name, rate: r.rate, base: r.rateBase, kind: r.costKind })) : [];
  const cands = { here: hereCands, past: pastCands, item: itemCands, rate: rateCands };
  // 「すべて」は出どころを全部並べる（抜粋にすると「小計の範囲しか出ない」ように見える。射場さん 9/13）
  const pickList: Cand[] = pick.tab === "all" ? [...hereCands, ...pastCands, ...itemCands, ...rateCands] : cands[pick.tab];
  const pickSel = Math.min(pick.sel, Math.max(0, pickList.length - 1));
  /** 人が閉じた（Esc・虫眼鏡）。同じ文字のままでは開き直さない */
  const closePicker = () => setPick((p) => ({ ...p, open: false, armed: false, dismissedQ: p.q.trim() }));
  const openPicker = (by: "typing" | "button", q?: string) => setPick((p) => ({ ...p, open: true, by, tab: "all", sel: 0, armed: false, dismissedQ: null, q: q ?? p.q }));
  /** 名称を打った。開く／閉じるは打つ手が止まって候補が分かってから（下の effect） */
  const pickerTyped = (v: string) => {
    setPick((p) => ({ ...p, q: v, sel: 0, armed: false, by: p.open ? p.by : "typing" }));
  };
  useEffect(() => {
    if (!pickable || pick.by !== "typing") return;
    if (dq !== pq || suggest.isPending || suggest.isFetching || suggest.isPlaceholderData) return; // 打つ手が止まって、その文字の結果が来てから判断する
    const all = [...hereCands, ...pastCands, ...itemCands, ...rateCands];
    const useful = pq !== "" && all.length > 0 && !(all.length === 1 && all[0]!.name === pq && all[0]!.src !== "rate"); // 候補が無い／打った名前と同じ候補しか無ければ出さない（うるさくしない）
    if (pick.open && !useful) setPick((p) => ({ ...p, open: false, armed: false }));
    else if (!pick.open && useful && pick.dismissedQ !== pq && document.activeElement?.id === "f_name") setPick((p) => ({ ...p, open: true, tab: "all", sel: 0, armed: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickable, pick.open, pick.by, pick.dismissedQ, pq, dq, suggest.data, suggest.isPending, suggest.isFetching, suggest.isPlaceholderData, edit]);
  useEffect(() => {
    if (!pickable && pick.open) closePicker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickable]);
  const pickAt = (i: number) => {
    const c = pickList[i];
    if (!c || edit == null) return;
    const r = rows[edit]!;
    if (c.src === "rate") {
      // 名前・率・基礎・種類を写すだけ。あとで会社設定を変えてもこの行は動かない
      upd(edit, { role: "overhead_rate", name: c.name, rate: c.rate, rateBase: c.base, costKind: c.kind, rateBaseLineIds: [] });
      closePicker();
      focusNext("f_note");
      return;
    }
    // T2：品目マスタ：名称・単位・税区分・品目を写す（単価・仕入れは今の行のまま）
    // 前の見積・この見積の中：仕入れがあれば写す（手入力になる＝自社は外れる）。無ければ今の行のまま（自社・未定）。税区分も写す
    const patch: Partial<Row> =
      c.src === "item"
        ? { name: c.name, unit: c.unit || r.unit, taxCategory: c.taxCategory, itemId: c.id }
        : { name: c.name, spec: c.spec, unit: c.unit, unitPrice: c.price, ...(c.taxCategory ? { taxCategory: c.taxCategory } : {}), ...(c.cost != null ? { costAmount: c.cost, costOwn: false } : { costAmount: r.costAmount }) };
    if (pick.multi) {
      // 続けて選ぶ：写して、下に新しい行を足して、そこを編集中に。引き出しは開いたまま
      const nr = mkRow("detail");
      autoRows.current.add(nr.id); // 続けて選ぶために足した行＝空のまま閉じたら消す（#301）
      record();
      putRows((rs) => [...rs.slice(0, edit).map((x) => x), { ...rs[edit]!, ...patch }, nr, ...rs.slice(edit + 1)]);
      setEdit(edit + 1);
      markDirty(true);
      setPick((p) => ({ ...p, q: "", sel: 0, armed: false }));
      focusNext("f_name");
      toast(`「${c.name}」を写しました。数量はあとで入力してください。`);
      return;
    }
    upd(edit, patch);
    closePicker();
    focusNext("f_qty");
  };

  const onRowKey = (i: number) => (ev: React.KeyboardEvent<HTMLTableRowElement>) => {
    const target = ev.target as HTMLElement;
    if (ev.key === "Escape") {
      ev.preventDefault();
      if (pick.open) closePicker(); // 引き出しが開いていれば先にそれを閉じる
      else closeEdit();
      return;
    }
    if (ev.nativeEvent.isComposing || ev.keyCode === 229) return;
    if (target.id === "f_name" && pick.open && (ev.key === "ArrowDown" || ev.key === "ArrowUp")) {
      ev.preventDefault();
      const n = pickList.length;
      if (n) setPick((p) => ({ ...p, armed: true, sel: (pickSel + (ev.key === "ArrowDown" ? 1 : -1) + n) % n }));
      return;
    }
    // #215：↓ は数量・単価・仕入れだけ、下の行の同じ列へ（Enter は次の欄へ）
    if (ev.key === "ArrowDown" && !ev.altKey && !ev.shiftKey && !ev.metaKey && !ev.ctrlKey && DOWN_FIELDS.has(target.id)) {
      ev.preventDefault();
      goDown(i, target.id);
      return;
    }
    // #215：Enter と Tab は同じ「次の欄へ」（虫眼鏡・「拾」のしるしは飛ばす）、Shift＋Tab は逆順。行の詳細の上段の欄（区分・場所…）はブラウザのまま
    const isTab = ev.key === "Tab";
    if ((!isTab && ev.key !== "Enter") || ev.altKey || ev.metaKey || ev.ctrlKey || target.tagName === "TEXTAREA") return;
    if (!isTab && target.tagName === "BUTTON" && !ORDER_BUTTONS.has(target.id)) return; // Enter はボタンを押す（拾い出しの合計の数量＝引き出しを開く）。率の行の対象は次の欄へ（ポップは Space・押す）
    if (!ORDERS[rows[i]!.role].includes(target.id) && !PRICING_ORDER.includes(target.id)) return;
    ev.preventDefault();
    if (isTab && ev.shiftKey) {
      fieldBack(i, target.id);
      return;
    }
    // 引き出しが開いていても、↑↓で選んだときか、打った名前が候補と同じときだけ写す（新しい名前を勝手に置き換えない）
    if (target.id === "f_name" && pick.open) {
      const c = pickList[pickSel];
      if (c && (pick.armed || c.name === (target as HTMLInputElement).value.trim())) {
        pickAt(pickSel);
        return;
      }
      closePicker();
    }
    if (target.id === "f_amt" && amtShortcut(i, (target as HTMLInputElement).value)) return;
    fieldForward(i, target.id);
  };
  /**
   * 次の欄へ（#215）。行の最後の欄（仕入れ・社内用を出していなければ備考）のあとは、単価が空で仕入れのある行なら行の詳細の下段の
   * 粗利率（パートナー・上乗せ率と続く）、そうでなければ次の行の名称（最後の行なら行を足す）
   */
  const fieldForward = (i: number, fid: string) => {
    const r = rows[i]!;
    const pk = PRICING_ORDER.indexOf(fid);
    if (pk >= 0) {
      const next = PRICING_ORDER.slice(pk + 1).map(fieldEl).find((x) => x != null);
      if (next) focusField(next);
      else nextLine(i, false, true);
      return;
    }
    const order = ORDERS[r.role];
    const next = order.slice(order.indexOf(fid) + 1).map(fieldEl).find((x) => x != null);
    if (next) {
      focusField(next);
      return;
    }
    // 粗利率（か「円で」の上乗せ額）の欄があるのは、仕入れのある行で社内用を出している見積だけ（仕入れが空・自社なら「—」で欄が無い）
    const start = r.role === "detail" && !r.unitPrice ? (fieldEl("f_margin") ?? fieldEl("f_markup_yen")) : null;
    if (start) focusField(start);
    else nextLine(i, false, true);
  };
  /** 前の欄へ（Shift＋Tab、#215）。行の最初の欄（名称）からは上の行の最後の欄へ */
  const fieldBack = (i: number, fid: string) => {
    const order = ORDERS[rows[i]!.role];
    const pk = PRICING_ORDER.indexOf(fid);
    const prev =
      pk >= 0
        ? (PRICING_ORDER.slice(0, pk).reverse().map(fieldEl).find((x) => x != null) ?? [...order].reverse().map(fieldEl).find((x) => x != null))
        : order.slice(0, order.indexOf(fid)).reverse().map(fieldEl).find((x) => x != null);
    if (prev) {
      focusField(prev);
      return;
    }
    const pos = visible.indexOf(i);
    const above = pos > 0 ? visible[pos - 1] : undefined;
    if (above == null) return;
    breakTyping();
    setEdit(above);
    if (sq.isDirty()) markDirty(true);
    focusNext([...ORDERS[rows[above]!.role]].reverse());
  };

  const amountOf = (r: Row) => calc.amounts[r.id] ?? 0;
  /* ---- パートナー手数料（#213、docs/40 §5・§6）：行ごとの手数料（使う内訳の行だけ。金額は DB が計算した値）。見積に付いていなければ null ---- */
  const markupSet = initial.markup ?? { marginPct: 20, round: 1 };
  const docRefPct = referralOn && head.referrerId ? head.referrerPct : null;
  const refFees = docRefPct != null ? referralFeesOf(rows.filter(inUseRow), docRefPct, amountOf) : null;
  const feeOf = (r: Row) => refFees?.byLine[r.id]?.fee ?? 0;
  /** 原価割れ：仕入れ（＋パートナー ON の見積は手数料）が金額を上回る行（docs/40 §7：手数料後の利益がマイナスの行は原価割れと同じ扱い） */
  const isUnder = (r: Row) => r.role === "detail" && !r.costOwn && r.costAmount != null && amountOf(r) > 0 && r.costAmount + feeOf(r) > amountOf(r);
  /** 手数料の分が入っていない行（知らせるだけ・止めない。docs/40 §7） */
  const uncoveredCount = docRefPct != null ? uncoveredLinesOf(rows.filter(inUseRow), docRefPct, markupSet.marginPct, amountOf).length : 0;
  /** パートナーの候補（顧客の詳細でパートナー ON の取引先。その見積の顧客自身もパートナーなら先頭） */
  const refOpts = useQuery({ queryKey: ["referrers"], queryFn: () => api<PartnerList>("GET", "/partners?type=referrer"), enabled: referralOn && !readOnly, staleTime: 30_000 });
  const refOptions = (() => {
    const xs = refOpts.data?.items ?? [];
    const self = xs.find((x) => x.id === head.clientId);
    return self ? [self, ...xs.filter((x) => x.id !== self.id)] : xs;
  })();
  /** 今のパートナーの略称（帯・合計の帯の表示。選び直した直後は候補から、読み直す前は API の答えから） */
  const refShort = head.referrerId ? (refOptions.find((x) => x.id === head.referrerId)?.displayName ?? (initial.referral?.partnerId === head.referrerId ? initial.referral.short : "")) : "";
  /**
   * 見積のパートナーを付ける・変える・外す（docs/40 §4）。付けた・率を変えた：見積の率に従う行（行の率が空）で仕入れと単価のあるふつうの行は、
   * 粗利率を変えずに新しい率を上乗せした単価にする（決定3「上乗せする」）。外す：行の率を空に（単価はそのまま）。行と一緒に1手で ↶ できる
   */
  const setDocReferral = (id: string | null, pct: number | null) => {
    const h = latest.current.head;
    if (id === h.referrerId && pct === h.referrerPct) return;
    record();
    let n = 0;
    if (id == null) {
      if (latest.current.rows.some((r) => r.referralPct != null)) putRows((rs) => rs.map((r) => (r.referralPct != null ? { ...r, referralPct: null } : r)));
    } else if (pct != null) {
      const ch = repriceForDocPct(latest.current.rows.filter(inUseRow), h.referrerId ? h.referrerPct : null, pct, markupSet.round);
      n = Object.keys(ch).length;
      if (n) putRows((rs) => rs.map((r) => (ch[r.id] != null ? { ...r, unitPrice: ch[r.id]! } : r)));
    }
    setH({ referrerId: id, referrerPct: id == null ? null : pct });
    if (n) toast(t(REFERRAL_MSG.repriced).replace("{n}", String(n)));
  };
  /** ★B（docs/40 §5.2）：仕入れを打って単価が空の行は、既定の粗利率とパートナーで単価を入れる（単価がある行は触らない。パートナー手数料の会社だけ） */
  const autoPrice = (i: number) => {
    if (!referralOn || readOnly) return;
    const r = latest.current.rows[i];
    if (!r || r.role !== "detail" || r.costOwn) return;
    const u = autoUnitPrice(r, docRefPct, markupSet.marginPct, markupSet.round);
    if (u != null) upd(i, { unitPrice: u });
  };
  const marginText = (r: Row) => {
    const a = amountOf(r);
    if (r.role !== "detail" || !a) return "";
    if (r.costAmount == null) return "—";
    const m = Math.round(((a - r.costAmount) / a) * 1000) / 10;
    return `${m}%`;
  };
  /** 社内用：ふつうの行の仕入れの合計（未入力の行は数えず、行数だけ知らせる。ルール7）。自社の行は仕入れ 0（未入力に数えない。#131 の2回目） */
  const costOf = (list: Row[]): CostSum => {
    // T2：立替金の行は仕入れ・粗利に入れない（未入力に数えない）
    const ds = list.filter(hasCostField);
    return { sum: ds.reduce((a, x) => a + (x.costOwn ? 0 : (x.costAmount ?? 0)), 0), missing: ds.filter((x) => !x.costOwn && x.costAmount == null).length, any: ds.length > 0 };
  };
  /** 小計の行 i の枠（同じ紙の、前の小計の次から）。表紙では内訳の仕入れも足す */
  const blockCost = (i: number): CostSum => {
    const sid = rows[i]?.sheetId ?? null;
    const same = rows.map((r, k) => ((r.sheetId ?? null) === sid ? k : -1)).filter((k) => k >= 0 && k < i);
    let from = 0;
    for (const k of same) if (rows[k]!.role === "subtotal") from = k + 1;
    const c = costOf(same.filter((k) => k >= from).map((k) => rows[k]!));
    if (useSections && sid === coverSheet?.id && from === 0) {
      const inner = costOf(rows.filter((r) => r.sheetId != null && r.sheetId !== coverSheet?.id && inUseRow(r))); // #199：使う内訳だけ
      return { sum: c.sum + inner.sum, missing: c.missing + inner.missing, any: c.any || inner.any };
    }
    return c;
  };
  const sheetCost = (id: string) => costOf(rows.filter((r) => r.sheetId === id));
  // #199：不使用の内訳の行は 仕入れの合計・原価割れの数・紙に出る行の数 に入れない
  const allCost = costOf(rows.filter(inUseRow));
  const underCount = rows.filter((r) => inUseRow(r) && isUnder(r)).length;
  const lineCount = rows.filter((r) => inUseRow(r) && NUMBERED.has(r.role)).length;

  /* ---- 内訳の紙の操作（試作 v3.7〜3.8） ---- */
  const [nameDlg, setNameDlg] = useState<{ title: string; value: string; onOk: (v: string) => void } | null>(null);
  const [delDlg, setDelDlg] = useState<Sheet | null>(null);
  const goSheet = (id: string) => {
    dropAutoBlank();
    setEdit(null);
    setSecEdit(null);
    setCur(id);
  };
  const addSection = () =>
    setNameDlg({
      title: "内訳の名前",
      value: "",
      onOk: (name) => {
        const sh = newSheet("breakdown", name);
        record();
        putSheets((xs) => [...xs, sh]);
        markDirty(true);
        goSheet(sh.id);
      },
    });
  const renameSection = () => {
    if (!curSheet) return;
    setNameDlg({ title: ESTIMATE_PC_MSG.sheetRenameTitle, value: curSheet.name, onOk: (name) => { record(); putSheets((xs) => xs.map((x) => (x.id === curSheet.id ? { ...x, name } : x))); markDirty(true); } });
  };
  const updSheet = (id: string, patch: Partial<Sheet>) => {
    record("isLabor" in patch ? null : `sheet:${id}:${Object.keys(patch).sort().join(",")}`);
    putSheets((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    markDirty("isLabor" in patch); // 名前・備考は打ち終わりを待つ。労務費の印はすぐ
  };
  /** タブの並び＝表紙の行の並び。表紙側からも内訳側からも動かせる */
  const moveSection = (id: string, d: -1 | 1) => {
    const bs = breakdowns;
    const i = bs.findIndex((x) => x.id === id);
    const j = i + d;
    if (i < 0 || j < 0 || j >= bs.length) return;
    const order = bs.slice();
    [order[i], order[j]] = [order[j]!, order[i]!];
    record();
    putSheets([coverSheet!, ...order]);
    markDirty(true);
    if (secEdit === id) focusNext("f_name", true);
  };
  const doDelSection = (sh: Sheet) => {
    const removedRows = rows.filter((r) => r.sheetId === sh.id);
    const idx = breakdowns.findIndex((x) => x.id === sh.id);
    record();
    putSheets((xs) => xs.filter((x) => x.id !== sh.id));
    putRows((rs) => rs.filter((r) => r.sheetId !== sh.id));
    setDelDlg(null);
    setEdit(null);
    setCur("cover");
    markDirty(true);
    // #200：保存されると「削除した内訳」に入る（行のある内訳だけ。30日）。保存の前ならこの「元に戻す」・↶ で戻る
    toast(SHEET_TRASH_MSG.deleted.replace("{name}", sh.name || "（名前なし）").replace("{n}", String(removedRows.length)), {
      label: "元に戻す",
      onClick: () => {
        record();
        putSheets((xs) => {
          const bs = xs.filter((x) => x.type === "breakdown");
          bs.splice(idx, 0, sh);
          return [xs.find((x) => x.type === "cover")!, ...bs];
        });
        putRows((rs) => [...rs, ...removedRows]);
        markDirty(true);
        setCur(sh.id);
      },
    });
  };
  /** #199：内訳を 利用しない ⇄ 利用する（↶ で戻せる。すぐ保存＝合計は保存の答えで描き直す） */
  const toggleSheetUse = (sh: Sheet) => {
    record();
    putSheets((xs) => xs.map((x) => (x.id === sh.id ? { ...x, inUse: x.inUse === false } : x)));
    markDirty(true);
  };
  /**
   * #199：内訳の複製（POST /estimates/:id/sheets/:sheetId/copy＝DB の fn_copy_sheet）。書きかけを送り終えてから、版つきで呼ぶ。
   * 新しい内訳は元のすぐ右・同じ名前。答えで行と紙を入れ替え、複製の前の形を1手として控える（↶ で複製を取り消せる。#195 の復元と同じ作り）。
   * 直後に新しいタブを選び、内訳名称の窓を開く（元の名前が入っている）。総額指定の値引きは写さない＝通知
   */
  const [copying, setCopying] = useState(false);
  const copySection = async (sh: Sheet) => {
    if (copying || alt) return;
    setCopying(true);
    try {
      dropAutoBlank();
      setEdit(null);
      setSecEdit(null);
      if (!(await settle())) {
        toast(RESTORE_MSG.unsaved);
        return;
      }
      breakTyping();
      record();
      const e = await api<SheetCopyResult>("POST", `/estimates/${initial.id}/sheets/${sh.id}/copy`, { version: versionRef.current });
      rememberLineage(e);
      putRows(e.lines.map(fromServerLine));
      putSheets(e.sheets.map(sheetOf));
      syncedFrom(e);
      setCalc(fromServerCalc(e));
      setSave({ kind: "saved", at: hhmm() });
      qc.setQueryData(["estimate", initial.id], e);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      refetchFollows(e);
      if (initial.revising) {
        void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
        void qc.invalidateQueries({ queryKey: ["estimate-marks", initial.id] });
      }
      setCur(e.copiedSheetId);
      if (e.skippedTargetDiscount) toast(ESTIMATE_PC_MSG.sheetCopyNoTarget);
      const newId = e.copiedSheetId;
      setNameDlg({
        title: ESTIMATE_PC_MSG.sheetRenameTitle,
        value: sh.name,
        onOk: (name) => {
          record();
          putSheets((xs) => xs.map((x) => (x.id === newId ? { ...x, name } : x)));
          markDirty(true);
        },
      });
    } catch (err) {
      dropSameTop(); // 断られた＝紙は変わっていない。控えた1手（いまと同じ形）は捨てる
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    } finally {
      setCopying(false);
    }
  };
  /**
   * #200（docs/81）：削除した内訳の復元（POST /estimates/:id/deleted-sheets/:did/restore＝DB の fn_sheet_restore）。複製と同じ作り：
   * 書きかけを送り終えてから版つきで呼び、答えで行と紙を入れ替え、復元の前の形を1手として控える（↶ で取り消すと、また「削除した内訳」に入る）
   */
  const [trashDlg, setTrashDlg] = useState(false);
  const [sheetRestoring, setSheetRestoring] = useState(false);
  const trashCount = !alt ? (initial.deletedSheetCount ?? 0) : 0;
  const restoreSection = async (did: string, name: string) => {
    if (sheetRestoring || alt) return;
    setSheetRestoring(true);
    try {
      dropAutoBlank();
      setEdit(null);
      setSecEdit(null);
      if (!(await settle())) {
        toast(RESTORE_MSG.unsaved);
        return;
      }
      breakTyping();
      record();
      const e = await api<SheetRestoreResult>("POST", `/estimates/${initial.id}/deleted-sheets/${did}/restore`, { version: versionRef.current });
      rememberLineage(e);
      putRows(e.lines.map(fromServerLine));
      putSheets(e.sheets.map(sheetOf));
      syncedFrom(e);
      setCalc(fromServerCalc(e));
      setSave({ kind: "saved", at: hhmm() });
      qc.setQueryData(["estimate", initial.id], e);
      void qc.invalidateQueries({ queryKey: ["estimate-deleted-sheets", initial.id] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      refetchFollows(e);
      if (initial.revising) {
        void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
        void qc.invalidateQueries({ queryKey: ["estimate-marks", initial.id] });
      }
      setTrashDlg(false);
      setCur(e.restoredSheetId);
      toast(SHEET_TRASH_MSG.restored.replace("{name}", name || "（名前なし）"), undefined, "success", "sheet-restored");
    } catch (err) {
      dropSameTop(); // 断られた＝紙は変わっていない。控えた1手（いまと同じ形）は捨てる
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    } finally {
      setSheetRestoring(false);
    }
  };
  /** #199：内訳をやめて1枚にまとめるとき、不使用の内訳があれば確かめる（#200 から不使用の内訳とその行は「削除した内訳」へ） */
  const [mergeAsk, setMergeAsk] = useState<string[] | null>(null);
  /**
   * 1枚 ⇄ 表紙＋内訳（試作 v3.8）。1枚にまとめるとき、内訳の名前になった見出しに secHead、閉じる小計に secEnd、
   * 表紙にあった行に coverRow の印を付けておき、分け直すときはその印の間をそのまま元の内訳に戻す。
   * 印の無いところ（1枚のまま新しく書いた部分）は見出しの行で分ける。印は画面の中だけ（保存しない）
   */
  const toggleSections = (opts: { dropUnused?: boolean } = {}) => {
    // #199：不使用の内訳があれば、先に確かめる（決定で不使用の内訳とその行を消してまとめる）
    if (useSections && unusedSheetIds.size && !opts.dropUnused) {
      setMergeAsk(breakdowns.filter((x) => unusedSheetIds.has(x.id)).map((x) => x.name || "（名前なし）"));
      return;
    }
    dropAutoBlank();
    setEdit(null);
    setSecEdit(null);
    record();
    const mk = marks.current;
    if (!useSections) {
      const orig = origSheets.current;
      const cover: Sheet = { ...newSheet("cover", "表紙"), ...(orig.cover ? { id: orig.cover } : {}) };
      const secs: Sheet[] = [];
      const out: Row[] = [];
      let curSec: Sheet | null = null;
      let endIdx = -1;
      const hadMarks = rows.some((r) => r.role === "group" && mk[r.id] === "secHead");
      rows.forEach((r, i) => {
        if (r.role === "group" && mk[r.id] === "secHead") {
          curSec = newSheet("breakdown", r.name);
          const was = orig.bySecHead[r.id];
          if (was && !secs.some((x) => x.id === was)) curSec.id = was; // まとめる前と同じ紙
          curSec.note = r.note;
          curSec.isLabor = false;
          secs.push(curSec);
          endIdx = -1;
          for (let k = i + 1; k < rows.length; k++) {
            if (rows[k]!.role === "group" && mk[rows[k]!.id] === "secHead") break;
            if (rows[k]!.role === "subtotal" && mk[rows[k]!.id] === "secEnd") { endIdx = k; break; }
          }
          return;
        }
        if (i === endIdx) { endIdx = -1; return; } // 閉じる小計は落とす（内訳の合計は表紙に出る）
        if (i < endIdx) { out.push({ ...r, sheetId: curSec!.id }); return; } // 元の内訳の中身は見出しも小計もそのまま
        if (mk[r.id] === "coverRow" && r.role !== "group") { out.push({ ...r, sheetId: cover.id }); return; } // 元は表紙にあった行は表紙へ
        if (r.role === "group") { curSec = newSheet("breakdown", r.name); curSec.note = r.note; secs.push(curSec); return; } // 印の無い見出し＝新しい内訳
        if (r.role === "subtotal") return; // 1枚の小計は落とす（内訳ごとの合計が表紙に並ぶ）
        if (!curSec) { curSec = newSheet("breakdown", "内訳1"); secs.push(curSec); }
        out.push({ ...r, sheetId: curSec.id });
      });
      if (!secs.length) secs.push(newSheet("breakdown", "内訳1"));
      marks.current = {};
      origSheets.current = { bySecHead: {} };
      putSheets([cover, ...secs]);
      putRows(out);
      setCur("cover");
      markDirty(true);
      toast(hadMarks ? `${secs.length}つの内訳に戻しました。` : `見出しで ${secs.length}つの内訳に分けました。`);
    } else {
      const out: Row[] = [];
      const newMarks: Record<string, Mark> = {};
      origSheets.current = { bySecHead: {}, cover: coverSheet?.id };
      // #199：不使用の内訳とその行は消える（確かめたあと）
      for (const sh of usedBreakdowns) {
        const h = newRow("group", { name: sh.name, note: sh.note });
        newMarks[h.id] = "secHead";
        origSheets.current.bySecHead[h.id] = sh.id;
        out.push(h);
        for (const r of rows.filter((x) => x.sheetId === sh.id)) out.push({ ...r, sheetId: null, isLabor: r.role === "detail" && (r.isLabor || sh.isLabor) });
        const t = newRow("subtotal", { name: "小計" });
        newMarks[t.id] = "secEnd";
        out.push(t);
      }
      let coverSub = false;
      for (const r of rows.filter((x) => x.sheetId === coverSheet?.id)) {
        newMarks[r.id] = "coverRow";
        if (r.role === "subtotal") coverSub = true;
        out.push({ ...r, sheetId: null });
      }
      marks.current = newMarks;
      putSheets([]);
      putRows(out);
      setCur("cover");
      markDirty(true);
      toast(coverSub ? "1枚にまとめました。表紙にあった小計は「前の小計の次から」になるので、率の対象を確かめてください。" : "1枚にまとめました。");
    }
  };
  /** 表紙の「内訳の合計」の行：名前 → 備考 → 次の内訳の行 → 表紙の最初の行 */
  const secNext = (id: string, fid: "f_name" | "f_note") => {
    if (fid === "f_name") { focusNext("f_note"); return; }
    const i = usedBreakdowns.findIndex((x) => x.id === id);
    const nx = usedBreakdowns[i + 1];
    if (nx) { setSecEdit(nx.id); focusNext("f_name"); return; }
    setSecEdit(null);
    if (visible.length) editRow(visible[0]!);
  };

  /* ---- 案件（現場）：顧客の下の箱。039 から見積は必ずどれかの案件に入る（docs/28）。雛形には顧客が無いので引かない ---- */
  const groups = useQuery({ queryKey: ["groups", head.clientId], queryFn: () => api<ProjectGroupList>("GET", `/partners/${head.clientId}/groups`), enabled: !alt });
  /* ---- 宛先の支店・営業所（028）：顧客に支店があるときだけセレクトを出す。出したあとは snapshot の名前 ---- */
  const branches = useQuery({ queryKey: ["branches", head.clientId], queryFn: () => api<PartnerBranchList>("GET", `/partners/${head.clientId}/branches`), enabled: !alt && !readOnly });
  const branchList = branches.data?.items ?? [];
  const branchName = readOnly
    ? (initial.issued?.recipientBranch ?? initial.client.branch?.name ?? null)
    : head.partnerBranchId
      ? (branchList.find((b) => b.id === head.partnerBranchId)?.name ?? (initial.client.branch?.id === head.partnerBranchId ? initial.client.branch.name : null))
      : null;
  const groupList = groups.data?.items ?? [];
  const curGroup: ProjectGroup | undefined =
    groupList.find((g) => g.id === head.projectGroupId) ??
    (initial.projectGroup && initial.projectGroup.id === head.projectGroupId
      ? { ...initial.projectGroup, clientId: head.clientId, partnerBranchId: null, partnerContactId: null, staffName: "", completedAt: null, nameFollowsSubject: initial.projectGroup.nameFollowsSubject ?? false, quoteCount: 0, jobCount: 0, orderedCount: 0, orderedTotal: 0, isOngoing: false, startedOn: null }
      : undefined);
  /** セレクトに並べる案件：完了にしたものは出さない（この見積が入っている案件は完了でも出す） */
  const pickableGroups = groupList.filter((g) => !g.completedAt || g.id === head.projectGroupId);
  /** 案件の名前が工事名に合わせて変わるか（自動でできて、見積がこの1枚のあいだ。docs/28 決めごと9） */
  const follows = !readOnly && !head.newGroup && !!curGroup?.nameFollowsSubject && (curGroup.quoteCount <= 1);
  /* ---- 顧客担当者（039）：顧客の詳細（担当者と支店）を引く。出したあとは凍結した宛名 ---- */
  const partnerQ = useQuery({ queryKey: ["partner", head.clientId], queryFn: () => api<Partner>("GET", `/partners/${head.clientId}`), enabled: !alt && !readOnly });
  const contactLabel = readOnly
    ? (initial.issued ? initial.issued.recipientContact : initial.client.contact ? `${initial.client.contact.department} ${initial.client.contact.name}`.trim() : null)
    : head.partnerContactId
      ? (() => {
          const k = partnerQ.data?.contacts.find((c) => c.id === head.partnerContactId) ?? (initial.client.contact?.id === head.partnerContactId ? initial.client.contact : null);
          return k ? `${k.department} ${k.name}`.trim() : null;
        })()
      : null;
  const [newGroupForm, setNewGroupForm] = useState<{ name: string; siteAddress: string; ownerName: string } | null>(null);
  const [groupError, setGroupError] = useState<string | undefined>();
  /** 案件を選ぶ。現場住所を見積に写したら true（空のときだけ写す。打ち直しはできる） */
  const pickGroup = (g: ProjectGroup): boolean => {
    const patch: Partial<Head> = { projectGroupId: g.id, newGroup: false };
    if (!head.siteAddress && g.siteAddress) patch.siteAddress = g.siteAddress;
    setH(patch);
    if (!patch.siteAddress) return false;
    setMore(true);
    toast(`${L.project}「${g.name}」から ${L.site} を写しました。`);
    return true;
  };
  const createGroup = useMutation({
    mutationFn: (v: { name: string; siteAddress: string; ownerName: string }) => api<ProjectGroup>("POST", `/partners/${head.clientId}/groups`, { name: v.name, siteAddress: v.siteAddress || undefined, ownerName: v.ownerName || undefined }),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ["groups", head.clientId] });
      setNewGroupForm(null);
      setGroupError(undefined);
      if (!pickGroup(g)) toast(`${L.project}「${g.name}」を作成しました。次からはこの${L.customer}の${L.project}に表示します。`);
    },
    onError: (e) => setGroupError(e instanceof ApiError && e.problem.errors?.name ? e.problem.errors.name : messageOf(e)),
  });

  const openIssue = async () => {
    closeEdit();
    // 書きかけを送り終えてから（番号は出す瞬間に付く）。保存できていなければ窓を開かずに理由を出す（#177）
    const why = await sq.ready();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    let company = issueDlg.company;
    if (needsCompany && !company.name) {
      try {
        const c = await api<Company>("GET", "/company");
        company = { name: c.name, address: c.address, phone: c.phone };
      } catch { /* 空のまま */ }
    }
    setIssueDlg({ open: true, company, busy: false });
  };
  const doIssue = async () => {
    if (needsCompany && !issueDlg.company.name.trim()) {
      setIssueDlg((d) => ({ ...d, error: ISSUE_MSG.companyRequired }));
      document.getElementById("is_co")?.focus();
      return;
    }
    setIssueDlg((d) => ({ ...d, busy: true, error: undefined }));
    try {
      const why = await sq.ready();
      if (why != null) {
        setIssueDlg((d) => ({ ...d, busy: false, error: saveGateText("issue", why) }));
        return;
      }
      const r = await api<IssueResult>("POST", `/estimates/${initial.id}/issue`, needsCompany ? { company: { name: issueDlg.company.name.trim(), address: issueDlg.company.address.trim(), phone: issueDlg.company.phone.trim() } } : {});
      ownVersions.current.add(r.updatedAt); // 読み直した見積書がこの版なら、窓を出さずに取り直す（#202 の直し）
      const notice = `見積書ができました。番号 ${r.displayNo ?? r.documentNo} が付きました。`;
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      if (!pcOnly.phone) {
        // 2026-10-22：PC は発行が通ったら送信の画面（紙の画面＝送信方法・送付状）へ。一言は移った先で出す。「‹ 見積書」で戻れる
        setIssueDlg({ open: false, company: issueDlg.company, busy: false });
        void qc.invalidateQueries({ queryKey: ["estimate", initial.id] });
        namePageNow(pageKind, docShort(pageKind, r.displayNo ?? r.documentNo)); // 送信の画面の戻るボタンに番号（移る前に。読み直しを待たない）
        nav(pdfUrl, { state: { notice, noticeTestId: "issued-notice" } });
        return;
      }
      setIssueDlg({ open: false, company: issueDlg.company, busy: false, justIssued: r });
      // 発行の直後の一言は通知（#171。前は題の下の帯「見積書ができました」）
      toast(notice, undefined, "success", "issued-notice");
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] }); // 読み取りの紙に切り替わる
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e instanceof ApiError && e.problem.errors ? (Object.values(e.problem.errors)[0] as string | undefined) : undefined;
      setIssueDlg((d) => ({ ...d, busy: false, error: err ?? messageOf(e) }));
    }
  };

  /* ---- 訂正版（#27、032、docs/18） ---- */
  const [reviseAsk, setReviseAsk] = useState(false); // 受注後の二択（直す／追加の工事）
  const [stopRevise, setStopRevise] = useState<{ open: boolean; busy: boolean; error?: string | undefined }>({ open: false, busy: false });
  const [reviseIssue, setReviseIssue] = useState<{ open: boolean; diff?: RevisionDiff | undefined; note: string; changeSheet: boolean; busy: boolean; error?: string | undefined }>({ open: false, note: "", changeSheet: false, busy: false });
  /* ---- 直しているときの印と「戻す」（037、docs/27 の3回目）。スモール・ビジネスのプランだけ。
     出どころは DB の差分（GET /estimates/:id/marks）。画面では計算しない。保存のたびに取り直す ---- */
  const marking = !alt && !!initial.revising && initial.historyEnabled;
  const marksQ = useQuery({ queryKey: ["estimate-marks", initial.id], queryFn: () => api<RevisionMarks>("GET", `/estimates/${initial.id}/marks`), enabled: marking, placeholderData: keepPreviousData, retry: false });
  /**
   * 相違（#190：スイッチ2つ「相違内容表示」「変更行のみ表示」を3択のセグメントに）：なし（既定）／表示／変更行のみ。
   * 「表示」はこの端末に覚える（gb.marks.mode）。「変更行のみ」は覚えない（次に開くと「表示」）＝開いたら行が隠れている、を作らない
   */
  const [marksMode, setMarksModeState] = useState<MarksMode>(() => {
    try {
      return localStorage.getItem("gb.marks.mode") === "show" ? "show" : "none";
    } catch {
      return "none";
    }
  });
  const setMarksMode = (m: MarksMode) => {
    if ((m === "only") !== (marksMode === "only")) closeEdit(); // 隠れる行を書いている途中で切り替えない
    setMarksModeState(m);
    try {
      if (m === "none") localStorage.removeItem("gb.marks.mode");
      else localStorage.setItem("gb.marks.mode", "show");
    } catch {
      /* 控えられない端末では、この画面の間だけ */
    }
  };
  const showMarks = marksMode !== "none";
  const onlyChanged = marksMode === "only";
  /** 印を出しているときだけ中身がある（外すと、いつものエディタと同じ見た目） */
  const mk: RevisionMarks | undefined = marking && showMarks ? marksQ.data : undefined;
  const markOf = new Map<string, RevisionMarkLine>((mk?.lines ?? []).map((x) => [x.lineId, x]));
  const [restoring, setRestoring] = useState(false);
  /** 書きかけを送り終えるまで待つ。送れなかった（400 など）ら false（共通の仕組み。#177） */
  const settle = sq.settle;
  /* ---- 049：サーバーの答えで紙を描き直す（取込・外す） ---- */
  /** サーバーの答えを「同じだった中身」として覚える（版と、読み直しの元） */
  const syncedFrom = (e: Estimate) => {
    if (template || invoice) return;
    versionRef.current = e.updatedAt;
    baseRef.current = { head: { ...latest.current.head }, rows: e.lines.map(fromServerLine), sheets: e.sheets.map(sheetOf) };
  };
  const applyServer = (e: Estimate) => {
    rememberLineage(e);
    const rs = e.lines.map(fromServerLine);
    const ss = e.sheets.map(sheetOf);
    putRows(rs);
    putSheets(ss);
    cutHistory(); // サーバーで行が変わった（取込・外す・端数処理・拾い出し）＝ここより前には戻さない
    syncedFrom(e);
    setCalc(fromServerCalc(e));
    setSave({ kind: "saved", at: hhmm() });
    qc.setQueryData(["estimate", initial.id], e);
    void qc.invalidateQueries({ queryKey: ["estimates"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    refetchFollows(e); // 取込・外す・端数処理・拾い出し：関わる所が変わったときだけ読み直す（性能の改善 A）
    if (initial.revising) {
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      void qc.invalidateQueries({ queryKey: ["estimate-marks", initial.id] });
    }
  };
  /*
   * 版の取り直し（#202 の誤検知の直し、2026-10-18、docs/82 §9）：画面のデータ（initial＝GET /estimates/:id の答え）が、この画面の覚えている版より
   * 新しくなったとき。1回目の発行・取消しは回の id が変わらずエディタを作り直さないので、前は発行の前の版のまま次の保存を送り、
   * 自分の操作に 409 を返されて「他の利用者が変更しました」が出た。
   *   自分の変更（発行・取消しの答えの版＝ownVersions か、最後に版を進めたのが自分＝updatedBy）で、この画面に送っていない修正が無い
   *   → 窓を出さずに最新の中身と版に入れ替える（版だけ取り直すと、古い欄のまま次の保存で最新を上書きしうる＝中身も一緒に）
   *   ほかの利用者の変更・送っていない修正がある → 何もしない（今までどおり、次の保存の 409 で「再読込しますか？」）
   */
  const ownVersions = useRef(new Set<string>());
  const adoptServer = (e: Estimate) => {
    const h = { ...latest.current.head, ...fromServerHead(e) };
    const rs = e.lines.map(fromServerLine);
    const ss = e.sheets.map(sheetOf);
    rememberLineage(e);
    versionRef.current = e.updatedAt;
    const same = JSON.stringify([latest.current.head, latest.current.rows, latest.current.sheets]) === JSON.stringify([h, rs, ss]);
    if (!same) {
      setHead(h);
      putRows(rs);
      putSheets(ss);
      latest.current = { head: h, rows: rs, sheets: ss };
      cutHistory(); // サーバーで中身が変わった＝ここより前には戻さない
    }
    baseRef.current = { head: h, rows: rs, sheets: ss };
    setCalc(fromServerCalc(e));
    refetchFollows(e);
  };
  useEffect(() => {
    if (alt) return;
    const v = initial.updatedAt;
    if (!(Date.parse(v) > Date.parse(versionRef.current))) return; // 同じ版（自分の保存の答え）・古い答え
    const mine = ownVersions.current.has(v) || (!!initial.updatedBy && initial.updatedBy === me.data?.user.id);
    if (!mine) return;
    if (sq.isDirty() || sq.isInflight() || sq.unsent || staleRef.current) return; // 送っていない修正がある：次の保存の 409 で窓
    adoptServer(initial);
  }, [initial]); // 画面のデータが入れ替わったときだけ（自分の保存の答えは版が同じなので何もしない）
  /** 取込の1枚を開く（行の「取込」か、帯の「まとめて追加」）。外注見積が無い・読み取り中なら文で知らせる */
  const openImport = (rowId: string | null, quoteId?: string) => {
    if (alt) return;
    dropAutoBlank();
    setEdit(null);
    setSecEdit(null);
    const ready = (vprog.data?.quotes ?? []).filter((q) => q.status === "review" || q.status === "done" || q.status === "manual");
    const q = quoteId ? ready.find((x) => x.vendorQuoteId === quoteId) : ready[0];
    if (!q) {
      toast((vprog.data?.quotes ?? []).length ? VENDOR_IMPORT_MSG.reading : fillLabels(VENDOR_IMPORT_MSG.none, L));
      return;
    }
    setVimp({ quoteId: q.vendorQuoteId, targets: rowId ? [rowId] : [] });
  };
  /** #198：{vendorQuote}の一覧の右側パネルの「見積で取込」（?vendorImport=<外注見積>）＝届いたらその外注見積で取込の1枚を開き、問い合わせは消す */
  const [vimpSp, setVimpSp] = useSearchParams();
  const vimpParam = vimpSp.get("vendorImport");
  useEffect(() => {
    if (!vimpParam || !vprog.data) return;
    openImport(null, vimpParam);
    setVimpSp(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete("vendorImport");
        return n;
      },
      { replace: true },
    );
  }, [vimpParam, vprog.data]); // eslint-disable-line react-hooks/exhaustive-deps
  /** 取込（POST /estimates/:id/vendor-import）。書きかけを送ってから */
  const doImport = async (req: Omit<VendorImportRequest, "sheetId" | "afterLineId"> & { afterLineId: string | null }): Promise<boolean> => {
    if (!(await settle())) {
      toast(RESTORE_MSG.unsaved);
      return false;
    }
    try {
      const body: VendorImportRequest = { ...req, sheetId: req.afterLineId ? null : curSheetId };
      const e = await api<Estimate & { message: string }>("POST", `/estimates/${initial.id}/vendor-import`, body);
      applyServer(e);
      toast(e.message);
      // まとめた行は名前が空＝その行を開いて名前の欄へ
      if (req.mode === "merge" && req.targetLineId) {
        const k = e.lines.findIndex((l) => l.id === req.targetLineId);
        if (k >= 0) { setEdit(k); focusNext("f_name"); }
      }
      return true;
    } catch (err) {
      toast(messageOf(err), undefined, "error");
      return false;
    }
  };
  /** 「外す」：仕入れを手入力に戻す（値は残す）。外注の行は「残り」に戻る */
  const doUnlink = async (lineId: string) => {
    if (!(await settle())) {
      toast(RESTORE_MSG.unsaved);
      return;
    }
    try {
      const e = await api<Estimate & { message: string }>("POST", `/estimates/${initial.id}/vendor-unlink`, { lineId });
      applyServer(e);
      toast(e.message);
    } catch (err) {
      toast(messageOf(err), undefined, "error");
    }
  };
  /* ---- #108：端数処理を当て直す（設定を変えても作りかけの見積は自動では変えない。docs/41 ★3） ---- */
  const roundingDiffers = !alt && !readOnly && !!calc.roundingNow && (calc.roundingNow.lineRoundingMode !== calc.lineRoundingMode || calc.roundingNow.roundingMode !== calc.roundingMode);
  const [roundingBusy, setRoundingBusy] = useState(false);
  const doApplyRounding = async () => {
    if (!(await settle())) {
      toast(RESTORE_MSG.unsaved);
      return;
    }
    setRoundingBusy(true);
    try {
      const e = await api<Estimate & { message: string }>("POST", `/estimates/${initial.id}/rounding`);
      applyServer(e);
      toast(e.message);
    } catch (err) {
      toast(messageOf(err), undefined, "error");
    } finally {
      setRoundingBusy(false);
    }
  };
  /** 取込の1枚の「＋ 行追加（分ける）」：最後の入れる先の下に行を足す */
  const addTarget = () => {
    if (!vimp) return;
    const lastId = vimp.targets[vimp.targets.length - 1];
    const k = rows.findIndex((r) => r.id === lastId);
    if (k < 0) return;
    const nr = mkRow("detail", { sheetId: rows[k]!.sheetId ?? null, quantity: 1, unit: "式" });
    record();
    putRows((rs) => [...rs.slice(0, k + 1), nr, ...rs.slice(k + 1)]);
    setVimp({ ...vimp, targets: [...vimp.targets, nr.id] });
    markDirty(true);
  };
  const removeTarget = (id: string) => {
    if (!vimp) return;
    record();
    putRows((rs) => rs.filter((r) => r.id !== id));
    setVimp({ ...vimp, targets: vimp.targets.filter((x) => x !== id) });
    markDirty(true);
  };
  const editTarget = (id: string, patch: Partial<TargetRow>) => {
    const k = rows.findIndex((r) => r.id === id);
    if (k >= 0) upd(k, patch as Partial<Row>);
  };
  const rowNoOf = (id: string): number => {
    let n = 0;
    for (const r of rows) {
      if (NUMBERED.has(r.role)) n++;
      if (r.id === id) return NUMBERED.has(r.role) ? n : 0;
    }
    return 0;
  };
  const targetRows: TargetRow[] = (vimp?.targets ?? []).map((id) => rows.find((r) => r.id === id)).filter((r): r is Row => !!r).map((r) => ({ id: r.id, role: r.role, name: r.name, spec: r.spec, note: r.note, quantity: r.quantity, unit: r.unit, unitPrice: r.unitPrice, costAmount: r.costAmount, sheetId: r.sheetId }));

  /**
   * 「戻す」「前回の内容に復元」：サーバーが前の回の行を同じ系譜で入れ直す。返ってきた下書きで紙を描き直す。
   * #195：復元の前の形を1手として控える（↶ で復元の前に戻れる。履歴は切らない）。復元で入った行・内訳の id は rememberLineage で系譜を覚えるので、
   * ↶ → ↷ で入れ直しても同じ系譜（「追加」にならない）
   */
  const doRestore = async (kind: "line" | "sheet", lineageId: string, okMsg: string) => {
    if (restoring) return;
    setRestoring(true);
    try {
      dropAutoBlank();
      setEdit(null);
      setSecEdit(null);
      if (!(await settle())) {
        toast(RESTORE_MSG.unsaved);
        return;
      }
      breakTyping();
      record();
      const e = await api<Estimate>("POST", `/estimates/${initial.id}/restore`, { kind, lineageId });
      rememberLineage(e);
      const rs = e.lines.map(fromServerLine);
      const ss = e.sheets.map(sheetOf);
      putRows(rs);
      putSheets(ss);
      syncedFrom(e);
      setCalc(fromServerCalc(e));
      setSave({ kind: "saved", at: hhmm() });
      qc.setQueryData(["estimate", initial.id], e);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["estimate-marks", initial.id] });
      toast(okMsg);
    } catch (err) {
      dropSameTop(); // 断られた＝紙は変わっていない。控えた1手（いまと同じ形）は捨てる
      toast(messageOf(err), undefined, "error");
    } finally {
      setRestoring(false);
    }
  };

  /** タブの数：その紙の 変更＋追加＋削除（数は API が数えたもの） */
  const tabCount = (sheetId: string | undefined) => {
    const c = sheetId ? (mk?.sheetCounts[sheetId] ?? 0) : 0;
    return c > 0 ? <span className="chip ai tabmk" data-testid="tab-count">変更 {c}</span> : null;
  };
  /** 消した行：いま開いている紙の分を、元の場所（直前の行の次。無ければ紙のいちばん上）に出す */
  const removedHere = (mk?.removed ?? []).filter((x) => (x.sheetId ?? null) === curSheetId);
  const visibleIds = new Set(visible.map((i) => rows[i]!.id));
  const removedTop = removedHere.filter((x) => !x.afterLineId || !visibleIds.has(x.afterLineId));
  const removedAfter = (lineId: string) => removedHere.filter((x) => x.afterLineId === lineId);
  const removedRow = (x: RevisionMarkRemoved) => (
    <RemovedRow key={"rm-" + x.lineageId} ctx={ctx} x={x} busy={restoring} onRestore={() => void doRestore("line", x.lineageId, RESTORE_MSG.restored)} />
  );
  /** 消した内訳：表紙に出す（この内訳の次。無ければ表紙のいちばん上） */
  const removedSheets = onCover ? (mk?.removedSheets ?? []) : [];
  const breakdownIds = new Set(breakdowns.map((x) => x.id));
  const removedSheetRow = (x: RevisionMarkRemovedSheet) => (
    <RemovedSheetRow key={"rs-" + x.lineageId} ctx={ctx} x={x} busy={restoring} onRestore={() => void doRestore("sheet", x.lineageId, RESTORE_MSG.sheetRestored)} />
  );
  /** 「変更行のみ」：印のある行と、いま書いている行だけ */
  const hideRow = (i: number) => onlyChanged && !!mk && edit !== i && !markOf.has(rows[i]!.id);
  const hideSec = (sheetId: string) => onlyChanged && !!mk && secEdit !== sheetId && !(mk.sheetCounts[sheetId] ?? 0);

  /** 見積を追加（033 → 039、docs/28）。同じ顧客・案件の新しい下書きへ（工事名は「案件名（追加工事）」） */
  /**
   * 受注・失注（#302 C3）：一覧の行の「受注」「失注」と同じ動き（確認は挟まず、通知の「元に戻す」で未受注に戻す）。
   * 頭の帯の主なボタンが「受注」、「失注」は ⋯ の中。決めたら見積書を読み直す（状態のチップ・請求書作成のボタンに変わる）
   */
  const refreshDecided = async () => {
    void qc.invalidateQueries({ queryKey: ["estimates"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    void qc.invalidateQueries({ queryKey: ["group"] });
    await qc.invalidateQueries({ queryKey: ["estimate", initial.id] });
  };
  const undecideHere = async () => {
    try {
      await api("POST", `/estimates/${initial.id}/undecide`, {});
      await refreshDecided();
      toast(`${head.subject || head.clientName} を未受注に戻しました。`);
    } catch (e) {
      toast(e instanceof ApiError && e.code === "UNDECIDE_HAS_INVOICE" ? DECIDE_MSG.hasInvoice : messageOf(e), undefined, "error");
    }
  };
  const decideHere = async (decision: "ordered" | "lost") => {
    try {
      await api<DecideResult>("POST", `/estimates/${initial.id}/decide`, { decision });
      await refreshDecided();
      toast(decision === "ordered" ? `${head.subject || head.clientName} を受注にしました。「請求書作成」から請求書を作成できます。` : `${head.subject || head.clientName} を失注にしました。`, { label: "元に戻す", onClick: () => void undecideHere() }, undefined, "decide-notice");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const addJob = async () => {
    setReviseAsk(false);
    try {
      const e2 = await api<Estimate>("POST", `/estimates/${initial.id}/add-job`);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["groups", head.clientId] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] }); // 元の見積に案件が付いた
      nav(`/estimates/${e2.id}`, { state: { fresh: true, notice: t(GROUP_MSG.jobAdded).replace("{name}", e2.projectGroup?.name ?? "") } });
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const doRevise = async () => {
    setReviseAsk(false);
    try {
      await api<Estimate>("POST", `/estimates/${initial.id}/revise`);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] }); // key が変わり、訂正版の編集に切り替わる
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const doStopRevise = async () => {
    setStopRevise((x) => ({ ...x, busy: true, error: undefined }));
    try {
      sq.discard(); // 書きかけは捨てる。消えるのはこの回の下書きだけ（出ている回はそのまま。035）
      await api<void>("DELETE", `/estimates/${initial.id}`);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] });
      setStopRevise({ open: false, busy: false });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setStopRevise((x) => ({ ...x, busy: false, error: messageOf(e) }));
    }
  };
  /** 比較（「（番号）として発行」の窓）。差分と「訂正の内容」の自動の下書きを取ってから開く */
  const openReviseIssue = async () => {
    closeEdit();
    const why = await sq.ready(); // 書きかけを送り終えてから。保存できていなければ窓を開かずに理由を出す（#177）
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    try {
      const diff = await api<RevisionDiff>("GET", `/estimates/${initial.id}/diff`);
      setReviseIssue({ open: true, diff, note: diff.autoNote, changeSheet: diff.changeSheetDefault, busy: false });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const doReviseIssue = async () => {
    const note = reviseIssue.note.trim();
    if (!note) {
      setReviseIssue((x) => ({ ...x, error: REVISE_MSG.noteRequired }));
      document.getElementById("rv_note")?.focus();
      return;
    }
    setReviseIssue((x) => ({ ...x, busy: true, error: undefined }));
    try {
      const why = await sq.ready();
      if (why != null) {
        setReviseIssue((x) => ({ ...x, busy: false, error: saveGateText("issue", why) }));
        return;
      }
      const r = await api<IssueResult>("POST", `/estimates/${initial.id}/issue`, { revisionNote: note, changeSheet: reviseIssue.changeSheet });
      ownVersions.current.add(r.updatedAt); // #202 の直し（訂正版はエディタを作り直すが、同じ形にしておく）
      setReviseIssue({ open: false, note: "", changeSheet: false, busy: false });
      const notice = `${r.displayNo ?? initial.displayNo ?? r.documentNo} として発行しました。`;
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      if (!pcOnly.phone) {
        // 2026-10-22：訂正版も PC は送信の画面へ（1回目の発行と同じ）
        void qc.invalidateQueries({ queryKey: ["estimate", initial.id] });
        namePageNow(pageKind, docShort(pageKind, r.displayNo ?? r.documentNo)); // 送信の画面の戻るボタンに番号（移る前に。読み直しを待たない）
        nav(pdfUrl, { state: { notice, noticeTestId: "issued-notice" } });
        return;
      }
      toast(notice, undefined, "success", "issued-notice");
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] });
      window.scrollTo({ top: 0 });
    } catch (e) {
      setReviseIssue((x) => ({ ...x, busy: false, error: messageOf(e) }));
    }
  };

  const doUnissue = async () => {
    setUnissue((u) => ({ ...u, busy: true, error: undefined }));
    try {
      const r = await api<UnissueResult>("POST", `/estimates/${initial.id}/unissue`, {});
      ownVersions.current.add(r.updatedAt); // 読み直した見積書がこの版なら、窓を出さずに取り直す（#202 の直し）
      setIssueDlg((d) => ({ ...d, justIssued: undefined }));
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["estimate-rounds", initial.id] });
      await qc.invalidateQueries({ queryKey: ["estimate", initial.id] }); // 書ける紙に戻る
      setUnissue({ open: false, busy: false, done: r.documentNo });
      // 取り消した直後は通知、そのあとは見出しのチップ「下書き」（#171・#273・#278）
      toast(`${r.documentNo} を取り消して下書きに戻しました。`, undefined, "success", "unissued-notice");
      window.scrollTo({ top: 0 });
    } catch (e) {
      setUnissue((u) => ({ ...u, busy: false, error: messageOf(e) }));
    }
  };
  const deleteDraft = async (cancelRfqs = false) => {
    try {
      await api<TrashResult>("DELETE", `/estimates/${initial.id}${cancelRfqs ? "?cancelRfqs=1" : ""}`);
      sq.discard();
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["estimates-trash"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["groups"] }); // 一緒にできた案件もゴミ箱へ（039・#200）
      void qc.invalidateQueries({ queryKey: ["group-list"] });
      // #200：ゴミ箱へ。一覧で「ゴミ箱に移しました」＋「元に戻す」（Trash.tsx の useTrashFlash）
      qc.removeQueries({ queryKey: ["estimate", initial.id] });
      nav("/estimates", { replace: true, state: { trashed: { id: initial.id } } });
    } catch (e) {
      // 送った見積依頼がある（#119、docs/56 §5.4）：取消して削除するかを聞く
      if (e instanceof ApiError && e.code === "ESTIMATE_HAS_RFQ") {
        setAskDelete(false);
        setAskRfqCancel(typeof e.problem.count === "number" ? e.problem.count : 1);
        return;
      }
      toast(messageOf(e), undefined, "error");
    }
  };

  /* ---- 見積の区分（#129）：並べ方を変える・場所を作る・まとめて付ける・確定にする ---- */
  /**
   * 並べ方の保存（PUT …/layout）は1つずつ送る（#176 B2）。続けて変えたときに2本が重なると、答えが前後して古い版を覚え、
   * 自分の次の行の保存が「ほかで変更があります」で断られていた。送っているあいだに選び直したら、終わってから最後に選んだものだけ送る。
   * 行の保存（flush）とも重ねない＝送るあいだは同じ「送っている最中」の印（inflight）を立てる（行の保存は印が下りてから送る）。
   * ほかの人・ほかの画面の本当の変更は、今までどおり行の保存の版の照合で断られる
   */
  const axisWant = useRef<LayoutAxis | null>(null);
  const axisBusy = useRef(false);
  const axisSaved = useRef<LayoutAxis>(initial.layoutAxis ?? "manual");
  const changeAxis = async (a: LayoutAxis) => {
    closeEdit();
    setAxSel(new Set());
    setAxis(a);
    axisWant.current = a;
    if (axisBusy.current) return; // 送っている最中：終わったら最後に選んだ並べ方を送る
    axisBusy.current = true;
    try {
      while (axisWant.current !== null) {
        const want = axisWant.current;
        axisWant.current = null;
        if (want === axisSaved.current) continue;
        await settle(); // 書きかけの行を送り、送っている最中の行の保存が終わるのを待つ（版をそろえる）
        // 送るあいだは行の保存と同じ「送っている最中」の印を立てる。終わったら待たせていた行の保存を送る（共通の仕組みの exclusive。#177）
        await sq.exclusive(async () => {
          // 版を付ける（2026-10-13：並べ方も版を進める＝確かめずに版を受け取ると、あとの保存がほかの直しを消しうる。違えば 409 →「読み直しますか？」）
          const version = template ? undefined : invoice ? invoice.version?.() : versionRef.current;
          const e = await api<Estimate>("PUT", invoice ? `/invoices/${initial.id}/layout` : `/estimates/${initial.id}/layout`, { axis: want, ...(version ? { version } : {}) });
          if (!invoice && !template) versionRef.current = e.updatedAt; // 並べ方も版を進める（行は変えないので読み直しの元はそのまま）
          if (invoice) invoice.synced?.(e);
          axisSaved.current = want;
          qc.setQueryData([docQ, initial.id], e);
          if (!invoice) void qc.invalidateQueries({ queryKey: ["estimates"] });
        });
      }
    } catch (err) {
      axisWant.current = null;
      setAxis(axisSaved.current);
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    } finally {
      axisBusy.current = false;
    }
  };
  /** 「＋ 新しい場所…」：名前を聞いて この見積の場所を作る（「2F ＞ 子供部屋」は2段で） */
  const askNewPlace = (done: (placeId: string) => void) => {
    setNameDlg({
      title: "新しい場所（2段にするときは「2F ＞ 子供部屋」）",
      value: "",
      onOk: (v) => {
        void axLists
          .createPlace(v)
          .then(done)
          .catch((err) => toast(messageOf(err), undefined, "error"));
      },
    });
  };
  /* ---- 拾い出し（#130）：数量は DB が決める。足す・直す・消すたびにサーバーの答えで紙を描き直す ---- */
  /** いま紙に出す数量（takeoff の行は DB が決めた値。保存の答えが返るまでは手元の値） */
  const qtyOf = (r: Row): number => (r.quantitySource === "takeoff" ? (calc.qty[r.id] ?? r.quantity ?? 0) : (r.quantity ?? 0));
  const openTakeoff = (lineId: string | null) => {
    if (alt) return;
    dropAutoBlank();
    setEdit(null);
    setSecEdit(null);
    setTkOpen({ lineId });
  };
  /** 書きかけを送ってから拾い出しの API を呼び、答え（見積）で紙を描き直す */
  const runTakeoff = async (call: (version: string) => Promise<TakeoffAnswer>): Promise<TakeoffAnswer | undefined> => {
    if (!readOnly && !(await settle())) {
      toast(RESTORE_MSG.unsaved);
      return undefined;
    }
    // 送るあいだは行の保存と同じ「送っている最中」の印を立てる（紙の保存と重ねない。#179 の3）。終わったら待たせていた紙の保存を送る
    return sq.exclusive(async () => {
      // 見積の版を付けて送る（2026-10-13：拾い出しの保存も見積の版を進める。ほかで変わっていたら 409 →「読み直しますか？」）
      let r: TakeoffAnswer;
      try {
        r = await call(versionRef.current);
      } catch (err) {
        if (isStale(err)) enterStale();
        throw err;
      }
      if (readOnly) {
        // 出したあとの紙：数量は変わらない。件数だけ取り直す（行は書き換えない）。版は進む
        versionRef.current = r.estimate.updatedAt;
        setCalc(fromServerCalc(r.estimate));
        qc.setQueryData(["estimate", initial.id], r.estimate);
      } else if (sq.isDirty()) {
        // 答えを待つ間に紙を直していた：答えの上にその直しを重ねる（答えで消さない。続けて送る）
        applyServerKeepingEdits(r.estimate);
      } else {
        applyServer(r.estimate);
      }
      return r;
    });
  };
  /** サーバーの答え（拾い出しなど）で紙を描き直すとき、まだ送っていない紙の直しを答えの上に重ねる（読み直しの 409 と同じ rebaseEdits。#179 の3） */
  const applyServerKeepingEdits = (e: Estimate) => {
    const theirsSnap = { head: { ...latest.current.head, ...fromServerHead(e) }, rows: e.lines.map(fromServerLine), sheets: e.sheets.map(sheetOf) };
    const mine = { head: latest.current.head, rows: latest.current.rows, sheets: latest.current.sheets };
    const merged = rebaseEdits(baseRef.current, mine, theirsSnap);
    rememberLineage(e);
    versionRef.current = e.updatedAt;
    baseRef.current = theirsSnap;
    setHead(merged.head);
    putRows(merged.rows);
    putSheets(merged.sheets);
    latest.current = merged;
    cutHistory();
    setCalc(fromServerCalc(e));
    qc.setQueryData(["estimate", initial.id], e);
    refetchFollows(e);
    markDirty(true);
  };
  /** 数量の出どころを切り替える：手で打つに戻す（値は残す）／拾い出しの合計にする（数量は保存の答えで入る） */
  const setQuantitySource = (lineId: string, source: QuantitySource) => {
    const i = rows.findIndex((r) => r.id === lineId);
    if (i < 0 || readOnly) return;
    upd(i, source === "manual" ? { quantitySource: "manual", quantity: qtyOf(rows[i]!) } : { quantitySource: "takeoff" });
  };
  /** 一式の行の下に「内訳の行」を足す（親＝その一式の行。合計には、単価を入れた分だけ入る） */
  const addChildLine = (i: number) => {
    const p = rows[i]!;
    let at = i + 1;
    while (at < rows.length && rows[at]!.parentLineId === p.id) at++;
    const r = mkRow("detail", { sheetId: p.sheetId ?? null, parentLineId: p.id, placeId: p.placeId, tradeId: p.tradeId });
    record();
    putRows((rs) => [...rs.slice(0, at), r, ...rs.slice(at)]);
    setEdit(at);
    markDirty(true);
    focusNext("f_name");
  };
  /** 内訳のない一式（社内用の知らせ。#130）：基準は会社の設定、印は DB が決めたもの、金額はいま紙に出ている額 */
  const lumpsum = alt
    ? null
    : lumpsumWarningsOf(
        rows.filter(inUseRow).map((r) => ({ id: r.id, amount: calc.amounts[r.id] ?? 0, lumpsumBasis: r.role === "detail" && isLumpsumUnit(r.unit) ? (calc.basis[r.id] ?? null) : null })), // #199：使う内訳だけ
        calc.subtotal,
        initial.lumpsumWarnings?.rule ?? LUMPSUM_RULE_DEFAULT,
      );
  const applyBulk = (patch: BulkPatch) => {
    const n = axSel.size;
    // #305：区分（明細／労務費）は選んだうちの明細の行だけ。労務費にすると材工が空の行は「手間」（区分の選ぶ欄と同じ）
    const { isLabor, ...rest } = patch;
    const p = withVendor(rest);
    record();
    putRows((rs) =>
      rs.map((r) => {
        if (!axSel.has(r.id)) return r;
        const x: Row = { ...r, ...p };
        if (isLabor === undefined || r.role !== "detail") return x;
        return { ...x, isLabor, ...(isLabor && !x.workType ? { workType: "labor" as const } : {}) };
      }),
    );
    markDirty(true);
    setAxSel(new Set()); // 付けたら選ぶのをやめる（帯を閉じる）
    toast(`${n}行に付けました。`);
  };
  const finalizeRows = (ids: Set<string>) => {
    const n = rows.filter((r) => ids.has(r.id) && r.lineState === "provisional").length;
    record();
    putRows((rs) => rs.map((r) => (ids.has(r.id) && r.lineState === "provisional" ? { ...r, lineState: "final" } : r)));
    markDirty(true);
    if (ids === axSel) setAxSel(new Set());
    toast(n ? `${n}行を確定にしました。` : "確定にしました。");
  };
  const provisionalCount = rows.filter((r) => r.lineState === "provisional" && r.role === "detail").length;
  const selectedProvisional = rows.filter((r) => axSel.has(r.id) && r.lineState === "provisional").length;
  /**
   * #199：選んだ行の複製（画面の中で写す＝保存で入る。↶ で戻せる）。写した行は選んだ行の直後（選んだうち最後の行の次）に、選んだ順のまま。
   * 新しい id（系譜も保存で新しく）。親（一式の内訳）・率の基礎が選んだ行なら写した行へ付け替える。総額指定の値引きは1見積に1つなので写さない。
   * 拾い出しは画面に無いので写さない＝数量は同じ値の手入力（内訳の複製は拾い出しも写す＝fn_copy_sheet）
   */
  const copyRows = (ids: Set<string>) => {
    const src = rows.filter((r) => ids.has(r.id) && !(r.role === "discount" && r.discountTargetTotal != null));
    const skipped = rows.filter((r) => ids.has(r.id)).length - src.length;
    if (!src.length) {
      if (skipped) toast(ESTIMATE_PC_MSG.sheetCopyNoTarget);
      return;
    }
    dropAutoBlank();
    setEdit(null);
    const map = new Map(src.map((r) => [r.id, crypto.randomUUID()]));
    const copies: Row[] = src.map((r) => ({
      ...r,
      id: map.get(r.id)!,
      parentLineId: r.parentLineId ? (map.get(r.parentLineId) ?? r.parentLineId) : r.parentLineId,
      rateBaseLineIds: r.rateBaseLineIds.map((x) => map.get(x) ?? x),
      ...(r.quantitySource === "takeoff" ? { quantitySource: "manual" as const, quantity: qtyOf(r) } : {}),
    }));
    const lastId = src[src.length - 1]!.id;
    record();
    putRows((rs) => {
      const at = rs.findIndex((x) => x.id === lastId) + 1;
      return [...rs.slice(0, at), ...copies, ...rs.slice(at)];
    });
    markDirty(true);
    if (ids === axSel) setAxSel(new Set());
    toast(ESTIMATE_PC_MSG.rowsCopied.replace("{n}", String(copies.length)) + (skipped ? `。${ESTIMATE_PC_MSG.sheetCopyNoTarget}` : ""));
  };
  /** 「工種なし n行 → 入れる」「場所なし n行 → 入れる」の小さな窓（#131 の2回目）：該当の行を順に並べて、行ごとに選ぶ */
  const [fill, setFill] = useState<"trade" | "place" | null>(null);
  const applyFill = (picked: Record<string, string>) => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    setFill(null);
    if (!ids.length) return;
    const key = fill === "place" ? "placeId" : "tradeId";
    record();
    putRows((rs) => rs.map((r) => (picked[r.id] ? { ...r, [key]: picked[r.id] } : r)));
    markDirty(true);
    toast(ESTIMATE_PC_MSG.fillDone.replace("{n}", String(ids.length)).replace("{axis}", t(fill === "place" ? AXES_MSG.place : AXES_MSG.trade)));
  };

  /* ---- 内訳へ移動（#138）：明細・説明・別途の行を 表紙／ほかの内訳の末尾へ。行の「…」のサブメニューと、まとめて選んだ帯のプルダウンから。
     一式の行はその内訳の行（子の行）も一緒に動かし、子の行だけを選んだときは親の一式ごと動かす（紙の上で親と子が離れないように）。
     拾い出し・写真・社内メモは行（系譜）に付いているので一緒に動く。丸ごとの保存（版つき）で送る。並べ方（#129）・出した回・請求書・雛形では出さない ---- */
  const MOVABLE: ReadonlySet<RowRole> = new Set<RowRole>(["detail", "note", "excluded"]);
  const canMoveSheet = useSections && !axisMode && !readOnly && !alt;
  const sheetLabelOf = (id: string) => (id === coverSheet?.id ? ESTIMATE_PC_MSG.moveCover : sheets.find((x) => x.id === id)?.name || "（名前なし）");
  /** 移す先：表紙＋内訳（いま開いている紙は出さない） */
  const moveTargets = canMoveSheet ? [coverSheet, ...breakdowns].filter((x): x is Sheet => !!x && x.id !== curSheetId).map((x) => ({ id: x.id, label: sheetLabelOf(x.id) })) : [];
  const moveRows = (ids: ReadonlySet<string>, toSheetId: string) => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    // 動かす根（一式の親）を集める。子の行だけを選んだら親を根に
    const roots = new Set<string>();
    let skipped = 0;
    for (const r of rows) {
      if (!ids.has(r.id)) continue;
      if (!MOVABLE.has(r.role)) {
        skipped++;
        continue;
      }
      roots.add(r.parentLineId && byId.has(r.parentLineId) ? r.parentLineId : r.id);
    }
    const moving = new Set<string>();
    for (const r of rows) {
      const root = roots.has(r.id) ? r.id : r.parentLineId && roots.has(r.parentLineId) ? r.parentLineId : null;
      if (root && (r.sheetId ?? null) !== toSheetId) moving.add(r.id);
    }
    if (!moving.size) {
      toast(ESTIMATE_PC_MSG.moveNone);
      return;
    }
    const withChildren = [...moving].some((id) => !ids.has(id));
    const snap = rows.map((r) => ({ id: r.id, sheetId: r.sheetId ?? null }));
    setEdit(null);
    setSecEdit(null);
    record();
    putRows((rs) => {
      const go = rs.filter((r) => moving.has(r.id)).map((r) => ({ ...r, sheetId: toSheetId }));
      const rest = rs.filter((r) => !moving.has(r.id));
      let at = rest.length;
      rest.forEach((r, k) => {
        if ((r.sheetId ?? null) === toSheetId) at = k + 1; // 移す先の紙の最後の行の次
      });
      return [...rest.slice(0, at), ...go, ...rest.slice(at)];
    });
    markDirty(true);
    if (ids === axSel) setAxSel(new Set());
    const text =
      ESTIMATE_PC_MSG.moveDone.replace("{n}", String(moving.size)).replace("{name}", sheetLabelOf(toSheetId)) +
      (withChildren ? ESTIMATE_PC_MSG.moveWithChildren : "") +
      (skipped ? ESTIMATE_PC_MSG.moveSkipped.replace("{n}", String(skipped)) : "");
    toast(text, {
      label: "元に戻す",
      onClick: () => {
        // 元の紙・元の並びに戻す（そのあとで直した中身は残す）：動かした行を、元の並びで1つ前にあった行の次へ
        const was = new Map(snap.map((x) => [x.id, x.sheetId]));
        record();
        putRows((rs) => {
          const back = new Map(rs.filter((r) => moving.has(r.id)).map((r) => [r.id, { ...r, sheetId: was.get(r.id) ?? null }]));
          const out = rs.filter((r) => !moving.has(r.id));
          snap.forEach((x, k) => {
            const m = back.get(x.id);
            if (!m) return;
            let at = 0;
            for (let j = k - 1; j >= 0; j--) {
              const p = out.findIndex((y) => y.id === snap[j]!.id);
              if (p >= 0) {
                at = p + 1;
                break;
              }
            }
            out.splice(at, 0, m);
          });
          return out;
        });
        setEdit(null);
        markDirty(true);
      },
    });
  };

  /* ---- #131：表の決まり（列・社内用）・並べ替え・見積依頼・行の「…」・積算表・読み直し ---- */
  const [showInternalSaved, setShowInternal] = useShowInternal();
  /** 社内用の列（{order}は出さない＝この紙の金額が仕入れそのもの。#233） */
  const showInternal = orderDoc ? false : showInternalSaved;
  /** 規格・仕様／備考 の幅（#140。人ごと） */
  const colSizer = useEstimateColumns();
  /** T2：税区分の列（使う機能「軽減税率・税区分」が ON の会社だけ。OFF は行の詳細で選ぶ） */
  const taxCol = can("reducedTax");
  const cols = columnsFor(showInternal, colSizer.widths, taxCol);
  /**
   * 金額の列でいちばん長い額（#190：金額は省略しない。表が列をその幅まで広げる）。小計は単価〜金額を結合して出すので数えない。
   * 仕入れは行の額と合計（合計の行の仕入れは結合しない）
   */
  const money: MoneyTexts = {
    price: longestMoney(rows.filter((r) => r.role === "detail" && r.unitPrice).map((r) => yen(r.unitPrice!))),
    amount: longestMoney([
      ...rows.filter((r) => r.role !== "subtotal" && r.role !== "group" && r.role !== "note" && amountOf(r)).map((r) => yen(amountOf(r))),
      ...(onCover ? usedBreakdowns.map((x) => yen(sheetTotal(x.id))) : []),
    ]),
    cost: longestMoney([
      ...rows.filter((r) => r.role === "detail" && r.costAmount != null).map((r) => yen(r.costAmount!)),
      yen(calc.costs?.total ?? allCost.sum),
      ...breakdowns.map((x) => yen(sheetCost(x.id).sum)),
    ]),
  };
  /** #199：基礎（選んだ小計・選んだ行）が不使用の内訳（率の行と別の紙）にある率の行。DB はその基礎を 0 にする＝画面は ⚠ */
  const rateBaseUnused = new Set(
    unusedSheetIds.size
      ? rows
          .filter((r) => r.role === "overhead_rate" && (r.rateBase === "subtotal" || r.rateBase === "lines"))
          .filter((r) => r.rateBaseLineIds.some((id) => {
            const b = rows.find((x) => x.id === id);
            return !!b?.sheetId && b.sheetId !== r.sheetId && unusedSheetIds.has(b.sheetId);
          }))
          .map((r) => r.id)
      : [],
  );
  const ctx: TblCtx = {
    cols, showInternal, readOnly, alt, onCover, field, takeoff: can("takeoff"), taxCol, taxDateHint: calc.taxDateHint, money, rateBaseUnused,
    // #233：{order}の行の詳細に内訳4つ（field の会社だけ）
    laborBreakdown: !!orderDoc?.labor,
    // #305：労務費の内訳の明細の行にも札「労務費」・#306：会社の率の名前（費目を選んだときの率）
    laborSheets: new Set(sheets.filter((x) => x.isLabor).map((x) => x.id)),
    ratePresets: ratePresets.data?.items,
    // #213：パートナーの手数料（仕入れの欄の利益率は手数料後・仕入元の欄の下段に「P 14,286」）と、行の詳細の 粗利率・上乗せ（書ける見積だけ）
    referral: refFees && docRefPct != null ? { pct: docRefPct, byLine: refFees.byLine } : null,
    pricing: !readOnly && !alt ? { marginPct: markupSet.marginPct, round: markupSet.round, mode: pricingMode, setMode: setPricingMode } : undefined,
  };
  const vendorsById = new Map(axLists.vendors.map((v) => [v.id, v]));
  /** 見積依頼に入れた行（外注先を決めて、仕入れはまだ・外注見積から入れていない）＝出どころに「依頼中 ○○」 */
  /** 系譜 → 送付済・回答ありの依頼（#218：1行に何通でも。未送付の印は外注先から画面が作る） */
  const rfqMarks = useMemo(() => {
    const m = new Map<string, RfqLineMark[]>();
    for (const x of rfqs.data?.marks ?? []) if (x.state !== "unsent") m.set(x.lineageId, [...(m.get(x.lineageId) ?? []), x]);
    return m;
  }, [rfqs.data]);
  /** {vendor}の id → 略称（しるしの「依頼済 A社・B社」） */
  const rfqVendorName = (vid: string) => vendorsById.get(vid)?.name ?? rfqs.data?.vendors.find((v) => v.vendor.id === vid)?.vendor.shortName ?? "";
  /** 行 → 系譜（サーバーの答えの lines[].lineageId。直している回は lineageRef も） */
  const lineageById = useMemo(() => new Map(initial.lines.map((l) => [l.id, l.lineageId])), [initial.lines]);
  /**
   * 書きかけを保存し終えるまで待つ（送っている最中ならその終わりも待つ）。保存できたら true。
   * 見積依頼書の下書きの項目は保存した行から作るので、行に外注先を付けた直後に開くときは必ずこれを通す
   */
  const settleSave = async (): Promise<boolean> => (await sq.settle()) && !staleRef.current;
  /** 見積依頼書を開く（#119）：その外注先の依頼（下書き → 最新）があれば開き、無ければ下書きを作る。先に保存してから（下書きの項目は保存した行から作る） */
  const openRfq = async (vendorId: string) => {
    try {
      if (!(await settleSave())) {
        if (!staleRef.current) toast(RFQ_MSG.saveFailed, undefined, "error");
        return;
      }
      const cur = rfqs.data?.vendors.find((v) => v.vendor.id === vendorId)?.current;
      if (cur) {
        nav(`/rfqs/${cur.id}`);
        return;
      }
      const r = await api<{ id: string }>("POST", `/estimates/${initial.id}/rfqs`, { vendorId });
      markNewRfq(r.id); // 新しく作った下書き＝「次回も同じ設定」を当てる（#220）
      nav(`/rfqs/${r.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "RFQ_EXISTS" && typeof e.problem.rfqId === "string") nav(`/rfqs/${e.problem.rfqId}`);
      else toast(messageOf(e), undefined, "error");
    }
  };
  /** 窓で外注先と行を選んだ（10/7 射場さん）：選んだ行に外注先を付けて（＝行の「見積依頼に入れる…」と同じ）、保存してから見積依頼書を開く */
  const assignRfq = (vendorId: string, lineIds: string[]) => {
    setRfqPick(false);
    const ids = new Set(lineIds);
    record();
    putRows((rs) => rs.map((r) => (ids.has(r.id) ? { ...r, ...withVendor({ vendorPartnerId: vendorId }) } : r)));
    markDirty(true);
    void openRfq(vendorId);
  };
  // #302（B3）：請求書も仕入元の名前を出す（見積依頼は見積書だけ＝請求書はしるしでなく名前だけ）
  const requestedOf = (r: Row) => (!template && (r.role === "detail" || r.role === "note") && r.vendorPartnerId && r.costAmount == null && !costRefs.get(r.id) ? vendorsById.get(r.vendorPartnerId) : undefined);
  /** その行から作った{order}（#233 2a。系譜で引く） */
  const orderMarks = useMemo(() => new Map((orderMarksQ.data?.marks ?? []).map((m) => [m.lineageId, m.orders])), [orderMarksQ.data]);
  const orderMarksOf = (r: Row): OrderRef[] => {
    if (!orderOn || (r.role !== "detail" && r.role !== "note")) return [];
    const lg = lineageById.get(r.id) ?? lineageRef.current.lines[r.id];
    return lg ? (orderMarks.get(lg) ?? []) : [];
  };
  /** その行の依頼（送付済・回答あり。#218） */
  const rfqMarksOf = (r: Row): RfqLineMark[] => {
    if (alt || !can("rfq") || (r.role !== "detail" && r.role !== "note")) return [];
    const lg = lineageById.get(r.id) ?? lineageRef.current.lines[r.id];
    return lg ? (rfqMarks.get(lg) ?? []) : [];
  };
  const requested: Requested[] = [];
  for (const r of rows) {
    const v = requestedOf(r);
    if (!v) continue;
    let x = requested.find((q) => q.vendorId === v.id);
    if (!x) requested.push((x = { vendorId: v.id, name: v.name, full: v.full, lines: [] }));
    x.lines.push(r.name || t("（{lineName}なし）"));
  }
  const readyQuotes = (vprog.data?.quotes ?? []).filter((q: VendorQuoteProgress) => q.status === "review" || q.status === "done" || q.status === "manual").length;
  /** 見積依頼に追加（#218、docs/88 §4.5）。行の外注先が空なら入れてから保存し、その行を依頼の明細に写す */
  const [reqDlg, setReqDlg] = useState<string | null>(null);
  const addToRfq = async (lineId: string, vendorId: string, rfqId: string | null) => {
    setReqDlg(null);
    const i = rows.findIndex((r) => r.id === lineId);
    if (i < 0) return;
    const r = rows[i]!;
    // 外注先を入れた保存の答えでは見積依頼を読み直さない（写したあとで1回だけ。前は2回読んでいた）
    const hold = rfqFollowHold.current;
    hold.on = true;
    hold.held = false;
    let added = false;
    if (!readOnly && !r.vendorPartnerId) {
      upd(i, { vendorPartnerId: vendorId });
      markDirty(true);
    }
    try {
      if (!(await settleSave())) {
        if (!staleRef.current) toast(RFQ_MSG.saveFailed, undefined, "error");
        return;
      }
      const lg = lineageById.get(lineId) ?? lineageRef.current.lines[lineId];
      if (!lg) return;
      const v = await api<{ id: string }>("POST", `/estimates/${initial.id}/rfq-lines`, { vendorId, lineageIds: [lg], ...(rfqId ? { rfqId } : {}) });
      added = true;
      if (!rfqId) markNewRfq(v.id); // 新しく作った下書き＝「次回も同じ設定」を当てる（#220）
      void qc.invalidateQueries({ queryKey: ["rfqs"] });
      toast(RFQ_MSG.addLineDone, { label: RFQ_MSG.rowOpen, onClick: () => nav(`/rfqs/${v.id}`) });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      hold.on = false;
      if (added || hold.held) void qc.invalidateQueries({ queryKey: ["estimate-rfqs", initial.id] });
    }
  };
  /**
   * 見積依頼解除（#270、docs/88 §4.5）：行の外注先を外して保存し、その{vendor}の下書きの見積依頼からもその行を外す（空になった下書きは API が削除）。
   * 送った依頼のある行には出さない（取消は見積依頼の画面）。確認の窓は出さない（「見積依頼に追加…」でいつでも戻せる・↶ で外注先は戻る）
   */
  const removeFromRfq = async (lineId: string) => {
    const i = rows.findIndex((r) => r.id === lineId);
    if (i < 0) return;
    const r = rows[i]!;
    const vendorId = r.vendorPartnerId;
    if (!vendorId) return;
    // 外注先を外した保存の答えでは見積依頼を読み直さない（外したあとで1回だけ。addToRfq と同じ）
    const hold = rfqFollowHold.current;
    hold.on = true;
    hold.held = false;
    let removed = false;
    upd(i, { vendorPartnerId: null });
    markDirty(true);
    try {
      if (!(await settleSave())) {
        if (!staleRef.current) toast(RFQ_MSG.saveFailed, undefined, "error");
        return;
      }
      const lg = lineageById.get(lineId) ?? lineageRef.current.lines[lineId];
      if (lg) await api<RfqLinesRemoved>("DELETE", `/estimates/${initial.id}/rfq-lines`, { vendorId, lineageIds: [lg] });
      removed = true;
      void qc.invalidateQueries({ queryKey: ["rfqs"] });
      toast(ESTIMATE_PC_MSG.requestCleared.replace("{name}", r.name || t("（{lineName}なし）")));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      hold.on = false;
      if (removed || hold.held) void qc.invalidateQueries({ queryKey: ["estimate-rfqs", initial.id] });
    }
  };
  /** {order}に追加（#233 2a、docs/93 §14.3）：保存してから、その行を{vendor}の{order}の下書き（無ければ新しい{order}）に写す。単価は行の仕入れ */
  const [ordDlg, setOrdDlg] = useState<string | null>(null);
  const addToOrder = async (lineId: string, vendorId: string, orderId: string | null) => {
    setOrdDlg(null);
    try {
      if (!(await settleSave())) {
        if (!staleRef.current) toast(RFQ_MSG.saveFailed, undefined, "error");
        return;
      }
      const lg = lineageById.get(lineId) ?? lineageRef.current.lines[lineId];
      if (!lg) return;
      const o = await api<{ id: string }>("POST", `/estimates/${initial.id}/order-lines`, { vendorId, lineageIds: [lg], ...(orderId ? { orderId } : {}) });
      void qc.invalidateQueries({ queryKey: ["estimate-order-marks", initial.id] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      toast(t(ORDER_BUNDLE_MSG.added), { label: ORDER_BUNDLE_MSG.open, onClick: () => nav(`/orders/${o.id}`) }, "success", "order-added-notice");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  /** 「自社にする」：仕入れ 0 として扱い「仕入れ未入力」に数えない（#131 の2回目） */
  const setOwn = (i: number) => {
    const r = rows[i];
    if (!r) return;
    upd(i, { costOwn: true, costAmount: null });
    markDirty(true);
    toast(ESTIMATE_PC_MSG.ownDone.replace("{name}", r.name || t("（{lineName}なし）")));
  };
  /** 「仕入れ手入力」：書ける見積はその行の仕入れの欄へ、出した見積は社内用の列の欄へ */
  const focusCost = (i: number) => {
    if (!readOnly) {
      editRow(i, "f_cost");
      return;
    }
    const el = document.getElementById(`cost-${rows[i]!.id}`) as HTMLInputElement | null;
    el?.focus();
    el?.select();
  };
  const svgI = (d: string) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
  /** 行の「…」（D2）：外注見積から取込…／仕入れを手入力／見積依頼に入れる…／写真を見る／（確定にする）／外注見積とのつながりを外す */
  const menuOf = (r: Row, i: number): RowMenuItem[] => {
    if (template || !(r.role === "detail" || r.role === "excluded" || r.role === "note" || r.role === "group")) return [];
    // #302（B3）：請求書は 仕入れを手入力・自社・（出した回は）仕入元変更 だけ（取込・見積依頼・{order}・写真は見積書だけ）
    if (invoice) {
      if (r.role === "group") return [];
      const ref = costRefs.get(r.id);
      const items: RowMenuItem[] = [];
      if (r.role === "detail") items.push({ label: ESTIMATE_PC_MSG.menuCostManual, icon: svgI("M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"), onSelect: () => focusCost(i), disabled: !!ref, testId: "menu-cost" });
      if (r.role === "detail") items.push({ label: ESTIMATE_PC_MSG.menuCostOwn, icon: svgI("M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"), onSelect: () => setOwn(i), disabled: readOnly || !!ref || !!r.vendorPartnerId || r.costOwn, testId: "menu-own" });
      // 請求書は行の詳細に仕入元の欄が無い（区分の段は見積書だけ）＝下書きでも出した回でも「…」の「仕入元変更」で直す
      items.push(...vendorItemsOf(r));
      return items;
    }
    if (r.role === "group" && (!field || !calc.att[r.id])) return []; // 見出しの行は写真があるときだけ（写真は field の会社だけ）
    const ref = costRefs.get(r.id);
    const att = calc.att[r.id];
    const items: RowMenuItem[] = [];
    const vq = can("vendorQuote");
    if (vq && (r.role === "detail" || r.role === "excluded")) items.push({ label: t(ESTIMATE_PC_MSG.menuImport), ...(readyQuotes ? { hint: ESTIMATE_PC_MSG.menuImportCount.replace("{n}", String(readyQuotes)) } : {}), icon: svgI("M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5M10 17l5-5-5-5M15 12H3"), onSelect: () => openImport(r.id), testId: "menu-import" });
    if (r.role === "detail") items.push({ label: ESTIMATE_PC_MSG.menuCostManual, icon: svgI("M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"), onSelect: () => focusCost(i), disabled: !!ref, testId: "menu-cost" });
    // 仕入れを「自社」に（#131 の2回目）。外注見積から入れた行・外注先の付いた行・出した見積は押せない（外注先を外すか、仕入れを打つ）
    if (r.role === "detail") items.push({ label: ESTIMATE_PC_MSG.menuCostOwn, icon: svgI("M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"), onSelect: () => setOwn(i), disabled: readOnly || !!ref || !!r.vendorPartnerId || r.costOwn, testId: "menu-own" });
    // 見積依頼に入れる…：使う機能 rfq の会社だけ（#149。外注見積の取込とは別）
    // #218（docs/88 §4.5）：「見積依頼に追加…」＝{vendor}と依頼を選んでその行を写す。同じ行を何社にでも・出した見積の行も（行の外注先は書ける見積だけ入れる）
    if (can("rfq") && (r.role === "detail" || r.role === "note")) items.push({ label: RFQ_MSG.menuAddLine, icon: svgI("M22 2L11 13M22 2l-7 20-4-9-9-4z"), onSelect: () => setReqDlg(r.id), testId: "menu-request" });
    // #270：「見積依頼解除」＝外注先が付いて、まだ送っていない行だけ（送った依頼のある行は見積依頼の画面で取消。{vendorQuote}から入れた行は「連携解除」）
    if (can("rfq") && !readOnly && (r.role === "detail" || r.role === "note") && r.vendorPartnerId && !ref && !rfqMarksOf(r).length)
      items.push({ label: RFQ_MSG.menuRemoveLine, icon: svgI("M22 2L11 13M22 2l-7 20-4-9-9-4zM4 4l16 16"), onSelect: () => void removeFromRfq(r.id), testId: "menu-request-remove" });
    // #233 2a：「{order}に追加…」＝{vendor}と追加先の下書きを選んでその行を写す（モジュール sales・PC）
    if (orderOn && !pcOnly.hidden("/orders/new") && (r.role === "detail" || r.role === "note"))
      items.push({ label: t(ORDER_BUNDLE_MSG.addToOrder), icon: svgI("M4 4h12l4 4v12H4zM8 12h8M8 16h5"), onSelect: () => setOrdDlg(r.id), testId: "menu-order" });
    // 写真を見る（写真は field の会社だけ。#142 段2）
    if (field)
      items.push({
        label: ESTIMATE_PC_MSG.menuPhotos,
        ...(att?.photos ? { hint: ESTIMATE_PC_MSG.menuPhotosCount.replace("{n}", String(att.photos)) } : {}),
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
        ),
        onSelect: () => setAttOpen(r.id),
        disabled: !att,
        testId: "menu-photos",
      });
    if (r.role === "detail" && r.lineState === "provisional" && !readOnly) items.push({ label: ESTIMATE_PC_MSG.menuFinalize, icon: svgI("M20 6L9 17l-5-5"), onSelect: () => finalizeRows(new Set([r.id])), testId: "menu-finalize" });
    // 一式の行の下に内訳の行（#130。前は行の詳細の2行目にあった）
    if (r.role === "detail" && !readOnly && isLumpsumUnit(r.unit) && !r.parentLineId) items.push({ label: TAKEOFF_MSG.addChild, icon: svgI("M12 5v14M5 12h14"), onSelect: () => addChildLine(i), testId: "add-child" });
    // 内訳へ移動（#138）：表紙＋内訳の名前のサブメニュー（いまの紙は出さない）。内訳の無い見積・並べ方・出した回では出さない
    if (MOVABLE.has(r.role) && moveTargets.length)
      items.push({
        label: ESTIMATE_PC_MSG.menuMoveSheet,
        icon: svgI("M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM10 13h6M13 10l3 3-3 3"),
        sub: moveTargets.map((x) => ({ label: x.label, onSelect: () => moveRows(new Set([r.id]), x.id) })),
        testId: "menu-move-sheet",
      });
    if (vq && (r.role === "detail" || r.role === "excluded"))
      items.push({ label: t(ESTIMATE_PC_MSG.menuUnlink), icon: svgI("M18.8 12.4l1.6-1.6a4 4 0 0 0-5.7-5.7l-1.6 1.6M5.2 11.6l-1.6 1.6a4 4 0 0 0 5.7 5.7l1.6-1.6M8 16l1.5-1.5M14.5 9.5L16 8M2 2l20 20"), onSelect: () => void doUnlink(r.id), disabled: !ref, divider: true, testId: "menu-unlink" });
    // #302（B3）：出した回でも仕入元を直せる（社内用の列。書ける回は行の詳細の仕入元の欄）
    if (readOnly && r.role !== "group") items.push(...vendorItemsOf(r));
    return items;
  };
  /** 出した回の行の仕入元（#302 B3）：変更（取引先検索のドロアー）・解除。外注見積から入れた行は出どころのしるしが仕入元なので出さない */
  const vendorItemsOf = (r: Row): RowMenuItem[] =>
    costRefs.get(r.id) || !(r.role === "detail" || r.role === "note" || r.role === "excluded")
      ? []
      : [
          { label: ESTIMATE_PC_MSG.menuVendorChange, icon: svgI("M3 18h18M5 18v-3a7 7 0 0 1 14 0v3M10 8V5h4v3"), onSelect: () => setVendorPickLine(r.id), divider: true, testId: "menu-vendor" },
          ...(r.vendorPartnerId ? [{ label: ESTIMATE_PC_MSG.menuVendorClear, onSelect: () => setVendorOf(r.id, null), testId: "menu-vendor-clear" }] : []),
        ];
  const srcOf = (r: Row, i: number): SourceInfo => {
    const ref = costRefs.get(r.id) ?? undefined;
    const rq = requestedOf(r);
    const mks = rfqMarksOf(r);
    return {
      costRef: ref,
      // 見積依頼が OFF（#149）の会社は しるし（未送付・依頼済・回答あり）を出さず、外注先の名前だけ。
      // #218：依頼があれば何社でも「依頼済 A社・B社」（外注先が無くても・仕入れが手入力でも）、無ければ外注先だけの「未送付 A社」
      requested: !ref && mks.length ? { short: rq?.name ?? "", full: rq?.full ?? "", marks: mks, names: rfqVendorName } : rq ? { short: rq.name, full: rq.full, plain: !can("rfq") || !!invoice } : undefined,
      // #233 2a：その行から作った{order}（下段に「発注済 PO-0001」）
      ordered: orderMarksOf(r),
      // #302（B3）：請求書も「自社」（前は請求書で出さず、見積から写した自社の行が「0・手入力」に見えた）
      own: !template && r.role === "detail" && r.costOwn && !ref && !r.vendorPartnerId,
      // 写真の数は field の会社だけ（社内メモはどの会社も）
      att: alt || !calc.att[r.id] ? undefined : { ...calc.att[r.id]!, ...(field ? {} : { photos: 0 }), onOpen: () => setAttOpen(r.id) },
      onImport: !alt && can("vendorQuote") && (r.role === "detail" || r.role === "excluded") ? () => openImport(r.id) : undefined,
      // 外注先のしるしを押す：取込の1枚（その外注見積）だけ（#283、docs/34 D3 の改め：原本は1枚の「原本」から。前は原本の引き出しも一緒に開いて1枚を覆った）
      onOpenSource: ref ? () => openImport(r.id, ref.vendorQuoteId) : undefined,
      menu: menuOf(r, i),
    };
  };
  /** 並べ替え（手で並べるとき）：つまみを押してからドラッグ。並べ方のときはつかもうとすると知らせる。↑↓は行の詳細に（ドラッグだけにしない） */
  const [armed, setArmed] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; where: "before" | "after" } | null>(null);
  useEffect(() => {
    const up = () => setArmed(null);
    document.addEventListener("mouseup", up);
    return () => document.removeEventListener("mouseup", up);
  }, []);
  const moveRowTo = (fromId: string, toId: string, where: "before" | "after") => {
    if (fromId === toId) return;
    record();
    putRows((rs) => {
      const a = rs.slice();
      const k = a.findIndex((x) => x.id === fromId);
      if (k < 0) return rs;
      const [m] = a.splice(k, 1);
      let t = a.findIndex((x) => x.id === toId);
      if (t < 0) return rs;
      if (where === "after") t++;
      a.splice(t, 0, m!);
      return a;
    });
    setEdit(null);
    markDirty(true);
  };
  const whereOf = (e: React.DragEvent): "before" | "after" => {
    const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY < b.top + b.height / 2 ? "before" : "after";
  };
  /** つまみを押した（ドラッグしない）ときの「上へ」「下へ」（ドラッグだけにしない。#131 の2回目で行の詳細の ↑↓ をここへ） */
  const movesOf = (i: number): RowMenuItem[] => {
    const pos = visible.indexOf(i);
    const up = pos > 0 ? rows[visible[pos - 1]!] : undefined;
    const down = pos >= 0 && pos < visible.length - 1 ? rows[visible[pos + 1]!] : undefined;
    const id = rows[i]!.id;
    return [
      // #302（C1）：1段目に「上に追加」「下に追加」、区切りのあとに「上へ」「下へ」
      { label: ESTIMATE_PC_MSG.addAbove, icon: svgI("M12 5v14M5 12h14"), onSelect: () => insertLine(i, "above"), testId: "add-above" },
      { label: ESTIMATE_PC_MSG.addBelow, icon: svgI("M12 5v14M5 12h14"), onSelect: () => insertLine(i, "below"), testId: "add-below" },
      { label: ESTIMATE_PC_MSG.moveUp, icon: svgI("M12 19V5M5 12l7-7 7 7"), disabled: !up, onSelect: () => { if (up) { closeEdit(); moveRowTo(id, up.id, "before"); } }, divider: true, testId: "move-up" },
      { label: ESTIMATE_PC_MSG.moveDown, icon: svgI("M12 5v14M19 12l-7 7-7-7"), disabled: !down, onSelect: () => { if (down) { closeEdit(); moveRowTo(id, down.id, "after"); } }, testId: "move-down" },
    ];
  };
  const dragOf = (r: Row, i: number): DragProps | undefined =>
    readOnly
      ? undefined
      : {
          enabled: !axisMode,
          locked: axisMode,
          armed: armed === r.id,
          drop: dropAt?.id === r.id ? dropAt.where : null,
          moves: axisMode ? [] : movesOf(i),
          onArm: () => {
            if (edit != null) closeEdit();
            setArmed(r.id);
          },
          onLocked: () => toast(ESTIMATE_PC_MSG.sortLocked),
          onStart: (e) => {
            dragId.current = r.id;
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", r.id);
          },
          onOver: (e) => {
            if (!dragId.current) return;
            e.preventDefault();
            const where = whereOf(e);
            if (dropAt?.id !== r.id || dropAt.where !== where) setDropAt({ id: r.id, where });
          },
          onDrop: (e) => {
            e.preventDefault();
            const from = dragId.current;
            const where = whereOf(e);
            dragId.current = null;
            setDropAt(null);
            setArmed(null);
            if (from) moveRowTo(from, r.id, where);
          },
          onEnd: () => {
            dragId.current = null;
            setDropAt(null);
            setArmed(null);
          },
        };
  /** 仮の行をすべて確定にする（表の上の「仮の行 n本 → すべて確定」） */
  const finalizeAll = () => {
    const ids = new Set(rows.filter((r) => r.lineState === "provisional" && r.role === "detail").map((r) => r.id));
    record();
    putRows((rs) => rs.map((r) => (ids.has(r.id) ? { ...r, lineState: "final" } : r)));
    markDirty(true);
    toast(ESTIMATE_PC_MSG.finalizedAll.replace("{n}", String(ids.size)));
  };
  /** 積算表を付ける（別紙）：見積ごとの選択（#131）。既定は会社の設定 */
  const [tkSheet, setTkSheet] = useState(initial.takeoffSheet ?? { on: false, source: "company" as const });
  const toggleTakeoffSheet = async () => {
    try {
      // #202：版を付けて送り、答えの版を覚える。書きかけの行を送り終えてから、行の保存と重ねずに（exclusive）
      await settle();
      const e = await sq.exclusive(() => api<Estimate & { message: string }>("PUT", `/estimates/${initial.id}/takeoff-sheet`, { on: !tkSheet.on, version: versionRef.current }));
      versionRef.current = e.updatedAt;
      if (e.takeoffSheet) setTkSheet(e.takeoffSheet);
      qc.setQueryData(["estimate", initial.id], e);
      toast(e.message);
    } catch (err) {
      if (isStale(err)) enterStale();
      else toast(messageOf(err), undefined, "error");
    }
  };
  /** 読み直す（409 のあと）：ほかで変わった最新に、この画面で直した分を重ねて、もう一度保存する */
  const reloadStale = async () => {
    setStale({ busy: true });
    try {
      // 請求書は請求書の最新（2026-10-13：請求書の保存にも版を入れた）
      const theirs = invoice?.reload ? await invoice.reload() : await api<Estimate>("GET", `/estimates/${initial.id}`);
      const docKey = docQ;
      const editingId = edit != null ? (rows[edit]?.id ?? null) : null;
      if (theirs.revision.id !== initial.revision.id || (theirs.status !== "draft" && !theirs.revising)) {
        // 出された・回が変わった：画面を作り直す（この画面で直した分は残せない）
        staleRef.current = false;
        sq.discard();
        setStale(null);
        if (invoice) invoice.synced?.(theirs);
        else versionRef.current = theirs.updatedAt;
        qc.setQueryData([docKey, initial.id], theirs);
        setReloadTick((x) => x + 1);
        toast(ESTIMATE_PC_MSG.staleReloaded);
        return;
      }
      const base = baseRef.current;
      const theirsHead: Head = { ...latest.current.head, ...fromServerHead(theirs) };
      const theirsSnap = { head: theirsHead, rows: theirs.lines.map(fromServerLine), sheets: theirs.sheets.map(sheetOf) };
      const mine = { head: latest.current.head, rows: latest.current.rows, sheets: latest.current.sheets };
      const merged = rebaseEdits(base, mine, theirsSnap);
      rememberLineage(theirs);
      // 版：見積はこの画面、請求書は請求書の画面（紙の上の欄も最新の上に重ねる）
      if (invoice) invoice.synced?.(theirs);
      else versionRef.current = theirs.updatedAt;
      baseRef.current = theirsSnap;
      setHead(merged.head);
      putRows(merged.rows);
      putSheets(merged.sheets);
      latest.current = merged;
      cutHistory(); // 読み直した（ほかで変わった最新に重ねた）＝ここより前には戻さない
      setCalc(fromServerCalc(theirs));
      qc.setQueryData([docKey, initial.id], theirs);
      refetchFollows(theirs); // ほかで外注先・外注見積の行が変わっていれば読み直す（性能の改善 A）
      if (!invoice) void qc.invalidateQueries({ queryKey: ["takeoffs", initial.id] }); // 拾い出しもほかで変わっているかもしれない
      setReloadTick((x) => x + 1);
      if (editingId) {
        const k = merged.rows.findIndex((x) => x.id === editingId);
        setEdit(k >= 0 ? k : null);
      }
      staleRef.current = false;
      setStale(null);
      const changed = JSON.stringify(merged) !== JSON.stringify(theirsSnap);
      if (changed) {
        markDirty(true);
        toast(ESTIMATE_PC_MSG.staleMerged);
      } else {
        sq.discard();
        setSave({ kind: "saved", at: hhmm() });
        toast(ESTIMATE_PC_MSG.staleReloaded);
      }
    } catch (err) {
      setStale({ busy: false });
      toast(messageOf(err), undefined, "error");
    }
  };

  /*
   * ---- 自動ログアウト（#189、docs/76 ④）：見積書の書きかけを守る（請求書・雛形は対象外） ----
   *   帯（期限の5分前）を出すとき＝書きかけを送る（flush）。切れたとき＝送れていない直し（送る前・送っている最中・断られた）を
   *   端末に退避（localStorage、キーは人と見積の番号）。ログインし直して同じ見積を開いたら、同じ人のときだけ最新に重ねて（rebaseEdits）
   *   自動で保存し、右下に「保存されていない入力を復元しました。」。退避は使ったら消す（別の人がログインしたら AutoLogoutHost が消す）
   */
  const protectInput = !template && !invoice && !readOnly;
  const unsentRef = useRef(sq.unsent);
  unsentRef.current = sq.unsent;
  const accountId = me.data?.session.accountId ?? null;
  useEffect(() => {
    if (!protectInput || !accountId) return;
    return registerUnsaved({
      flush: () => sq.flush(),
      stash: () => {
        if (!sq.isDirty() && !sq.isInflight() && !unsentRef.current) return;
        const stash: EstimateStash<Head, Row, Sheet> = {
          v: 1,
          revisionId: initial.revision.id,
          base: baseRef.current,
          mine: { head: latest.current.head, rows: latest.current.rows, sheets: latest.current.sheets },
        };
        writeStash(accountId, initial.id, stash);
      },
    });
    // sq の関数・ref は画面のあいだ同じ
  }, [protectInput, accountId, initial.id, initial.revision.id]);
  const restoredRef = useRef(false);
  useEffect(() => {
    if (template || invoice || !accountId || restoredRef.current) return;
    restoredRef.current = true;
    const st = readStash<EstimateStash<Head, Row, Sheet>>(accountId, initial.id);
    if (!st) return;
    removeStash(accountId, initial.id);
    // 出した・回が変わった見積には重ねない（直せない）
    if (readOnly || st.v !== 1 || st.revisionId !== initial.revision.id) return;
    const now = { head: latest.current.head, rows: latest.current.rows, sheets: latest.current.sheets };
    const merged = rebaseEdits(st.base, st.mine, now);
    if (JSON.stringify(merged) === JSON.stringify(now)) return;
    setHead(merged.head);
    putRows(merged.rows);
    putSheets(merged.sheets);
    latest.current = merged;
    cutHistory();
    markDirty(true);
    toast(AUTO_LOGOUT_MSG.restored);
    // 開いたときに1回だけ（me の取り直しで繰り返さない）
  }, [accountId]);

  /* ---- 元に戻す／やり直す（#140）の本体 ---- */
  /** 行の削除の窓（#140。下の askDelLine） */
  const [rowDel, setRowDel] = useState<{ id: string; name: string } | null>(null);
  /** 控えの形に戻す。開いている行は同じ行のまま（無くなったら閉じる）・いまの内訳が無くなったら表紙へ。丸ごとの保存（版つき）で送る */
  const restoreSnap = (to: Snap) => {
    const editingId = edit != null ? (latest.current.rows[edit]?.id ?? null) : null;
    putRows(to.rows);
    putSheets(to.sheets);
    // #213：パートナーと率も控えの形に（行の単価と一緒に戻す）
    if (to.ref.id !== latest.current.head.referrerId || to.ref.pct !== latest.current.head.referrerPct) {
      const ref = { referrerId: to.ref.id, referrerPct: to.ref.pct };
      setHead((h) => ({ ...h, ...ref }));
      latest.current = { ...latest.current, head: { ...latest.current.head, ...ref } };
    }
    marks.current = to.marks;
    origSheets.current = to.orig;
    const k = editingId ? to.rows.findIndex((x) => x.id === editingId) : -1;
    setEdit(k >= 0 ? k : null);
    if (secEdit && !to.sheets.some((x) => x.id === secEdit)) setSecEdit(null);
    if (cur !== "cover" && !to.sheets.some((x) => x.id === cur)) setCur("cover");
    const ids = new Set(to.rows.map((x) => x.id));
    setAxSel((x) => (x.size && [...x].some((id) => !ids.has(id)) ? new Set([...x].filter((id) => ids.has(id))) : x));
    markDirty(true);
  };
  const undo = () => {
    if (readOnly) return;
    const to = hist.current!.undo(snapNow());
    if (to) restoreSnap(to);
    setHistTick((t) => t + 1);
  };
  const redo = () => {
    if (readOnly) return;
    const to = hist.current!.redo(snapNow());
    if (to) restoreSnap(to);
    setHistTick((t) => t + 1);
  };
  const undoRef = useRef(undo);
  undoRef.current = undo;
  const redoRef = useRef(redo);
  redoRef.current = redo;
  /** 窓・引き出しが開いているあいだは ⌘Z を見ない（その窓の中の操作を優先） */
  const undoBlocked = useRef(false);
  undoBlocked.current = !!(vimp || tkOpen || attOpen || rowDel || nameDlg || delDlg || fill || reqDlg || stale || pick.open);
  useEffect(() => {
    if (readOnly) return;
    // ⌘Z／⌘⇧Z（Windows は Ctrl）。文字を打つ欄にいるときはブラウザの文字の取り消しを優先する（ここでは何もしない）
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing || e.altKey || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z") return;
      if (isTextField(document.activeElement) || undoBlocked.current || document.querySelector(".dim")) return;
      e.preventDefault();
      if (e.shiftKey) redoRef.current();
      else undoRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [readOnly]);
  const canUndo = !readOnly && hist.current!.canUndo;
  const canRedo = !readOnly && hist.current!.canRedo;

  /* ---- 行の削除（#140）：何か入っている行だけ窓で確かめる。空の行は窓なしで消す（消した行を薄く残す「戻す」はそのまま） ---- */
  /** 名称・規格・数量・単価・備考・仕入れ（率の行の率・値引きの金額も）のどれかが入っている。小計の既定の名前「小計」は数えない */
  const hasContent = (r: Row) =>
    !!((r.role === "subtotal" ? r.name.trim() !== "小計" : r.name.trim()) || r.spec.trim() || r.note.trim() || r.quantity || r.unitPrice || r.costAmount != null || r.rate || r.amount);
  const askDelLine = (i: number) => {
    const r = rows[i];
    if (!r) return;
    if (!hasContent(r)) {
      delLine(i);
      return;
    }
    setRowDel({ id: r.id, name: r.name.trim() });
  };
  const closeRowDel = (done: boolean) => {
    const target = rowDel;
    setRowDel(null);
    if (done && target) {
      const k = latest.current.rows.findIndex((x) => x.id === target.id);
      if (k >= 0) delLine(k);
      return;
    }
    // 取消：行の詳細の「削除」に戻る
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-testid="delete-line"]')?.focus());
  };

  /* ---------------------------------------------------------------- 描画 */
  let no = 0;
  /* 題の右の「？」（#92）：帯の使い方・仕組みの説明と「自動で保存」をここに1つにまとめる（帯には事実だけ残す） */
  const rev = initial.revision.no;
  const unissuedNo = unissue.done ?? initial.documentNo;
  const pageHelp = helpText([
    !readOnly ? (template ? TEMPLATE_MSG.autoSaved : "下書きは自動で保存されます。") : null,
    ...(template
      ? [t(TEMPLATE_MSG.editHelp)]
      : invoice
        ? (invoice.help ?? [])
        : fresh && rows.length === 0
          ? [`${L.subject}を書き入れ、「行追加」で明細を書きます。行はその場で書き直せます（行を押すだけ）。`]
          : readOnly
            ? [
                issueDlg.justIssued && pcOnly.phone ? `右上の「PDF 表示」で${L.customer}に渡す見積書になります。` : null,
                initial.canRevise && initial.canUnissue
                  ? `修正するときは ${rev + 1}回目（${initial.documentNo}-${rev + 1}）として発行します（前の回は残ります）。`
                  : initial.canUnissue && rev >= 2
                    ? `修正するときは、この ${rev}回目だけを取消して下書きに戻します（${rev - 1}回目が発行済みの見積に戻ります）。`
                    : initial.canUnissue
                      ? `修正するときは「取消」で下書きに戻します（番号 ${initial.documentNo} はそのままです）。`
                      : initial.canRevise
                        ? `修正するときは ${rev + 1}回目（${initial.documentNo}-${rev + 1}）として再発行します（発行した見積書は残ります）。`
                        : null,
                initial.order?.status === "ordered" && initial.order.plan?.next?.draftInvoiceId ? "下書きの請求書を発行すると、次の回を作成できます。" : null,
                initial.order?.status === "lost" ? "一覧の「失注」から元に戻せます。" : null,
                // 前は題の下の帯の中（#171）
                initial.history.length && !initial.historyEnabled ? "前回の控えの PDF は題の右の「⋯」から開けます。回どうしをくらべるのは、スモール・ビジネスのプランで使えます（アカウント → 料金プラン）。" : null,
              ]
            : initial.revising
              ? [`${initial.revision.no}回目（${initial.displayNo}）を作成中です。発行済みの回は ${initial.revising.baseRevisionNo}回目（${md(initial.revising.baseIssueDate)}・${yen(initial.revising.baseTotal)} 円）です。`]
              : unissuedNo
                ? // #273：見出しのチップ（番号ありの下書き）の title と同じ1行
                  [`${unissuedTitle(unissuedNo)}。`, `「見積書発行」を押すと、同じ番号 ${unissuedNo} で発行します。`]
                : []),
    underCount && !readOnly ? "原価割れの行（朱色）は、このまま発行することもできます。修正するときは行を押して、単価か仕入れを変更してください。" : null,
  ]);
  const pageTitle = template ? TEMPLATE_MSG.editTitle : invoice ? (orderDoc?.title ?? "請求書") : "見積書";
  /* 紙の下の「計算方法」の「？」（#92）：丸め・インボイス制度・別途・小計・値引き・社内用の列 */
  const calcHelp = helpText([
    // #108：端数処理（2026-10-01 射場さん：簡潔に・業務の言葉で）
    `端数処理 明細・率の行：${roundingCodeLabel(calc.lineRoundingMode)}。`,
    `消費税：税率ごとの合計に1回だけ${roundingCodeLabel(calc.roundingMode)}（インボイス制度）。`,
    `マイナス金額：${NEGATIVE_ROUNDING_LABEL[negativeRoundingOf(calc.lineRoundingMode)]}（${NEGATIVE_ROUNDING_EXAMPLE[negativeRoundingOf(calc.lineRoundingMode)]}）。`,
    !alt ? `作成時の端数処理で計算します。変更は 設定 → 端数処理（管理者）。` : null,
    "別途の行は合計に入りません。",
    "小計の行は「前の小計の次から」を合計します。",
    useSections ? "表紙には各内訳の合計が先頭に並び、その下に表紙の行（調整費・別途・率・小計・値引き）を書けます。表紙の小計は「内訳の合計＋そこまでの表紙の行」です。内訳の合計の金額は、内訳書で修正します。" : null,
    "値引きの行で「税抜合計を ○円に調整」を打つと、値引きの金額は自動で決まります（空なら金額の欄に打った額）。",
    "社内用の列の小計は仕入れの合計と、それに対する利益率です。",
  ]);
  /** 行に渡す拾い出しまわり（#130）：紙に出す数量・件数・一式の印・内訳の行の親の名前。雛形・請求書には無い */
  const tkOf = (r: Row): RowTakeoff | undefined =>
    alt || r.role !== "detail"
      ? undefined
      : {
          qty: qtyOf(r),
          count: calc.tk[r.id] ?? 0,
          basis: isLumpsumUnit(r.unit) ? (calc.basis[r.id] ?? null) : null,
          childOf: r.parentLineId ? (rows.find((x) => x.id === r.parentLineId)?.name ?? null) : undefined,
          onOpen: () => openTakeoff(r.id),
        };
  /** 1行（見る／書く）。手で並べる・並べ方（#129）の両方で使う。No. は描く順に振る */
  const renderLine = (i: number) => {
    const r = rows[i]!;
    if (NUMBERED.has(r.role)) no++;
    const n = NUMBERED.has(r.role) ? no : "";
    const pos = visible.indexOf(i);
    const mark = markOf.get(r.id);
    const onRevert = mark?.change === "changed" ? () => void doRestore("line", mark.lineageId, RESTORE_MSG.reverted) : undefined;
    const gone = removedAfter(r.id).map(removedRow);
    if (hideRow(i)) return gone.length ? <Fragment key={r.id}>{gone}</Fragment> : null;
    const v: LineView = {
      r,
      ctx,
      no: n,
      amount: amountOf(r),
      under: isUnder(r),
      tk: tkOf(r),
      // 並べ方のときは見出しが場所・工種なので、名称の右のしるしは出さない
      axis: axisMode ? { place: null, trade: null } : { place: can("places") ? placeName(r.placeId) : null, trade: can("trades") ? tradeName(r.tradeId) : null },
      src: srcOf(r, i),
      block: r.role === "subtotal" ? blockCost(i) : undefined,
      drag: edit === i ? undefined : dragOf(r, i),
      select: readOnly || alt ? undefined : { on: axSel.has(r.id), set: (on) => setAxSel((x) => { const y = new Set(x); if (on) y.add(r.id); else y.delete(r.id); return y; }) },
      onEdit: (fid) => editRow(i, fid),
      // #302（B3）：請求書も出した回の仕入れを直せる（雛形は無い）
      onCostEdit: readOnly && !template ? saveIssuedCost : undefined,
      // #306：率の行の対象の金額（API の計算値）
      ...(r.role === "overhead_rate" && r.id in calc.rateBase ? { rateBaseAmount: calc.rateBase[r.id] } : {}),
      mark,
      onRevert,
      busy: restoring,
    };
    return (
      <Fragment key={r.id}>
        {edit === i ? (
          <EditRow
            {...v}
            pos={pos}
            rows={visible.map((k) => rows[k]!)}
            axisMode={axisMode}
            strip={() => (!alt ? <AxisStrip r={r} lists={axLists} upd={(p) => upd(i, p)} onAskNew={askNewPlace} field={field} places={can("places")} trades={can("trades")} labor={field && r.role === "detail" && r.isLabor} /> : null)}
            provStrip={!alt ? <ProvisionalStrip r={r} upd={(p) => upd(i, p)} onFinalize={() => finalizeRows(new Set([r.id]))} /> : null}
            unitPreferred={!alt ? axLists.itemUnit(r.itemId) : null}
            onKey={onRowKey(i)}
            upd={(p) => upd(i, p)}
            onType={(t) => changeType(i, t)}
            onInsert={() => nextLine(i, true)}
            onDelete={() => askDelLine(i)}
            onClose={closeEdit}
            onAmtBlur={(x) => amtShortcut(i, x)}
            focusNext={focusNext}
            onNameTyped={pickerTyped}
            onSearch={() => {
              if (pick.open && pick.by === "button") closePicker();
              else openPicker("button", (document.getElementById("f_name") as HTMLInputElement | null)?.value ?? "");
              focusNext("f_name", true);
            }}
            pickOpen={pick.open}
            onUnlink={!alt ? () => void doUnlink(r.id) : undefined}
            onCostBlur={() => autoPrice(i)}
          />
        ) : (
          <ViewRow {...v} />
        )}
        {gone}
      </Fragment>
    );
  };

  /* ---- 頭の帯（#131）：状態・顧客・見積日・No.・合計・主のボタン（#173 で「≡」は題の右の「⋯」へ） ---- */
  /* 見出しの右の状態チップ（#171、docs/10 §5.12）：前は題の下の帯（出したあと・取り消した下書き・受注／失注・訂正中）。
     出した直後・取り消した直後の一言は通知、そのあとも続く状態はチップ、帯の中のボタンはチップの右（estHeadActions） */
  const issuedDay = initial.issued ? dayJst(initial.issued.at) : null;
  const issuedText = `${initial.displayNo ?? initial.documentNo ?? ""} として${issuedDay ? md(issuedDay) + "に" : ""}発行しました`;
  const decidedDay = initial.order?.decidedAt ? md(initial.order.decidedAt.slice(0, 10)) + "に" : "";
  const status: TopStatus = readOnly
    ? initial.order?.status === "ordered"
      ? { label: "受注", kind: "ordered", title: `${issuedText}。${decidedDay}受注しました` }
      : initial.order?.status === "lost"
        ? { label: "失注", kind: "lost", title: `${issuedText}。${decidedDay}失注にしました` }
        : { label: `発行済${issuedDay ? ` ${mdSlash(issuedDay)}` : ""}`, kind: "issued", title: `${issuedText}。発行済みのため修正できません` }
    : initial.revising
      ? {
          label: `訂正中 ${initial.revision.no}回目`,
          kind: "draft",
          title: `${initial.revision.no}回目（${initial.displayNo}）を作成中です。発行済みの回は ${initial.revising.baseRevisionNo}回目（${md(initial.revising.baseIssueDate)}・${yen(initial.revising.baseTotal)} 円）です`,
        }
      : !template && !invoice && (unissue.done || initial.documentNo)
        ? // #273・#278（docs/99 §3・§9）：発行を取消して下書きに戻した見積（番号はそのまま）も一覧と同じ「下書き」。番号ありは乗せたときの説明で
          { label: UNISSUED_MSG.chip, kind: "draft", title: unissuedTitle(unissue.done ?? initial.documentNo ?? "") }
        : { label: "下書き", kind: "draft" };
  const saveNode = (
    <span className="hint autosave" id="autosave" role="status">
      {save.kind === "idle" ? "" : save.kind === "saving" ? "保存しています…" : save.kind === "saved" ? `保存しました ${save.at}` : `⚠ 保存できませんでした：${save.msg}${save.retrying ? "（少し待って自動でもう一度送ります）" : ""}`}
    </span>
  );
  /** 帳票の頭（DocHead）の自動保存は2行（1行目＝保存の様子・2行目＝時刻。文言は前の1行の形のまま＝「保存しました」「10:31」。間の空白で読み上げと文字の並びは「保存しました 10:31」。#245） */
  const saveNode2 = (
    <span className="autosave dh-save2" id="autosave" role="status">
      {save.kind === "error" ? (
        <span className="dh-save-err">{`⚠ 保存できませんでした：${save.msg}${save.retrying ? "（少し待って自動でもう一度送ります）" : ""}`}</span>
      ) : (
        <>
          <span>{save.kind === "saving" ? "保存しています…" : save.kind === "saved" ? "保存しました" : "自動保存"}</span>{" "}
          <span className="num">{save.kind === "saved" ? save.at : "\u00a0"}</span>
        </>
      )}
    </span>
  );
  /* 見出しの「⋯」の下の2段（#173：前は右の「≡」。見出しのメニューは「⋯」の1つ、docs/10 §5.12）：
     doc＝PDF（下書きの PDF・前の回の控え・紙の形・積算表）・見積依頼・写真台帳、danger＝下書きを削除（訂正中も同じ。前は主なボタンにもあった） */
  const docActions: HeadAction[] = alt
    ? []
    : [
        // #302（B1）：下書きの PDF は頭の帯の脇のボタン「PDF 確認」（発行の左）へ移した（前はこの段の1つ目）
        // 前の回の紙（控えの PDF）。回をくらべられないプラン（パーソナル）の出した見積だけ。前は題の下の帯の中（#171）
        ...(readOnly && !initial.historyEnabled
          ? initial.history.map((h) => {
              const url = `/api/v1/estimates/${initial.id}/pdf?rev=${h.no}`;
              return {
                label: pdfTab.busy === url ? MSG.pdfMaking : `${initial.documentNo}${h.no >= 2 ? `-${h.no}` : ""} 控え（${md(h.issueDate)}・${yen(h.total)} 円）`,
                icon: <Icon k="doc" />,
                disabled: pdfTab.busy != null,
                onSelect: () => pdfTab.open(url, { stored: true }),
                testId: `history-${h.no}`,
              };
            })
          : []),
        ...(hasBreakdown
          ? [
              { label: ESTIMATE_PC_MSG.formSheets, checked: pdfForm === "sheets", onSelect: () => setPdfForm("sheets"), testId: "pdf-form-sheets" },
              { label: ESTIMATE_PC_MSG.formFlat, checked: pdfForm === "flat", onSelect: () => setPdfForm("flat"), testId: "pdf-form-flat" },
            ]
          : []),
        ...(!readOnly && initial.takeoffSheet && can("takeoff") ? [{ label: t(ESTIMATE_PC_MSG.takeoffSheet), checked: tkSheet.on, onSelect: () => void toggleTakeoffSheet(), testId: "takeoff-sheet" }] : []),
        // 見積依頼書（#119）：使う機能 rfq の会社だけ（rfqs を読むのが rfq の会社だけ。#149）。写真台帳は field の会社だけ（写真があるときだけ）
        // 見積依頼書を作成…は書ける見積ではいつも出す（10/7 射場さん。外注先の付いた行が無ければ窓で外注先と行を選ぶ）。
        // 出した見積は行に外注先を付けられないので、外注先の付いた行か送った依頼があるときだけ
        ...(rfqs.data && (!readOnly || rfqs.data.vendors.some((v) => v.lineCount > 0 || v.current)) ? [{ label: RFQ_MSG.menuCreate, icon: <Icon k="doc" />, onSelect: () => setRfqPick(true), testId: "menu-rfq" }] : []),
        ...(field && Object.values(calc.att).some((a) => a.photos > 0) ? [{ label: RFQ_MSG.menuLedger, icon: <Icon k="doc" />, onSelect: () => setLedgerOpen(true), testId: "menu-photo-ledger" }] : []),
      ].map((a): HeadAction => ({ ...a, group: "doc" }));
  const dangerActions: HeadAction[] =
    alt || readOnly
      ? []
      : [
          initial.revising
            ? { label: ESTIMATE_PC_MSG.discard, danger: true, group: "danger", onSelect: () => setStopRevise({ open: true, busy: false }), testId: "discard-revision" }
            : { label: ESTIMATE_PC_MSG.discard, danger: true, group: "danger", onSelect: () => setAskDelete(true), testId: "discard-draft" },
        ];
  const primary = readOnly ? (
    <Link className="btn primary hprim" to={pdfUrl} data-testid="pdf-link">
      <Icon k="doc" />
      {ESTIMATE_PC_MSG.pdfOpen}
    </Link>
  ) : (
    <button className="btn primary hprim" type="button" disabled={lineCount === 0} title={lineCount === 0 ? "明細を1行以上入力すると発行できます" : undefined} onClick={() => void (initial.revising ? openReviseIssue() : openIssue())} data-testid="issue-estimate">
      <Icon k="lock" />
      {initial.revising ? `${initial.displayNo} として発行` : "見積書発行"}
    </button>
  );
  /* 見出しの右（#171、docs/10 §5.12）：前は題の下の帯（issued-band・order-band・revising-band）の中にあったボタンとチップ */
  const order = readOnly ? initial.order : null;
  const estChips = alt ? null : (
    <>
      <EditingChip editors={editors} />
      {order?.status === "ordered" && order.plan ? (
        // 042：分割の予定がある見積は「分割 n／m回 請求済」（案件の画面と同じ決まり）
        <span className={"chip" + (order.plan.next == null ? " ai" : " quiet")} data-testid="order-plan" title={order.plan.next == null ? "すべての回を出しました" : undefined}>
          分割 {order.plan.issuedCount}／{order.plan.count}回 請求済
        </span>
      ) : dnq.chip ? (
        dnq.chip
      ) : order?.status === "ordered" && order.invoice && order.invoicedCount > 0 && order.invoice.status !== "draft" ? (
        <span className="chip ai" data-testid="order-plan" title={`一括の請求書 ${order.invoice.documentNo ?? ""}`}>請求済</span>
      ) : null}
    </>
  );
  const unissueLabel = initial.revision.no >= 2 ? `${initial.revision.no}回目を取消` : "取り消して下書きに戻す";
  const reviseNow = () => (initial.order?.status === "ordered" ? setReviseAsk(true) : void doRevise());
  /* 見出しの右の操作（#172、docs/10 §5.12）：1280px でも1段に収める＝状態チップのほかは 主なボタン1つ（rank の小さいもの）と「⋯」。
     出したあと＝訂正版を作成（取消しかできなければ取消）、受注＝請求書の動き（作成・次の回・表示・分割の予定作成）、訂正中＝下書きを削除 */
  const estActions: HeadAction[] = alt
    ? []
    : readOnly
      ? [
          // 受注／失注（024）。受注の見積から請求書へ。元に戻すのは一覧（通知の「元に戻す」）
          // {deliveryNote}の会社（#266）：{deliveryNote}作成・{deliveryNote}から請求書作成（見積から直接請求した受注・分割は今のまま）
          ...(order?.status === "ordered" && dnq.active
            ? dnq.actions
            : order?.status === "ordered"
            ? order.plan
              ? order.plan.next == null
                ? []
                : order.plan.next.draftInvoiceId
                  ? [{ label: `${order.plan.next.seq}回目下書き表示`, to: `/invoices/${order.plan.next.draftInvoiceId}`, testId: "order-next", rank: 1 }]
                  : [{ label: `${order.plan.next.seq}回目請求書作成${order.plan.next.label ? `（${order.plan.next.label}）` : ""}`, onSelect: () => void createInvoice(initial.id, order.plan!.next!.id), testId: "order-next", rank: 1 }]
              : order.invoice
                ? [{ label: order.invoice.status === "draft" ? "請求書下書き表示" : `請求書 ${order.invoice.documentNo ?? ""} 表示`, to: `/invoices/${order.invoice.id}`, testId: "order-invoice", rank: 1 }]
                : initial.projectGroup?.billingTiming === "split"
                  ? [
                      { label: "分割予定作成", to: `/groups/${initial.projectGroup.id}?plan=${initial.id}`, testId: "order-plan-new", rank: 1 },
                      { label: "一括請求", onSelect: () => void createInvoice(initial.id), testId: "order-lump" },
                    ]
                  : [{ label: "請求書作成", onSelect: () => void createInvoice(initial.id), testId: "order-invoice-new", rank: 1 }]
            : []),
          // #302（C3）：発行済で未受注のときは「受注」を頭の帯のボタン（主なボタン＝訂正版作成の左。下の orderBtn）・「失注」は ⋯ の中
          ...(order?.status === "none" && initial.status === "issued" ? [{ label: ESTIMATE_PC_MSG.decideLost, onSelect: () => void decideHere("lost"), testId: "head-lost" }] : []),
          ...(initial.canRevise ? [{ label: "訂正版作成", onSelect: reviseNow, testId: "make-revision", rank: 2 }] : []),
          ...(initial.canUnissue ? [{ label: unissueLabel, onSelect: () => setUnissue({ open: true, busy: false }), testId: "unissue-estimate", ...(initial.canRevise ? { title: "まだ渡していなければ" } : {}), rank: 3 }] : []),
          ...(order?.status === "ordered" && order.plan && initial.projectGroup ? [{ label: "分割予定", to: `/groups/${initial.projectGroup.id}`, testId: "order-plan-open" }] : []),
          ...(order?.status === "ordered" ? [{ label: "見積追加", onSelect: () => void addJob(), testId: "add-job", title: `同じ${L.project}に、別の番号の見積を追加します` }] : []),
        ]
      : // #302（B1）：下書き（訂正中も）は頭の帯の脇のボタン「PDF 確認」（主なボタン＝発行ボタンの左）。乗せると「PDF で確認（透かし入り）」
        [{ label: ESTIMATE_PC_MSG.pdfCheck, title: ESTIMATE_PC_MSG.pdfDraft, to: pdfUrl, onLink: openDraftPdf, testId: "pdf-link", rank: 1 }];
  const headActions: HeadAction[] = [...estActions, ...docActions, ...dangerActions];
  // 主なボタンは状態チップの右・「⋯」は頭のいちばん右（#243、docs/94 B-6）
  /** #302（C3）：発行済で未受注の見積書の「受注」（一覧の受注と同じ動き・通知の「元に戻す」）。主なボタン（訂正版作成）の左 */
  const orderBtn =
    !alt && order?.status === "none" && initial.status === "issued" ? (
      <button type="button" className="small" onClick={() => void decideHere("ordered")} data-testid="head-order">
        {ESTIMATE_PC_MSG.decideOrdered}
      </button>
    ) : null;
  const mainActs = headActions.length ? <HeadActions actions={headActions} label={ESTIMATE_PC_MSG.headMenu} part="main" /> : null;
  const estActionsNode = orderBtn || mainActs ? <>{orderBtn}{mainActs}</> : null;
  const estMenuNode = headActions.length ? <HeadActions actions={headActions} label={ESTIMATE_PC_MSG.headMenu} part="more" /> : null;
  /* #315（11/5、docs/103 §8）：頭の宛先は「略称 ／ 支店 ／ 担当者」（取引先検索の選んだあとの1行と同じ形・敬称なし）。乗せると紙の宛名（正式名称・敬称つき） */
  const rcp = headRecipientOf({ name: head.clientName, shortName: head.clientShort, honorific: head.honorific, branch: branchName, contact: contactLabel });
  const clientNode = (
    <span className="cust">
      <button type="button" className="custbtn" onClick={() => !readOnly && setPickClient((v) => !v)} aria-expanded={pickClient} title={rcp.paper} data-testid="doc-client">
        <b>{rcp.name}</b>
        {rcp.branch ? <span className="brn">／ {rcp.branch}</span> : null}
        {rcp.contact ? <span className="brn who">／ {rcp.contact}</span> : null}
      </button>
      {/* 取引先検索のドロアー（#288、docs/101 §4）。担当者・最近の行で選ぶと支店・担当者も入る */}
      <PartnerPickerDrawer
        open={pickClient}
        role="customer"
        onClose={() => setPickClient(false)}
        onPick={(c) => {
          if (c.id === head.clientId) {
            if (c.branch || c.contact) setH({ partnerBranchId: c.branch?.id ?? null, partnerContactId: c.contact?.id ?? null });
            return;
          }
          // 顧客は案件ごとに1つ（決めごと7）：新しい顧客の新しい案件に入り直す。支店・担当者は選んだものだけ
          setNewGroupForm(null);
          setH({ clientId: c.id, clientName: c.name, clientShort: c.shortName ?? "", honorific: c.honorific, projectGroupId: null, newGroup: true, partnerBranchId: c.branch?.id ?? null, partnerContactId: c.contact?.id ?? null });
          // #315：通知の文も略称（敬称なし）
          toast(`${L.customer}を ${listNameOf({ name: c.name, shortName: c.shortName ?? "" })} にしました。`);
        }}
      />
    </span>
  );

  /* ---- 道具の列（#131 の2回目、Figma PC2 → #187 Figma 172:175）：内訳のタブ（選んでいるタブの「…」に 名前・左へ・右へ・労務費・消す）＋「＋」・右上に 1枚｜表紙＋内訳。
     表示順・元に戻す・社内用は #187 から頭の帯の2行目（請求書・雛形は道具の列の右のまま）。端数処理は「計算方法」の「？」の中へ。
     1枚｜表紙＋内訳は #139 でアイコン2つ（紙1枚／重なった紙）。名前は aria-label と title、押している方は藍（is-on） ---- */
  const sheetSeg = !readOnly && !axisMode ? (
    <span className="seg sheetseg" role="group" aria-label="構成">
      <button type="button" className={useSections ? undefined : "is-on"} aria-pressed={!useSections} aria-label={ESTIMATE_PC_MSG.oneSheet} title={ESTIMATE_PC_MSG.oneSheet} onClick={() => useSections && toggleSections()} data-testid="sheet-one">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5" />
        </svg>
      </button>
      <button type="button" className={useSections ? "is-on" : undefined} aria-pressed={useSections} aria-label={ESTIMATE_PC_MSG.coverSheets} title={ESTIMATE_PC_MSG.coverSheets} onClick={() => !useSections && toggleSections()} data-testid="sheet-split">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1zM8 9V6.5a.5.5 0 0 1 .5-.5H18.5a.5.5 0 0 1 .5.5V17a.5.5 0 0 1-.5.5H16M11 6V3.5a.5.5 0 0 1 .5-.5H21.5a.5.5 0 0 1 .5.5V14a.5.5 0 0 1-.5.5H19" />
        </svg>
      </button>
    </span>
  ) : null;
  const dots = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
  /** 選んでいる内訳のタブの「…」（前は表の上の「この内訳の紙」の帯） */
  const sheetMenuOf = (x: Sheet): RowMenuItem[] => [
    { label: ESTIMATE_PC_MSG.sheetRename, onSelect: renameSection, testId: "sheet-rename" },
    // #199：複製は見積だけ（DB の fn_copy_sheet。拾い出し・写真も写す）。請求書・雛形には出さない
    ...(!alt ? [{ label: ESTIMATE_PC_MSG.sheetCopy, onSelect: () => void copySection(x), disabled: copying, testId: "sheet-copy" }] : []),
    { label: ESTIMATE_PC_MSG.sheetLeft, onSelect: () => moveSection(x.id, -1), disabled: breakdowns[0]?.id === x.id, testId: "sheet-left" },
    { label: ESTIMATE_PC_MSG.sheetRight, onSelect: () => moveSection(x.id, 1), disabled: breakdowns[breakdowns.length - 1]?.id === x.id, testId: "sheet-right" },
    ...(field ? [{ label: ESTIMATE_PC_MSG.sheetLabor, checked: x.isLabor, onSelect: () => updSheet(x.id, { isLabor: !x.isLabor }), testId: "sheet-labor" }] : []),
    // #199：利用しない（不使用）⇄ 利用する。合計・紙・比較から外す（中身は見られる・直せる）
    { label: x.inUse === false ? ESTIMATE_PC_MSG.sheetUse : ESTIMATE_PC_MSG.sheetDisuse, divider: true, onSelect: () => toggleSheetUse(x), testId: "sheet-use" },
    { label: ESTIMATE_PC_MSG.sheetDelete, danger: true, divider: true, onSelect: () => setDelDlg(x), testId: "sheet-delete" },
  ];
  /* #200：削除した内訳（1件以上のときだけ。見積だけ・書ける回だけ）。押すと窓 */
  const trashBtn = trashCount > 0 && !readOnly ? (
    <button type="button" className="btn text etrash" onClick={() => setTrashDlg(true)} data-testid="sheet-trash-open">
      {SHEET_TRASH_MSG.entry} {trashCount}
    </button>
  ) : null;
  /* 内訳のタブ（試作 v3.7）。並べ方（#129）のときは出さない（紙は軸ごと） */
  const sheetTabs =
    useSections && !axisMode ? (
      <div className="etabs" id="tabs" role="tablist" aria-label="内訳書">
        <button type="button" role="tab" aria-selected={onCover} className={"etab cover" + (onCover ? " is-on" : "")} onClick={() => goSheet("cover")}>
          <b><span className="covt">表紙</span>{tabCount(coverSheet?.id)}</b>
          <span className="num tsub">{usedBreakdowns.length}内訳</span>
        </button>
        {breakdowns.map((x) => (
          <span key={x.id} className={"etab" + (cur === x.id ? " is-on" : "") + (field && x.isLabor ? " labor" : "") + (x.inUse === false ? " is-unused" : "")} data-testid={x.inUse === false ? "sheet-tab-unused" : undefined}>
            <button type="button" role="tab" aria-selected={cur === x.id} onClick={() => goSheet(x.id)} title={field && x.isLabor ? `${x.name}（労務費）` : undefined}>
              <b>
                {x.name || "（名前なし）"}
                {x.inUse === false ? <span className="sheet-unused">{ESTIMATE_PC_MSG.sheetUnused}</span> : null}
                {tabCount(x.id)}
              </b>
              <span className="num tsub">{yen(sheetTotal(x.id))}</span>
            </button>
            {cur === x.id && !readOnly ? <RowMenu label={ESTIMATE_PC_MSG.sheetMenu.replace("{name}", x.name || "この内訳")} items={sheetMenuOf(x)} testId="sheet-menu" trigger={dots} triggerClass="tabmore" /> : null}
          </span>
        ))}
        {!readOnly ? (
          <button type="button" className="etabadd" onClick={addSection} aria-label={ESTIMATE_PC_MSG.addSheet} title={ESTIMATE_PC_MSG.addSheet} data-testid="sheet-add">
            <Icon k="plus" />
          </button>
        ) : null}
        {trashBtn}
      </div>
    ) : null;
  /** 元に戻す／やり直す（#140）：表示順の右（#187 から頭の帯の2行目）。押せないときは灰 */
  const modKey = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl+";
  const undoBar = !readOnly ? (
    <span className="undo" role="group" aria-label={`${ESTIMATE_PC_MSG.undo}・${ESTIMATE_PC_MSG.redo}`}>
      <button type="button" className="undobtn" aria-label={ESTIMATE_PC_MSG.undo} title={`${ESTIMATE_PC_MSG.undo}（${modKey}Z）`} disabled={!canUndo} onClick={undo} data-testid="undo">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
        </svg>
      </button>
      <button type="button" className="undobtn" aria-label={ESTIMATE_PC_MSG.redo} title={`${ESTIMATE_PC_MSG.redo}（${modKey}${modKey === "⌘" ? "⇧Z" : "Shift+Z"}）`} disabled={!canRedo} onClick={redo} data-testid="redo">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
        </svg>
      </button>
    </span>
  ) : null;
  /** 表示順（#129 の並べ方。#187 で呼び方を「表示順」に）：見積書は頭の帯の2行目、請求書・雛形は道具の列の右。手動以外のときは右に「？」 */
  const layoutSel = !template ? (
    <>
      <LayoutSelect value={axis} onChange={(a) => void changeAxis(a)} disabled={readOnly} axes={layoutAxesFor({ trades: can("trades"), places: can("places") }, axis)} />
      {axisMode ? (
        <Help testId="help-axis" label="表示順の説明" text={helpText([
          "表示順を変えても、行・金額は変わりません。帳票（画面・PDF）の並びだけが変わります。",
          // #142 段6 No.3・4：工種は言葉の {trade}、場所は使う機能で OFF の会社には書かない
          t(`手で作った見出し・小計の行は印字しません（表示順を「手動」に戻すと印字します）。${can("places") ? "場所・{trade}" : "{trade}"}の付いていない行は「その他」に並びます。`),
          t(`${can("places") ? "場所・{trade}" : "{trade}"}は行を押した行の詳細か、上の「${can("places") ? "{trade}なし／場所なし" : "{trade}なし"} → 入力」で付けます。No の □ で選ぶと、まとめて付けられます。`),
          ESTIMATE_PC_MSG.sortLocked + "。",
        ])} />
      ) : null}
    </>
  ) : null;
  /** 社内用の列を出す（端末に覚える）。見積書は頭の帯の2行目（詳細の左）、請求書・雛形は道具の列の右 */
  const internalSw = (
    <span className="intsw">
      <span className="muted">{ESTIMATE_PC_MSG.showInternal}</span>
      <Switch checked={showInternal} onChange={setShowInternal} label={ESTIMATE_PC_MSG.showInternal} testId="show-internal" bare />
    </span>
  );
  /**
   * 道具の列（#187、Figma 172:175）：左に内訳のタブ（折り返す）＋「＋」、右上に紙の形（1枚｜表紙＋内訳）。タブが何段になっても紙の形は右上から動かない（grid の 1fr auto）。
   * 表示順・元に戻す・社内用・詳細は頭の帯の2行目（前はここに1つの折り返しで並べ、内訳が増えるとタブの続きに流れた）。
   * 表示順が手動以外（タブも紙の形も出さない）・出した見積の1枚の紙は、この段ごと出さない（空の段を残さない）。
   * 請求書・雛形（頭の帯が無い）は今までどおり右に 表示順・↶ ↷・社内用 も置く
   */
  // #245（docs/94 B-4）：帳票の頭（DocHead）を使う画面は、紙の形・表示順・社内用は頭の3段目、↶ ↷ は1段目へ＝道具の列は内訳のタブの段だけ
  const docHeadOn = true;
  const toolRight = docHeadOn ? null : orderDoc ? (
    // {order}（#233）：表示順は手動だけ・社内用の列は無い
    <>
      {sheetSeg}
      {undoBar}
    </>
  ) : alt ? (
    <>
      {sheetSeg}
      {layoutSel}
      {undoBar}
      {internalSw}
    </>
  ) : sheetSeg;
  const toolbar = sheetTabs || toolRight || trashBtn ? (
    <div className="etools" data-testid="estimate-tools">
      <div className="etools-l">{sheetTabs ?? trashBtn}</div>
      <div className="etools-r">{toolRight}</div>
    </div>
  ) : null;
  /* 相違（037 の印）：#171 で段（帯）をやめ、表の上の切替スイッチに。#187 で内訳タブの段の下に1行。#188 から回の帯の中。
     #190 から 回の帯の右のブロックに「相違 なし／表示／変更行のみ」（3択のセグメント）と件数のバッジ（0 は薄い色）。合計の増減は帯に出さない（下書きのチップにある）。
     回の帯を畳んだときは件数を出さない（訂正中のときだけ） */
  const marksSeg = (disabled: boolean, baseNo: number | null) => (
    <span className={"mksw" + (disabled ? " is-busy" : "")} aria-hidden={disabled || undefined} data-testid={disabled ? "marks-bar-loading" : "marks-bar"}>
      <span className="mklbl" title={baseNo ? `前の回（${baseNo}回目）との相違` : undefined}>{ESTIMATE_PC_MSG.marksLabel}</span>
      <Segmented<MarksMode>
        options={MARKS_MODES.map((v) => (disabled ? { value: v, label: ESTIMATE_PC_MSG.marksModes[v] } : { value: v, label: ESTIMATE_PC_MSG.marksModes[v], testId: `marks-${v}` }))}
        value={marksMode}
        onChange={setMarksMode}
        label={`${ESTIMATE_PC_MSG.marksShow}${baseNo ? `（${baseNo}回目と比較）` : ""}`}
        disabled={disabled}
        testId={disabled ? undefined : "marks-mode"}
        className="mkseg"
      />
    </span>
  );
  const marksBadges = (c: RevisionMarks["counts"] | null) => (
    <span className={"mkbadges" + (c ? "" : " is-busy")} aria-hidden={!c || undefined} data-testid={c ? "marks-counts" : undefined}>
      {(["changed", "added", "removed", "calc"] as const).map((k) => (
        <Fragment key={k}>
          <span className={"mkbadge" + ((c?.[k] ?? 0) ? "" : " is-zero")} data-testid={c ? `marks-count-${k}` : undefined}>
            {MARK_LABEL[k]} <b className="num">{c?.[k] ?? 0}</b>
          </span>{" "}
        </Fragment>
      ))}
    </span>
  );
  const marksTools = marking && (marksQ.data || marksQ.isPending)
    ? (collapsed: boolean): RoundMarks =>
        marksQ.data
          ? { mode: marksSeg(false, marksQ.data.base.revisionNo), counts: collapsed ? undefined : marksBadges(marksQ.data.counts) }
          : // 相違内容が届くまで（#179）：同じ形を見えない形で置いて場所を取る（後から入ると回の帯が折れて表が下へずれる）
            { mode: marksSeg(true, null), counts: collapsed ? undefined : marksBadges(null) }
    : undefined;
  /* ---- お知らせの1行（#131 の2回目）：仮 n行 → すべて確定・一式（内訳なし）n行・工種なし n行 → 入れる（場所なし）・行に入れていない拾い出し。該当があるときだけ ---- */
  const usesTrade = axis === "trade" || axis === "trade_place" || axis === "place_trade";
  const usesPlace = axis === "place" || axis === "trade_place" || axis === "place_trade";
  const tradeMissing = rows.filter((r) => r.role === "detail" && !r.tradeId);
  const placeMissing = rows.filter((r) => r.role === "detail" && !r.placeId);
  const showTradeMissing = !alt && can("trades") && tradeMissing.length > 0 && (usesTrade || rows.some((r) => r.role === "detail" && r.tradeId));
  const showPlaceMissing = !alt && can("places") && placeMissing.length > 0 && usesPlace;
  const notices: React.ReactNode[] = [];
  // 原価割れ（書ける見積はここ。出した見積は帯の下の知らせ＝仕入れだけ直せることを添える）
  if (underCount && !readOnly)
    notices.push(
      <span key="under" className="nt">
        <span className="chip shu" data-testid="under-count">{ESTIMATE_PC_MSG.underCount.replace("{n}", String(underCount))}</span>
      </span>,
    );
  if (provisionalCount && !alt)
    notices.push(
      <span key="prov" className="nt" data-testid="provisional-bar">
        <span className="chip prov" data-testid="provisional-count">{ESTIMATE_PC_MSG.provisionalCount.replace("{n}", String(provisionalCount))}</span>
        {!readOnly ? (
          <button type="button" className="ntlink" data-testid="finalize-all" onClick={finalizeAll}>→ {ESTIMATE_PC_MSG.finalizeAll}</button>
        ) : null}
      </span>,
    );
  if (lumpsum && lumpsum.count)
    // 社内用の知らせ（#130）。出すのは止めない。内訳の行・拾い出し・メモ・外注見積の出どころのどれかがあれば消える
    notices.push(
      <span key="lump" className="nt">
        <span className="chip" data-testid="lumpsum-warning" title={`${TAKEOFF_MSG.lumpsumWarning.replace("{n}", String(lumpsum.count)).replace("{yen}", yen(lumpsum.total))}（社内用）`}>
          {ESTIMATE_PC_MSG.lumpsumCount.replace("{n}", String(lumpsum.count))}
        </span>
      </span>,
    );
  if (showTradeMissing)
    notices.push(
      <span key="trade" className="nt">
        <span className="chip" data-testid="trade-missing">{t(ESTIMATE_PC_MSG.tradeMissing).replace("{n}", String(tradeMissing.length))}</span>
        {!readOnly ? (
          <button type="button" className="ntlink" data-testid="trade-fill" onClick={() => setFill("trade")}>→ {ESTIMATE_PC_MSG.fillIn}</button>
        ) : null}
      </span>,
    );
  if (showPlaceMissing)
    notices.push(
      <span key="place" className="nt">
        <span className="chip" data-testid="place-missing">{ESTIMATE_PC_MSG.placeMissing.replace("{n}", String(placeMissing.length))}</span>
        {!readOnly ? (
          <button type="button" className="ntlink" data-testid="place-fill" onClick={() => setFill("place")}>→ {ESTIMATE_PC_MSG.fillIn}</button>
        ) : null}
      </span>,
    );
  // T2：送料の行を軽減にしている（止めない。contract の shippingReducedWarning）
  const shippingWarn = !readOnly ? rows.map((r) => shippingReducedWarning(r.name, r.taxCategory)).find((x): x is string => !!x) : undefined;
  if (shippingWarn)
    notices.push(
      <span key="ship" className="nt" data-testid="shipping-reduced">{shippingWarn}</span>,
    );
  // T2：見積 → 請求書で税率が変わった（請求書だけ。取引日が切替日をまたいだ）
  if (invoice && calc.taxChanged)
    taxChangedText(calc.taxChanged, jdateOf).forEach((x, k) =>
      notices.push(
        <span key={`taxchg${k}`} className="nt" data-testid="tax-changed">{x}</span>,
      ),
    );
  if (!alt && calc.unassigned)
    notices.push(
      <span key="tk" className="nt">
        <button type="button" className="chip tkloose" data-testid="takeoff-unassigned" onClick={() => openTakeoff(null)}>{TAKEOFF_MSG.unassigned.replace("{n}", String(calc.unassigned))}</button>
      </span>,
    );
  const noticeLine = notices.length ? (
    <div className="enotice" data-testid="estimate-notice">
      {notices.map((x, k) => (
        <Fragment key={k}>
          {k ? <span className="ntsep" aria-hidden="true">・</span> : null}
          {x}
        </Fragment>
      ))}
    </div>
  ) : null;
  /** 社内用の小計の行：仕入れの合計と「仕入れ未入力 n行」（API の集計。雛形・請求書は画面の行から） */
  const costs = calc.costs ?? { total: allCost.sum, missing: allCost.missing, lines: 0 };
  const foot = (
    <>
      {useSections && !axisMode && curSheet ? (
        <TotalRow ctx={ctx} cls="tsheet" label="この内訳の合計" amount={sheetTotal(curSheet.id)} cost={{ total: sheetCost(curSheet.id).sum, missing: sheetCost(curSheet.id).missing, amount: sheetTotal(curSheet.id) }} />
      ) : null}
      <TotalRow ctx={ctx} cls="tsub" id="t_sub" sub label={`小計${useSections ? `（${usedBreakdowns.length}内訳＋表紙の行）` : ""}`} amount={calc.subtotal} cost={{ total: costs.total, missing: costs.missing, amount: calc.subtotal, fee: refFees?.total }} />
      {/* #213（docs/40 §6.2）：パートナー ON の見積だけ「{referrer} 見本不動産 10%　手数料 ○円　手数料後の利益 ○円（○%）」（社内用） */}
      {refFees && docRefPct != null ? (
        <ReferralBandRow
          ctx={ctx}
          label={t(`{referrer} ${refShort} ${pctShort(docRefPct)}%`)}
          fee={refFees.total}
          profit={costs.missing ? null : calc.subtotal - costs.total - refFees.total}
          margin={costs.missing || calc.subtotal <= 0 ? null : Math.round(((calc.subtotal - costs.total - refFees.total) / calc.subtotal) * 1000) / 10}
          missing={costs.missing}
        />
      ) : null}
      {/* T2：並びは 10% → 8% → 1% → 免税 → 非課税 → 不課税（紙と同じ）。免税・非課税・不課税は対象額だけ。立替金は内訳に並べず、消費税の下に1行 */}
      {calc.taxes.some(isTaxRow) ? (
        taxRowsInOrder(calc.taxes).map((x) => (
          <TotalRow key={`${x.kind}-${x.rate}-${x.reduced}`} ctx={ctx} cls="ttax" label={<>{taxScreenLabel(x)}<span className="muted">（対象 {yen(x.taxableBase)} 円）</span></>} amount={x.kind === "taxable" ? x.taxAmount : null} />
        ))
      ) : (
        <TotalRow ctx={ctx} cls="ttax" label="消費税" amount={calc.taxTotal} />
      )}
      {calc.reimbursementTotal ? <TotalRow ctx={ctx} cls="ttax treimb" id="t_reimb" label={TAX_MSG.reimbursement} amount={calc.reimbursementTotal} /> : null}
      <TotalRow ctx={ctx} cls="gt" id="t_total" label="合計（税込）" amount={calc.total} />
    </>
  );
  /**
   * 画面だけ（#131 の2回目）：1段目と同じ名前の2段目の見出し（「その他」の下の「その他」）は1つにする。その2段目の「○○ 計」も、
   * すぐ下の1段目の「○○ 計」と同じ名前・同じ額なら出さない。紙（PDF）は変えない（layoutLines はそのまま）
   */
  const axFlat = axLayout?.flat ?? [];
  const axDup = (k: number): boolean => {
    const x = axFlat[k];
    if (x?.kind === "heading" && x.level === 2) {
      const p = axFlat[k - 1];
      return p?.kind === "heading" && p.level === 1 && p.name === x.name;
    }
    if (x?.kind === "subtotal" && x.level === 2) {
      const nx = axFlat[k + 1];
      return nx?.kind === "subtotal" && nx.level === 1 && nx.name === x.name && nx.amount === x.amount;
    }
    return false;
  };
  const body = axLayout ? (
    // 並べ方（#129）：作った見出し → 行 → 「○○ 計」（layoutLines の flat。PDF の「1枚に続けて」と同じ並び）
    axLayout.flat.map((x, k) =>
      axDup(k) ? null : x.kind === "line" ? (
        renderLine(rowIndex.get(x.line.id)!)
      ) : x.kind === "heading" ? (
        <AxisHeadingRow key={"axh-" + x.key} ctx={ctx} name={x.name} level={x.level} />
      ) : x.kind === "subtotal" ? (
        <AxisSubtotalRow key={"axs-" + x.key} ctx={ctx} name={x.name} amount={x.amount} level={x.level} />
      ) : null,
    )
  ) : (
    <>
      {removedSheets.filter((x) => !x.afterSheetId || !breakdownIds.has(x.afterSheetId)).map(removedSheetRow)}
      {onCover
        ? usedBreakdowns.map((x, k) => {
            no++;
            const gone = removedSheets.filter((g) => g.afterSheetId === x.id).map(removedSheetRow);
            if (hideSec(x.id)) return gone.length ? <Fragment key={x.id}>{gone}</Fragment> : null;
            return (
              <Fragment key={x.id}>
                <SecRow
                  ctx={ctx}
                  sh={x}
                  no={no}
                  total={sheetTotal(x.id)}
                  cost={sheetCost(x.id)}
                  editing={secEdit === x.id}
                  first={k === 0}
                  last={k === usedBreakdowns.length - 1}
                  readOnly={readOnly}
                  onEdit={(fid) => {
                    dropAutoBlank();
                    setEdit(null);
                    setSecEdit(x.id);
                    focusNext(fid);
                  }}
                  onChange={(p) => updSheet(x.id, p)}
                  onMove={(d) => moveSection(x.id, d)}
                  onOpen={() => goSheet(x.id)}
                  onClose={() => setSecEdit(null)}
                  onNext={(fid) => secNext(x.id, fid)}
                  mkCount={mk?.sheetCounts[x.id] ?? 0}
                />
                {gone}
              </Fragment>
            );
          })
        : null}
      {removedTop.map(removedRow)}
      {visible.map(renderLine)}
    </>
  );
  const table = (
    <EstimateTable ctx={ctx} customer={L.customer} foot={foot} testId="estimate-table" sizer={colSizer}>
      {body}
      {!visible.length && !(onCover && usedBreakdowns.length) && !removedHere.length ? <EmptyRow ctx={ctx} text="明細がありません。下の「行追加」を押してください。" /> : null}
      {onlyChanged && mk && !removedHere.length && !removedSheets.length && visible.every((i) => hideRow(i)) && !(onCover && usedBreakdowns.some((x) => !hideSec(x.id))) && (visible.length || (onCover && usedBreakdowns.length)) ? (
        <EmptyRow ctx={ctx} text={ESTIMATE_PC_MSG.marksNoneHere} testId="marks-none-here" />
      ) : null}
    </EstimateTable>
  );
  /* ---- 頭の帯の「詳細」（#187、Figma 172:175「詳細（開いたところ）」）：{顧客}担当者 → 支店・営業所（支店のある{顧客}だけ）→ {案件} → 現場 → 工期 → 有効期限 → 工事概要 → 担当。
     前は担当者・支店・案件 ›・現場 ＋を2行目に並べていた（#131 の2回目）。2行目の右は 表示順・↶ ↷・社内用・詳細（headTools）。
     新しい{案件}の欄と担当者の登録の欄は、その行のすぐ下に開く。
     #272（docs/99 §2、Figma 288:146 案A）：PC は 3段目と同じ「名前が上・値が下」の項目を横に流す（帯は .dh-more の淡い地。欄の幅は .edrow の w-…）。767px 以下は1列のまま ---- */
  // 現場：工務店向けの言葉「工事場所」のときは短く「現場」（Figma）。ほかの言葉（納品先など）はそのまま
  const siteLabel = L.site === "工事場所" ? ESTIMATE_PC_MSG.site : L.site;
  /** {customer}の詳細を待っている間（#179）：詳細の中の行を見えない形で描いて場所だけ取る（担当者・支店の欄が後から入って下の行がずれないように）。
      支店の一覧は{customer}の詳細にも入っている＝新しい見積（作成画面で読んだ{customer}が残っている）は待たない */
  const detailBusy = !alt && !readOnly && partnerQ.isLoading;
  const detailText = (v: string | null | undefined, testId?: string) => (
    <span className={"edtext" + (v ? "" : " muted")} data-testid={testId}>{v || ESTIMATE_PC_MSG.contactNone}</span>
  );
  const detailPanel = alt ? null : (
    <div className={"esdetail" + (detailBusy ? " is-busy" : "")} data-testid="estimate-detail" aria-busy={detailBusy || undefined}>
      {/* {customer}担当者：選ぶと紙の宛名が「部署 氏名 様」（御中は付けない）。説明は欄の右の「？」（#187：前は題の「？」の中） */}
      {readOnly ? (
        <div className="edrow w-280">
          <span className="edl">{L.customer}{ESTIMATE_PC_MSG.contact}</span>
          <div className="edv">{detailText(contactLabel, "detail-contact")}</div>
        </div>
      ) : partnerQ.data ? (
        <ContactSelect
          row
          partner={partnerQ.data}
          branchId={head.partnerBranchId}
          id="h_contact"
          value={head.partnerContactId}
          help={<Help testId="help-party" label={`${L.customer}${ESTIMATE_PC_MSG.contact}の説明`} text={partyFieldsHelp(L)} />}
          onPartner={(p) => qc.setQueryData(["partner", head.clientId], p)}
          onChange={(v) => {
            setH({ partnerContactId: v });
            const k = qc.getQueryData<Partner>(["partner", head.clientId])?.contacts.find((c) => c.id === v);
            toast(k ? `宛名を「${`${k.department} ${k.name}`.trim()} 様」にしました（御中は付けません）。` : `宛名の担当者を外しました（${head.clientName} ${head.honorific}）。`);
          }}
        />
      ) : (
        // 届くまでの代わり：届いたあとと同じ形（名前・選ぶ欄・右の登録）。is-busy で見えない
        <div className="edrow w-280">
          <label className="edl" htmlFor="h_contact">{L.customer}{ESTIMATE_PC_MSG.contact}</label>
          <div className="edv">
            <select id="h_contact" className="inp" disabled>
              <option>{contactLabel ?? ESTIMATE_PC_MSG.contactNone}</option>
            </select>
          </div>
          <div className="eda" />
        </div>
      )}
      {readOnly ? (
        branchName ? (
          <div className="edrow w-240">
            <span className="edl">{ESTIMATE_PC_MSG.branch}</span>
            <div className="edv">{detailText(branchName, "detail-branch")}</div>
          </div>
        ) : null
      ) : branchList.length > 0 ? (
        <div className="edrow w-240">
          <label className="edl" htmlFor="h_branch">{ESTIMATE_PC_MSG.branch}</label>
          <div className="edv">
            <select
              id="h_branch"
              className="inp"
              aria-label="宛先の支店・営業所"
              value={head.partnerBranchId ?? ""}
              onChange={(e) => {
                const id = e.target.value || null;
                setH({ partnerBranchId: id });
                const b = branchList.find((x) => x.id === id);
                toast(b ? `宛先を ${rcp.name} ${b.name} にしました。` : `宛先を ${rcp.name}（本社）にしました。`);
              }}
            >
              <option value="">{ESTIMATE_PC_MSG.branchNone}</option>
              {branchList.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      ) : null}
      {/* {案件}：選び直すと{案件}が変わる（現場住所が空なら写す）。「新しい{案件}を作成…」は行のすぐ下に欄 */}
      <div className="edrow pgroup w-360">
        <label className="edl" htmlFor="projSel">{L.project}</label>
        <div className="edv">
          <select
            id="projSel"
            className="inp"
            value={newGroupForm ? "new" : head.newGroup ? "pending" : (head.projectGroupId ?? "")}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "new") {
                setNewGroupForm({ name: "", siteAddress: "", ownerName: "" });
                setGroupError(undefined);
                return;
              }
              setNewGroupForm(null);
              const g = groupList.find((x) => x.id === v);
              if (g) pickGroup(g);
            }}
          >
            {head.newGroup ? <option value="pending">新しい{L.project}（{L.subject}と同じ名前）</option> : null}
            {!head.newGroup && !curGroup ? <option value="">（{L.project}を選んでください）</option> : null}
            {pickableGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}（見積 {g.quoteCount}枚）
              </option>
            ))}
            {curGroup && !pickableGroups.some((g) => g.id === curGroup.id) ? <option value={curGroup.id}>{curGroup.name}</option> : null}
            {!readOnly ? <option value="new">新しい{L.project}を作成…</option> : null}
          </select>
          {/* 案件の名前が工事名に合わせて変わること・新しい案件の欄の扱いは「？」へ（#92） */}
          {follows || newGroupForm ? (
            <span data-testid={follows ? "follow-note" : undefined}>
              <Help
                testId="help-group"
                label={`${L.project}の説明`}
                text={helpText([
                  follows ? `${t(GROUP_MSG.followNote)}。` : null,
                  newGroupForm ? `${field ? `${L.siteAddress}と施主名` : L.siteAddress}は暗号化して保存します。同じ${L.project}に見積を追加するとき、同じ住所を打ち直さなくて済みます。` : null,
                ])}
              />
            </span>
          ) : null}
        </div>
        <div className="eda">
          {head.projectGroupId && !newGroupForm && !head.newGroup ? (
            <Link className="btn text sm" to={`/groups/${head.projectGroupId}`} data-testid="open-group">
              {ESTIMATE_PC_MSG.openGroup.replace("{project}", L.project)} ›
            </Link>
          ) : null}
        </div>
      </div>
      {newGroupForm ? (
        <form
          className="newgroup edform"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newGroupForm.name.trim()) {
              setGroupError(t(GROUP_MSG.nameRequired));
              return;
            }
            createGroup.mutate({ name: newGroupForm.name.trim(), siteAddress: newGroupForm.siteAddress.trim(), ownerName: newGroupForm.ownerName.trim() });
          }}
          noValidate
        >
          <div className="field" style={{ flex: "2 1 220px" }}>
            <label htmlFor="ng_name">{L.project}の名前</label>
            <input id="ng_name" className="inp" autoFocus value={newGroupForm.name} onChange={(e) => setNewGroupForm({ ...newGroupForm, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.nativeEvent.isComposing || e.keyCode === 229) && e.preventDefault()} />
          </div>
          <div className="field" style={{ flex: "3 1 280px" }}>
            <label htmlFor="ng_addr">{L.siteAddress}（任意。見積の{L.site}に写します）</label>
            <input id="ng_addr" className="inp" value={newGroupForm.siteAddress} onChange={(e) => setNewGroupForm({ ...newGroupForm, siteAddress: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.nativeEvent.isComposing || e.keyCode === 229) && e.preventDefault()} />
          </div>
          {field ? (
            <div className="field" style={{ flex: "1 1 160px" }}>
              <label htmlFor="ng_owner">施主（任意）</label>
              <input id="ng_owner" className="inp" value={newGroupForm.ownerName} onChange={(e) => setNewGroupForm({ ...newGroupForm, ownerName: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.nativeEvent.isComposing || e.keyCode === 229) && e.preventDefault()} />
            </div>
          ) : null}
          <button type="submit" className="btn primary" disabled={createGroup.isPending}>{L.project}作成</button>
          <button type="button" className="btn quiet" onClick={() => { setNewGroupForm(null); setGroupError(undefined); }}>取消</button>
          <div style={{ flexBasis: "100%" }}><ErrorText>{groupError}</ErrorText></div>
        </form>
      ) : null}
      {/* 欄の幅（#272、PC）：現場 360・工期 200・有効期限 140・概要 360・担当 180 */}
      {(
        [
          ["siteAddress", siteLabel, "w-360"],
          ["workPeriodText", L.period, "w-200"],
          ["validityText", ESTIMATE_PC_MSG.validity, "w-140"],
          ["workOutline", L.outline, "w-360"],
          ["staffName", ESTIMATE_PC_MSG.staff, "w-180"],
        ] as const
      ).map(([k, label, w]) => (
        <div className={`edrow ${w}`} key={k}>
          <label className="edl" htmlFor={`h_${k}`}>{label}</label>
          <div className="edv">
            <input id={`h_${k}`} className="inp" value={head[k]} readOnly={readOnly} onChange={(e) => setH({ [k]: e.target.value } as Partial<Head>)} />
          </div>
        </div>
      ))}
      {field && curGroup?.ownerName ? (
        <div className="edrow">
          <span className="edl">施主（{L.project}から）</span>
          <div className="edv"><span className="edtext">{curGroup.ownerName} 様</span></div>
        </div>
      ) : null}
      {/* #213（docs/40 §4）：パートナー。スイッチ（既定 OFF）→ パートナー ON の取引先から選ぶ → 登録の率。この見積だけ率を変えられる（★A） */}
      {referralOn ? (
        <div className="edrow w-280" data-testid="referral-row">
          <span className="edl">{t(REFERRAL_MSG.docSwitch)}</span>
          <div className="edv refedv">
            <Switch
              checked={!!head.referrerId || refPicking}
              disabled={readOnly}
              label={t(REFERRAL_MSG.docSwitch)}
              bare
              testId="referral-switch"
              onChange={(on) => {
                if (on) setRefPicking(true);
                else {
                  setRefPicking(false);
                  setDocReferral(null, null);
                }
              }}
            />
            {head.referrerId || refPicking ? (
              readOnly ? (
                <span className="edtext" data-testid="referral-text">{refShort} {head.referrerPct != null ? `${pctShort(head.referrerPct)}%` : ""}</span>
              ) : (
                <>
                  <select
                    className="inp refsel"
                    value={head.referrerId ?? ""}
                    aria-label={t(REFERRAL_MSG.docPick)}
                    data-testid="referral-select"
                    onChange={(e) => {
                      const o = refOptions.find((x) => x.id === e.target.value);
                      if (o) {
                        setRefPicking(false);
                        setDocReferral(o.id, o.referralPct);
                      }
                    }}
                  >
                    {head.referrerId ? null : <option value="">{t(REFERRAL_MSG.docPick)}</option>}
                    {head.referrerId && !refOptions.some((x) => x.id === head.referrerId) ? <option value={head.referrerId}>{refShort}</option> : null}
                    {refOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.displayName}（{pctShort(o.referralPct)}%）
                      </option>
                    ))}
                  </select>
                  {head.referrerId ? (
                    <label className="refpct">
                      {REFERRAL_MSG.docPct}
                      <PctField
                        id="h_referrerPct"
                        value={head.referrerPct}
                        aria-label={`${t(REFERRAL_MSG.docSwitch)}の${REFERRAL_MSG.docPct}`}
                        data-testid="referral-pct"
                        onCommit={(v) => {
                          if (v != null && v > 0 && v < 90) setDocReferral(head.referrerId, v);
                          else toast(t(REFERRAL_MSG.pctRange), undefined, "error");
                        }}
                      />
                      %
                    </label>
                  ) : null}
                  <Help text={t(REFERRAL_MSG.docPctHelp)} label={`${t(REFERRAL_MSG.docSwitch)}の説明`} testId="help-referral" />
                  {refOpts.data && !refOptions.length ? <span className="muted" data-testid="referral-none">{t(REFERRAL_MSG.docNone)}</span> : null}
                </>
              )
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
  /** 2行目の右（#187）：表示順・元に戻す／やり直す・社内用・詳細。ログインの情報が届く前から同じ形で描く（表示順の欄は幅を決めてある＝#179） */
  const detailBtn = (
    // #138：縁付きの小さなボタン「詳細 ⌄」。開いているあいだは is-open（藍の縁）で ⌃
    <button
      type="button"
      className={"btn sm quiet detailbtn" + (more ? " is-open" : "")}
      onClick={() => setMore((v) => !v)}
      title={more ? "閉じる" : `${L.customer}${ESTIMATE_PC_MSG.contact}・${L.project}・${siteLabel}・${L.period}・${ESTIMATE_PC_MSG.validity}・${L.outline}・${ESTIMATE_PC_MSG.staff}`}
      aria-expanded={more}
      data-testid="head-more"
    >
      {ESTIMATE_PC_MSG.more}
      <Icon k="chev" />
    </button>
  );
  const headTools = alt ? null : (
    <>
      {layoutSel}
      {sheetSeg}
      {internalSw}
      {detailBtn}
    </>
  );
  const addBar = !readOnly ? (
    <div className="eadd">
      <button type="button" className="btn text addline" onClick={() => addLine("detail")}>
        <Icon k="plus" />
        {ESTIMATE_PC_MSG.addLine}
      </button>
      <span className="others" role="group" aria-label={ESTIMATE_PC_MSG.otherLines}>
        {(["group", "note", "subtotal", "overhead_rate", "excluded", "discount"] as const).map((k) => (
          <button key={k} type="button" className="btn text sm" data-testid={`add-${k}`} onClick={() => addLine(k)}>{ROW_ROLE_LABEL[k]}</button>
        ))}
      </span>
    </div>
  ) : null;

  return (
    <>
      {invoice?.docHead ? (
        <DocHead
          back={back}
          save={saveNode2}
          undo={undoBar}
          primary={invoice.docHead.primary}
          menu={invoice.docHead.menu}
          kind={invoice.docHead.kind}
          kindLabel={invoice.docHead.kindLabel}
          title={invoice.docHead.title}
          chips={
            invoice.chips ? (
              <span className="hd-state" data-testid="head-state">
                {invoice.chips}
              </span>
            ) : null
          }
          help={pageHelp ? <Help testId="help-invoice" label={`${pageTitle}の画面の説明`} text={pageHelp} /> : null}
          total={invoice.docHead.showTotal === false ? null : { total: calc.total, subtotal: calc.subtotal, taxTotal: calc.taxTotal }}
          meta={invoice.docHead.meta}
          tools={
            <>
              {sheetSeg}
              {orderDoc ? null : layoutSel}
              {orderDoc ? null : internalSw}
              {invoice.docHead.tools}
            </>
          }
          testId="doc-head"
        />
      ) : template ? (
        // #245：見積書のテンプレートの編集も帳票の頭（2段目の題＝テンプレート名）
        <DocHead
          back={back}
          save={saveNode2}
          undo={undoBar}
          kind="estimates"
          kindLabel={pageTitle}
          title={
            <input
              id="subj"
              className="dh-subj"
              style={{ width: `calc(${Math.max(8, [...head.subject].reduce((a, c) => a + (c.charCodeAt(0) < 0x2000 ? 0.6 : 1.04), 0) + 1)}em + 18px)` }}
              aria-label={TEMPLATE_MSG.titleLabel}
              placeholder={TEMPLATE_MSG.titleLabel}
              value={head.subject}
              onChange={(e) => setH({ subject: e.target.value })}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229) return;
                e.preventDefault();
                if (!rows.length) addLine("detail");
              }}
            />
          }
          help={pageHelp ? <Help testId="help-template" label={`${pageTitle}の画面の説明`} text={pageHelp} /> : null}
          total={{ total: calc.total, subtotal: calc.subtotal, taxTotal: calc.taxTotal }}
          tools={
            <>
              {sheetSeg}
              {layoutSel}
              {internalSw}
            </>
          }
          testId="doc-head"
        />
      ) : template || invoice ? (
        <>
          <BackButton back={back} compact />
          <div className="hrow center">
            <h1>
              {pageTitle}
              {pageHelp ? <Help testId={template ? "help-template" : "help-invoice"} label={`${pageTitle}の画面の説明`} text={pageHelp} /> : null}
            </h1>
            {invoice?.chips ? (
              <span className="hd-state" data-testid="head-state">
                {invoice.chips}
              </span>
            ) : null}
            <span className="sp" />
            {saveNode}
            {invoice ? invoice.actions : null}
          </div>
        </>
      ) : (
        <TopBand
          back={back}
          meta={
            <>
              {/* #213（docs/40 §6.3）：「詳細」を畳んでいても、パートナー ON なら「P 見本不動産 10%」（社内用。社内用を切ると消える） */}
              {referralOn && showInternal && head.referrerId && head.referrerPct != null ? (
                <span className="chip refchip" data-testid="referral-chip" title={t(`{referrer} ${refShort} ${pctShort(head.referrerPct)}%`)}>
                  {REFERRAL_MSG.feeMark} {refShort} {pctShort(head.referrerPct)}%
                </span>
              ) : null}
              {/* #218（docs/88 §4.5）：その見積に付いた見積依頼の数。0件なら出さない（届く前も幅を取らない＝帯の2行目の右の道具は動かない）。
                  #275（docs/99 §5）：取消した依頼は数えない（取消だけなら出さない。開く先は今のまま「すべて」） */}
              {can("rfq") && liveRfqCount ? (
                <Link className="esrfq" to={`/rfqs?f=all&estimate=${initial.id}`} data-testid="estimate-rfq-count">
                  {RFQ_MSG.bandCount.replace("{n}", String(liveRfqCount))} ›
                </Link>
              ) : null}
              {/* #223 の2回目（docs/89 §6）：送付の記録（送付状を付けて印刷・保存・メールした回数）。0回なら出さない */}
              {initial.documentNo ? <DeliveriesLink filter={{ document: initial.id }} testId="estimate-deliveries" /> : null}
            </>
          }
          title={
            <input
              id="subj"
              className="subjin"
              style={{ width: `calc(${Math.max(6, [...head.subject].reduce((a, c) => a + (c.charCodeAt(0) < 0x2000 ? 0.6 : 1.04), 0) + 1)}em + 14px)` }}
              aria-label={L.subject}
              placeholder={L.subject}
              value={head.subject}
              readOnly={readOnly}
              title={head.subject || undefined}
              onChange={(e) => setH({ subject: e.target.value })}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229) return;
                e.preventDefault();
                if (!rows.length) addLine("detail");
              }}
            />
          }
          help={pageHelp ? <Help testId="help-estimate" label={`${pageTitle}の画面の説明`} text={pageHelp} /> : null}
          status={status}
          client={clientNode}
          date={
            <>
              {/* S3（★10）：日付の欄は DateInput（文字で打てる＋カレンダーの窓）。カレンダーのボタンを含むので label でくるまない（読み上げは aria-label） */}
              <span className="esdate">
                <DateInput inline label="見積日" ariaLabel="見積日" value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v })} testId="issue-date" />
              </span>
              {/* T2：取引日（税率が1年のうちに変わる区分を使っている・取引日が入っているときだけ。空＝見積日で計算） */}
              {calc.taxDateHint ? (
                <span className="esdate taxpoint">
                  <label htmlFor="h_taxPointDate">{TAX_MSG.taxDate}</label>
                  <DateInput inline id="h_taxPointDate" label={TAX_MSG.taxDate} ariaLabel={TAX_MSG.taxDate} value={head.taxPointDate ?? ""} readOnly={readOnly} onChange={(v) => setH({ taxPointDate: v || null })} testId="tax-point-date" />
                  <Help label={TAX_MSG.taxDate} text={TAX_MSG.taxDateHelp} testId="help-tax-point-date" />
                </span>
              ) : null}
            </>
          }
          docNo={initial.displayNo ?? initial.documentNo}
          total={calc.total}
          subtotal={calc.subtotal}
          taxTotal={calc.taxTotal}
          save={saveNode2}
          undo={alt ? null : undoBar}
          primary={primary}
          tools={headTools}
          chips={estChips}
          actions={estActionsNode}
          menu={estMenuNode}
        >
          {more ? detailPanel : null}
        </TopBand>
      )}

      {/* 題の下の帯（出したあと・取り消した下書き・受注／失注・訂正中・請求書の帯）は #171 でやめた：直後の一言は通知、状態は見出しのチップ、ボタンは見出しの右（docs/10 §5.12） */}
      {!invoice && !template && initial.historyEnabled && initial.revision.no >= 2 ? <RoundStrip estimateId={initial.id} expected={{ no: initial.revision.no, revising: !!initial.revising }} marks={marksTools} /> : null}
      {underCount && readOnly ? (
        <div className="warnbar">
          ⚠ <b>原価割れが {underCount}件 あります。</b> {readOnly ? "発行済みの見積書なので単価は修正できません（仕入れだけは社内用の列で修正できます）。" : null}
        </div>
      ) : null}
      {staleRef.current && !stale ? (
        <div className="next warn" data-testid="stale-band">
          <b>{ESTIMATE_PC_MSG.staleSaving}</b>
          <button type="button" className="small" onClick={() => setStale({ busy: false })}>{ESTIMATE_PC_MSG.staleReload}</button>
        </div>
      ) : null}
      <ErrorText>{save.kind === "error" && !save.retrying && !staleRef.current ? save.msg : undefined}</ErrorText>

      {/* ---- 頭の欄（請求書は Invoice.tsx が描く・雛形はタイトルと合計だけ） ---- */}
      {invoice ? (
        invoice.head({ total: calc.total })
      ) : null}

      {/* 外注見積の帯（#131）：外注見積の取込（vendorQuote）の会社だけ。#218（docs/88 §4.5・§12 の 8）：見積依頼の段はやめた＝
          見積依頼は頭の帯の「見積依頼 n件 ›」・「⋯」・行の「…」から */}
      {!alt && can("vendorQuote") ? (
        <VendorStrip
          vendorQuotes
          progress={vprog.data}
          requested={[]}
          groupId={initial.projectGroup?.id ?? null}
          readOnly={readOnly}
          onBulk={(qid) => openImport(null, qid)}
          onOriginal={(qid) => setOrig({ quoteId: qid, page: 1 })}
        />
      ) : null}

      {/* ---- 明細（#187：道具の列（内訳のタブ・紙の形）→ お知らせの1行 → まとめて付ける → 表 → 行を追加。#188：相違内容表示は回の帯の中へ） ---- */}
      <div className="paper esheet">
        {toolbar}
        {noticeLine}
        {axSel.size && !readOnly && !alt ? (
          <BulkBar
            count={axSel.size}
            provisional={selectedProvisional}
            lists={axLists}
            onApply={applyBulk}
            onFinalize={() => finalizeRows(axSel)}
            onClear={() => setAxSel(new Set())}
            onAskNew={askNewPlace}
            moveTargets={moveTargets}
            onMove={(sid) => moveRows(axSel, sid)}
            onCopy={() => copyRows(axSel)}
            field={field}
            places={can("places")}
            trades={can("trades")}
          />
        ) : null}
        {table}
        {addBar}
      </div>

      {/* 計算の説明は「計算方法」の「？」の中（#92。丸め・インボイス制度・別途・小計・社内用の列。#139 で「計算のきまり」から言い換え） */}
      <p className="calcnote">
        計算方法
        <Help testId="help-calc" label="計算方法の説明" text={calcHelp} />
      </p>
      {/* #108（docs/41 ★3）：設定（または顧客）の端数処理が、この見積を作ったときと違う。自動では変えず、押したら当て直す */}
      {roundingDiffers && calc.roundingNow ? (
        <div className="paper" data-testid="rounding-differs" style={{ background: "var(--warn)", padding: "12px 16px", marginTop: 8 }}>
          <div className="row" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>
              既定の端数処理（明細：{roundingCodeLabel(calc.roundingNow.lineRoundingMode)}／消費税：{roundingCodeLabel(calc.roundingNow.roundingMode)}
              {/* マイナスの基準は明細・消費税のどちらかが違えば出す（会社の基準は1つ＝いまの明細のコードの基準） */}
              {negativeRoundingOf(calc.roundingNow.lineRoundingMode) !== negativeRoundingOf(calc.lineRoundingMode) || negativeRoundingOf(calc.roundingNow.roundingMode) !== negativeRoundingOf(calc.roundingMode)
                ? `／マイナス：${NEGATIVE_ROUNDING_LABEL[negativeRoundingOf(calc.roundingNow.lineRoundingMode)]}`
                : ""}
              ）と異なります。
            </span>
            <span className="sp" />
            <button type="button" className="btn" data-testid="rounding-apply" disabled={roundingBusy} onClick={() => void doApplyRounding()}>
              {roundingBusy ? "再計算中…" : "既定値で再計算"}
            </button>
          </div>
        </div>
      ) : null}

      {invoice?.footer ?? null}
      {/* 紙の備考：見積書と雛形（#197。雛形から見積書を作ると写る）。請求書は Invoice.tsx が footer に描く */}
      {!invoice ? (
        <div className="paper memo">
          {initial.revising ? (
            <div className="field" style={{ marginBottom: 12 }}>
              {/* 差替文（#188：前は「差し替えの一文（…）」。説明は「？」） */}
              <span className="rslabel">
                <label htmlFor="replace_sentence">{ESTIMATE_PC_MSG.replaceSentence}</label>
                <Help testId="help-replace-sentence" label={`${ESTIMATE_PC_MSG.replaceSentence}の説明`} text={ESTIMATE_PC_MSG.replaceSentenceHelp} />
              </span>
              <input id="replace_sentence" className="inp" value={head.replaceSentence} maxLength={200} onChange={(e) => setH({ replaceSentence: e.target.value })} onKeyDown={guardComposingEnter} />
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="memo">{ESTIMATE_PC_MSG.memoLabel}</label>
            <textarea id="memo" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
          </div>
        </div>
      ) : null}
      {/* #302（B3）：出した回の行の仕入元（「…」の「仕入元変更」）。取引先検索のドロアー（role vendor） */}
      <PartnerPickerDrawer
        open={vendorPickLine != null}
        role="vendor"
        onClose={() => setVendorPickLine(null)}
        onPick={(p) => {
          const id = vendorPickLine;
          setVendorPickLine(null);
          void qc.invalidateQueries({ queryKey: ["partners", "vendor"] });
          if (id) setVendorOf(id, p.id);
        }}
      />
      {askDelete ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAskDelete(false)}>
          <div className="dlg" role="alertdialog" aria-label={ESTIMATE_PC_MSG.discard} onKeyDown={(e) => e.key === "Escape" && setAskDelete(false)}>
            <div className="dh">{ESTIMATE_PC_MSG.discard}</div>
            <div className="db">
              <p style={{ margin: 0 }} data-testid="discard-whole-ask">
                {initial.documentNo
                  ? DISCARD_MSG.askWholeNumbered.replace("{no}", initial.documentNo)
                  : initial.projectGroup?.autoOnly
                  ? t(DISCARD_MSG.askWholeWithGroup).replace("{name}", initial.projectGroup.name)
                  : DISCARD_MSG.askWhole}
              </p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setAskDelete(false)}>取消</button>
              <button type="button" className="btn shu" onClick={() => void deleteDraft()}>削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {askRfqCancel != null ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAskRfqCancel(null)}>
          <div className="dlg" role="alertdialog" aria-label={ESTIMATE_PC_MSG.discard} onKeyDown={(e) => e.key === "Escape" && setAskRfqCancel(null)}>
            <div className="dh">{ESTIMATE_PC_MSG.discard}</div>
            <div className="db">
              <p style={{ margin: 0 }} data-testid="discard-rfq-ask">{t(RFQ_MSG.hasRfq).replace("{n}", String(askRfqCancel))}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setAskRfqCancel(null)}>取消</button>
              <button type="button" className="btn shu" data-testid="discard-rfq-confirm" onClick={() => { setAskRfqCancel(null); void deleteDraft(true); }}>{RFQ_MSG.cancelAndDelete}</button>
            </div>
          </div>
        </div>
      ) : null}
      {rfqPick && rfqs.data ? (
        <RfqVendorDialog
          data={rfqs.data}
          pick={readOnly ? undefined : { vendors: axLists.vendors, lines: rows.filter((r) => r.role === "detail" || r.role === "note").map((r) => ({ id: r.id, name: r.name, spec: r.spec })), customer: L.customer, onAssign: assignRfq }}
          onPick={(v) => { setRfqPick(false); void openRfq(v); }}
          onClose={() => setRfqPick(false)}
        />
      ) : null}
      {ledgerOpen ? <PhotoLedgerDialog estimateId={initial.id} onClose={() => setLedgerOpen(false)} /> : null}
      {stale ? <StaleDialog busy={stale.busy} onReload={() => void reloadStale()} onLater={() => setStale(null)} /> : null}
      {fill ? (
        <AxisFillDialog
          kind={fill}
          rows={(fill === "trade" ? tradeMissing : placeMissing).map((r) => ({ id: r.id, name: r.name }))}
          lists={axLists}
          onAskNew={askNewPlace}
          onApply={applyFill}
          onClose={() => setFill(null)}
        />
      ) : null}
      {ordDlg ? (
        <OrderAddLineDialog
          lineName={rows.find((x) => x.id === ordDlg)?.name ?? ""}
          value={rows.find((x) => x.id === ordDlg)?.vendorPartnerId ?? null}
          vendors={axLists.vendors}
          customer={L.customer}
          onApply={(v, orderId) => void addToOrder(ordDlg, v, orderId)}
          onClose={() => setOrdDlg(null)}
        />
      ) : null}
      {reqDlg ? (
        <RfqAddLineDialog
          estimateId={initial.id}
          groupId={initial.projectGroup?.id ?? null}
          lineName={rows.find((x) => x.id === reqDlg)?.name ?? ""}
          value={rows.find((x) => x.id === reqDlg)?.vendorPartnerId ?? null}
          vendors={axLists.vendors}
          customer={L.customer}
          onApply={(v, rfqId) => void addToRfq(reqDlg, v, rfqId)}
          onClose={() => setReqDlg(null)}
        />
      ) : null}
      {nameDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setNameDlg(null)}>
          <form
            className="dlg"
            role="dialog"
            aria-label={nameDlg.title}
            onSubmit={(e) => {
              e.preventDefault();
              const v = nameDlg.value.trim();
              if (!v) { document.getElementById("dlgName")?.focus(); return; }
              setNameDlg(null);
              nameDlg.onOk(v);
            }}
            onKeyDown={(e) => e.key === "Escape" && setNameDlg(null)}
          >
            <div className="dh">{nameDlg.title}</div>
            <div className="db">
              <Field id="dlgName" label="名前">
                <input id="dlgName" className="inp" autoFocus value={nameDlg.value} onChange={(e) => setNameDlg({ ...nameDlg, value: e.target.value })} onKeyDown={guardComposingEnter} />
              </Field>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" onClick={() => setNameDlg(null)}>取消</button>
              <button type="submit" className="btn primary">決定</button>
            </div>
          </form>
        </div>
      ) : null}
      {rowDel ? (
        // 行の削除（#140）：内訳の削除の窓と同じ部品（題は「〜を削除しますか」、ボタンは「取消」「削除」）
        <div className="dim" onClick={(e) => e.target === e.currentTarget && closeRowDel(false)}>
          <div className="dlg" role="alertdialog" aria-label={ESTIMATE_PC_MSG.rowDeleteTitle} data-testid="row-delete-dialog" onKeyDown={(e) => e.key === "Escape" && closeRowDel(false)}>
            <div className="dh">{rowDel.name ? ESTIMATE_PC_MSG.rowDeleteAsk.replace("{name}", rowDel.name) : ESTIMATE_PC_MSG.rowDeleteAskBlank}</div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => closeRowDel(false)} data-testid="row-delete-cancel">取消</button>
              <button type="button" className="btn" style={{ borderColor: "var(--shu)", color: "var(--shu)" }} onClick={() => closeRowDel(true)} data-testid="row-delete-confirm">削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {delDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDelDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label="内訳を削除" onKeyDown={(e) => e.key === "Escape" && setDelDlg(null)}>
            <div className="dh">{SHEET_TRASH_MSG.deleteAsk.replace("{name}", delDlg.name || "（名前なし）")}</div>
            <div className="db">
              {/* #200：行のある内訳は「削除した内訳」に入る（30日）。行の無い内訳は入らない */}
              <p style={{ margin: 0 }} data-testid="sheet-delete-body">
                {(() => {
                  const n = rows.filter((r) => r.sheetId === delDlg.id).length;
                  return n ? SHEET_TRASH_MSG.deleteBody.replace("{n}", String(n)).replace("{amount}", yen(sheetTotal(delDlg.id))) : SHEET_TRASH_MSG.deleteEmpty;
                })()}
              </p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setDelDlg(null)}>取消</button>
              <button type="button" className="btn" style={{ borderColor: "var(--shu)", color: "var(--shu)" }} onClick={() => doDelSection(delDlg)}>削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {trashDlg ? <DeletedSheetsDialog estimateId={initial.id} busy={sheetRestoring} onRestore={(id, name) => void restoreSection(id, name)} onClose={() => setTrashDlg(false)} /> : null}
      {mergeAsk ? (
        // #199：不使用の内訳があるときの「1枚」の確かめ（決定で不使用の内訳とその行を消してまとめる。↶ で戻せる）
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setMergeAsk(null)}>
          <div className="dlg" role="alertdialog" aria-label={ESTIMATE_PC_MSG.mergeUnusedTitle} onKeyDown={(e) => e.key === "Escape" && setMergeAsk(null)} data-testid="merge-unused">
            <div className="dh">{ESTIMATE_PC_MSG.mergeUnusedTitle}</div>
            <div className="db">
              <p style={{ margin: 0 }}>{ESTIMATE_PC_MSG.mergeUnusedAsk.replace("{names}", mergeAsk.join("」「"))}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setMergeAsk(null)}>取消</button>
              <button
                type="button"
                className="btn"
                style={{ borderColor: "var(--shu)", color: "var(--shu)" }}
                onClick={() => {
                  setMergeAsk(null);
                  toggleSections({ dropUnused: true });
                }}
              >
                {ESTIMATE_PC_MSG.mergeUnusedOk}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {issueDlg.open ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
          <div className="dlg" role="dialog" aria-label="見積書発行" onKeyDown={(e) => e.key === "Escape" && !issueDlg.busy && setIssueDlg((d) => ({ ...d, open: false }))}>
            <div className="dh">
              発行前の確認
              <Help
                testId="help-issue"
                label="確かめの説明"
                text={helpText([
                  initial.documentNo ? null : "番号の最初は Q-0001 です。書式はあとで変えられます。",
                  "仕入れ・利益率は印刷されません。",
                  needsCompany ? "会社名は帳票に印字するため必須です。住所と電話は未入力でも発行できます。次回からは表示しません。" : null,
                ])}
              />
            </div>
            <div className="db">
              <div className="pv">
                <div><span className="muted">{L.customer}</span><b>{head.clientName} {head.honorific}</b></div>
                <div><span className="muted">合計（税込）</span><b className="num">{yen(calc.total)} 円</b></div>
                {initial.documentNo ? (
                  <div><span className="muted">番号</span><b className="num">{initial.documentNo}</b></div>
                ) : (
                  <div><span className="muted">番号</span><b className="num">発行時に付きます</b></div>
                )}
                <div><span className="muted">{L.subject}</span><b>{head.subject || <span className="shu">（未入力）</span>}</b></div>
              </div>
              {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で */}
              {initial.documentNo ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(initial.documentNo)}</p> : null}
              {underCount ? <div className="warnbar" style={{ margin: 0 }}>⚠ <b>原価割れが {underCount}件 あります。</b>このまま発行できますが、社内の確認は済んでいますか。</div> : null}
              {provisionalCount ? <div className="provwarn" data-testid="issue-provisional"><b>{ESTIMATE_PC_MSG.provisionalWarn.replace("{n}", String(provisionalCount))}</b>このまま発行できます。</div> : null}
              {/* #213（docs/40 §7）：手数料の分が入っていない行は知らせるだけ（据え置きは正しい操作なので止めない） */}
              {uncoveredCount ? (
                <div className="provwarn" data-testid="issue-uncovered">
                  <b>{REFERRAL_MSG.uncovered.replace("{n}", String(uncoveredCount))}</b>
                  <Help text={t(REFERRAL_MSG.uncoveredHelp.replace("{m}", String(markupSet.marginPct)))} label="手数料の分の説明" testId="help-uncovered" />
                </div>
              ) : null}
              {needsCompany ? (
                <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 14 }}>
                  <p style={{ margin: "0 0 4px" }}>
                    <b>はじめての発行なので、あと3つだけ。</b>
                  </p>
                  <Field id="is_co" label="会社名（屋号・お名前でも）" style={{ marginBottom: 10 }}>
                    <input id="is_co" className="inp" autoFocus autoComplete="organization" value={issueDlg.company.name} onChange={(e) => setIssueDlg((d) => ({ ...d, company: { ...d.company, name: e.target.value }, error: undefined }))} onKeyDown={(e) => { if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) { e.preventDefault(); document.getElementById("is_addr")?.focus(); } }} />
                  </Field>
                  <div className="row" style={{ alignItems: "flex-end" }}>
                    <Field id="is_addr" label="住所（見積書の右上に印字）" style={{ flex: "2 1 280px" }}>
                      <input id="is_addr" className="inp" value={issueDlg.company.address} onChange={(e) => setIssueDlg((d) => ({ ...d, company: { ...d.company, address: e.target.value } }))} />
                    </Field>
                    <div style={{ flex: "1 1 250px" }}>
                      <PhoneFields id="is_tel" label="電話番号" value={issueDlg.company.phone} onChange={(phone) => setIssueDlg((d) => ({ ...d, company: { ...d.company, phone } }))} />
                    </div>
                  </div>
                </div>
              ) : null}
              <ErrorText>{issueDlg.error}</ErrorText>
              <p className="hint" style={{ margin: 0 }}>発行したあとは、この見積書は修正できません。修正するときは「取消」で下書きに戻して再発行します（番号はそのまま）。</p>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" disabled={issueDlg.busy} onClick={() => setIssueDlg((d) => ({ ...d, open: false }))}>戻って修正</button>
              <button type="button" className="btn primary big" disabled={issueDlg.busy} onClick={() => void doIssue()}>
                <Icon k="lock" />
                この内容で発行
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {reviseAsk ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setReviseAsk(false)}>
          <div className="dlg" role="dialog" aria-label="この見積を修正しますか" onKeyDown={(e) => e.key === "Escape" && setReviseAsk(false)}>
            <div className="dh">受注したあとの修正方法は、2つあります</div>
            <div className="db">
              {/* 二択を選ぶための1行は残す。各1文（#92 決め9） */}
              <p style={{ margin: 0 }}><b>この見積を修正（{initial.revision.no + 1}回目 {initial.documentNo}-{initial.revision.no + 1}）</b>：数量・単価・行を修正し、契約の変更として「変更点」のページを付けられます。</p>
              <p style={{ margin: 0 }}><b>追加の見積を作成</b>：請求書を分けたいときは、同じ{L.project}に新しい番号の見積をつくります。</p>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" onClick={() => setReviseAsk(false)}>取消</button>
              <button type="button" className="btn" onClick={() => void addJob()}>追加見積作成</button>
              <button type="button" className="btn primary" data-testid="revise-confirm" onClick={() => void doRevise()}>見積修正</button>
            </div>
          </div>
        </div>
      ) : null}
      {stopRevise.open ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !stopRevise.busy && setStopRevise((x) => ({ ...x, open: false }))}>
          <div className="dlg" role="alertdialog" aria-label="下書き削除" onKeyDown={(e) => e.key === "Escape" && !stopRevise.busy && setStopRevise((x) => ({ ...x, open: false }))}>
            <div className="dh">下書き削除</div>
            <div className="db">
              <p style={{ margin: 0 }} data-testid="discard-ask">
                {t(DISCARD_MSG.askRevision)
                  .replace("{n}", String(initial.revision.no))
                  .replace("{prev}", String(initial.revising?.baseRevisionNo ?? initial.revision.no - 1))
                  .replace("{no}", displayDocumentNo(initial.documentNo, initial.revising?.baseRevisionNo ?? 1) ?? "")}
                。<b>元に戻せません。</b>
              </p>
              <ErrorText>{stopRevise.error}</ErrorText>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus disabled={stopRevise.busy} onClick={() => setStopRevise((x) => ({ ...x, open: false }))}>編集続行</button>
              <button type="button" className="btn shu" disabled={stopRevise.busy} data-testid="discard-revision-confirm" onClick={() => void doStopRevise()}>{initial.revision.no}回目削除</button>
            </div>
          </div>
        </div>
      ) : null}
      {reviseIssue.open && reviseIssue.diff ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !reviseIssue.busy && setReviseIssue((x) => ({ ...x, open: false }))}>
          <div className="dlg wide" role="dialog" aria-label={`${initial.displayNo} として発行`} onKeyDown={(e) => e.key === "Escape" && !reviseIssue.busy && setReviseIssue((x) => ({ ...x, open: false }))}>
            <div className="dh">{initial.displayNo} として発行</div>
            <div className="db">
              <div className="pv">
                <div>
                  <span className="muted">合計（税込）</span>
                  <b className="num">{yen(reviseIssue.diff.base.total)} 円 → {yen(reviseIssue.diff.next.total)} 円</b>
                  <span className={reviseIssue.diff.next.total - reviseIssue.diff.base.total < 0 ? "shu num" : "num"}>
                    （{reviseIssue.diff.next.total - reviseIssue.diff.base.total >= 0 ? "＋" : "−"}{yen(Math.abs(reviseIssue.diff.next.total - reviseIssue.diff.base.total))} 円）
                  </span>
                </div>
                {reviseIssue.diff.cost.old != null && reviseIssue.diff.cost.new != null ? (
                  <div>
                    <span className="muted">社内用（{L.customer}には出ません）</span>
                    <span>利益 <b className="num">{yen(reviseIssue.diff.base.subtotal - reviseIssue.diff.cost.old)}</b> → <b className="num">{yen(reviseIssue.diff.next.subtotal - reviseIssue.diff.cost.new)}</b> 円</span>
                  </div>
                ) : (
                  <div><span className="muted">社内用</span><span className="hint">仕入れ未入力の行があるので、利益は出しません</span></div>
                )}
              </div>
              {provisionalCount ? <div className="provwarn" data-testid="issue-provisional"><b>{ESTIMATE_PC_MSG.provisionalWarn.replace("{n}", String(provisionalCount))}</b>このまま発行できます。</div> : null}
              {/* #213（docs/40 §7）：手数料の分が入っていない行は知らせるだけ（据え置きは正しい操作なので止めない） */}
              {uncoveredCount ? (
                <div className="provwarn" data-testid="issue-uncovered">
                  <b>{REFERRAL_MSG.uncovered.replace("{n}", String(uncoveredCount))}</b>
                  <Help text={t(REFERRAL_MSG.uncoveredHelp.replace("{m}", String(markupSet.marginPct)))} label="手数料の分の説明" testId="help-uncovered" />
                </div>
              ) : null}
              <div className="rvdiff" data-testid="revise-diff">
                {reviseIssue.diff.rows.filter((x) => x.kind === "head").length ? (
                  <div>
                    <div className="rvh">上段</div>
                    {reviseIssue.diff.rows.filter((x) => x.kind === "head").map((x, i) => (
                      <div className="rvrow" key={i}>
                        <span>{x.name}</span>
                        <span className="muted" style={{ whiteSpace: "pre-wrap" }}>{x.fields.map((f) => `${f.old || "（空）"} → ${f.new || "（空）"}`).join("、")}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                {(["changed", "added", "removed"] as const).map((kind) => {
                  const rows = reviseIssue.diff!.rows.filter((x) => x.kind === "line" && x.change === kind);
                  if (!rows.length) return null;
                  return (
                    <div key={kind}>
                      <div className="rvh">{kind === "changed" ? `変えた ${rows.length}行` : kind === "added" ? `追加した ${rows.length}行` : `削除した ${rows.length}行`}</div>
                      {rows.map((x, i) => (
                        <div className="rvrow" key={i}>
                          {kind === "removed" ? <s>{x.name || "（名前なし）"}</s> : <span>{x.name || "（名前なし)"}</span>}
                          {x.sheetName ? <span className="muted">（{x.sheetName}）</span> : null}
                          {kind === "changed" ? (
                            <span className="muted">{x.fields.map((f) => `${f.label} ${f.old || "（空）"} → ${f.new || "（空）"}`).join("、")}</span>
                          ) : kind === "added" ? (
                            <><span className="num">{x.newAmount != null && x.newAmount !== 0 ? yen(x.newAmount) : ""}</span><span className="chip ai">追加</span></>
                          ) : (
                            <><span className="num">{x.oldAmount != null && x.oldAmount !== 0 ? yen(x.oldAmount) : ""}</span><span className="chip shu">消した</span></>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {reviseIssue.diff.rows.filter((x) => x.kind === "sheet").length ? (
                  <div>
                    <div className="rvh">内訳書</div>
                    {reviseIssue.diff.rows.filter((x) => x.kind === "sheet").map((x, i) => (
                      <div className="rvrow" key={i}>
                        <span>{x.name || "（名前なし）"}</span>
                        {/* #199：使う／不使用 の変化は label が ""＝「不使用 → 使用」 */}
                        <span className="muted" data-testid="diff-sheet-change">{x.change === "added" ? "追加した" : x.change === "removed" ? "削除した" : x.fields.map((f) => `${f.label ? f.label + " " : ""}${f.old} → ${f.new}`).join("、")}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <p className="hint" style={{ margin: "6px 0 0" }}>変更のない {reviseIssue.diff.unchangedCount}行 は表示していません。</p>
              </div>
              <Field id="rv_note" label="訂正の内容" help={<Help testId="help-revise" label="訂正の内容の説明" text="帳票には差替文と「訂正の内容」を印字します。前回は控えとして残ります。" />}>
                <input id="rv_note" className="inp" value={reviseIssue.note} onChange={(e) => setReviseIssue((x) => ({ ...x, note: e.target.value, error: undefined }))} onKeyDown={guardComposingEnter} />
              </Field>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" style={{ width: 20, height: 20, margin: 0 }} checked={reviseIssue.changeSheet} onChange={(e) => setReviseIssue((x) => ({ ...x, changeSheet: e.target.checked }))} />
                最後のページに「変更点」の表を付ける（当初／今回／増減）
                {initial.order?.status === "ordered" && field ? <span className="hint">受注後の変更なので、書面に残すことをお勧めします</span> : null}
              </label>
              <ErrorText>{reviseIssue.error}</ErrorText>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" disabled={reviseIssue.busy} onClick={() => setReviseIssue((x) => ({ ...x, open: false }))}>戻って修正</button>
              <button type="button" className="btn primary big" disabled={reviseIssue.busy} onClick={() => void doReviseIssue()} data-testid="revise-issue-confirm">
                <Icon k="lock" />
                {initial.displayNo} として発行
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {dnq.dialogs}
      {unissue.open ? (
        // 取消しの窓（#188）：題に警告のしるし＋1文。説明は「？」。2回目からは「2回目「Q-0005-2」を…」
        <UnissueDialog
          doc="quote"
          no={initial.displayNo ?? initial.documentNo ?? ""}
          round={initial.revision.no}
          busy={unissue.busy}
          error={unissue.error}
          onClose={() => setUnissue((u) => ({ ...u, open: false }))}
          onConfirm={() => void doUnissue()}
        />
      ) : null}
      <Picker
        open={pick.open}
        q={pick.q}
        tab={pick.tab}
        sel={pickSel}
        multi={pick.multi}
        groups={cands}
        showItems={can("items")}
        list={pickList}
        loading={suggest.isPending}
        onQ={(q) => setPick((p) => ({ ...p, q, sel: 0, armed: false }))}
        onTab={(t) => setPick((p) => ({ ...p, tab: t, sel: 0 }))}
        onMove={(d) => {
          const n = pickList.length;
          if (n) setPick((p) => ({ ...p, armed: true, sel: (pickSel + d + n) % n }));
        }}
        onPick={pickAt}
        onMulti={(on) => setPick((p) => ({ ...p, multi: on }))}
        onClose={() => {
          closePicker();
          focusNext("f_name", true);
        }}
      />
      {vimp && vprog.data ? (
        <VendorImportPicker
          progress={vprog.data}
          quoteId={vimp.quoteId}
          onQuoteChange={(qid) => setVimp({ ...vimp, quoteId: qid })}
          targets={targetRows}
          rowNo={rowNoOf}
          amountOf={(t) => calc.amounts[t.id] ?? 0}
          issued={readOnly}
          pricing={pricingNow}
          onPricing={setVPricing}
          referralPct={docRefPct}
          onEditTarget={editTarget}
          onAddTarget={addTarget}
          onRemoveTarget={removeTarget}
          onImport={doImport}
          onUnlink={(lineId) => void doUnlink(lineId)}
          onClose={() => setVimp(null)}
          onOriginal={(page) => setOrig({ quoteId: vimp.quoteId, page })}
          origOpen={!!orig}
          labels={L}
        />
      ) : null}
      {!alt ? <VendorOriginal orig={orig} onClose={() => setOrig(null)} /> : null}
      {!alt && attOpen ? <LineAttachmentsDialog photos={field} estimateId={initial.id} lineId={attOpen} lineName={rows.find((x) => x.id === attOpen)?.name ?? ""} onClose={() => setAttOpen(null)} /> : null}
      {!alt && tkOpen ? (
        <TakeoffDrawer
          estimateId={initial.id}
          lineId={tkOpen.lineId}
          rows={rows}
          qtyOf={qtyOf}
          lists={axLists}
          issued={readOnly}
          onClose={() => setTkOpen(null)}
          run={runTakeoff}
          reloadTick={reloadTick}
          onSource={setQuantitySource}
          onAskNewPlace={askNewPlace}
        />
      ) : null}
      {toastNode}
      {back.dialog}
      {sq.dialog}
    </>
  );
}
